import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  approveAdminReport,
  approveAdminLearningProfile,
  approveSelectedAdminLearningProfileMetrics,
  answerAdminLearningQuestion,
  archiveAdminOutstandingSample,
  createAdminThresholdDraftFromAcceptedProposals,
  createAdminPdfLearningDrafts,
  discardThresholdDraft,
  generateAdminThresholdLearningProposals,
  getAdminArchivedSamples,
  getAdminAlerts,
  getAdminAlphaTesters,
  getAdminCustomerDetail,
  getAdminCustomers,
  getAdminExcelArtemisReview,
  getAdminExcelArtemisReviewDetail,
  getAdminModules,
  getAdminOutstandingSampleDetail,
  getAdminOutstandingSamples,
  getAdminReportDetail,
  getAdminReports,
  getAdminReportDataSource,
  getAdminReportSnapshot,
  getAdminReportReviewQueue,
  getAdminReleaseQueue,
  releaseAdminReport,
  releaseAdminReportBatch,
  getAdminHistory,
  restoreAdminHistory,
  getAdminSummary,
  getAdminThresholdLearningLab,
  getAdminLearningOperations,
  getAdminThresholds,
  getAdminUsers,
  inviteAdminUser,
  inviteAdminAlphaTester,
  previewAdminThresholdDraftFromAcceptedProposals,
  publishThresholdDraft,
  renameThresholdDraft,
  rebuildAdminPdfLearning,
  rebuildAdminLearningProfiles,
  markAdminPdfLearningReady,
  resolveAdminPdfLearningException,
  saveAdminPdfLearningDirection,
  restoreAdminArchivedSample,
  correctAdminLearningProfile,
  interpretAdminLearningDirection,
  confirmAdminLearningDirection,
  interpretAdminLearningQuestion,
  confirmAdminLearningQuestion,
  getAdminLearningQuestionConversation,
  sendAdminLearningQuestionMessage,
  confirmAdminLearningQuestionProposition,
  runAdminLearningQuestionAction,
  finalizeAdminLearningQuestionConversation,
  reviewAdminThresholdLearningProposal,
  resolveAdminAlert,
  replyToAdminAlphaFeedback,
  recordAdminLearningOperationsAudit,
  saveAdminExcelArtemisReviewAnnotation,
  saveThresholdDraft,
  saveAndPublishThresholdConfig,
  saveAdminThresholdLearningGuidance,
  seedAdminThresholds,
  syncAdminReportReviewQueue,
  createAdminReportSnapshotJob,
  updateAdminReportReviewSettings,
  updateAdminReportSnapshotSchedule,
  updateAdminReportSnapshotPin,
  updateAdminDashboardAccess,
  updateAdminCustomerAccount,
  updateAdminAlphaTesterStatus,
  holdAllAdminAlphaTesters,
  resendAdminAlphaTesterInvite,
  searchAdminAlphaTesterCustomers,
  updateAdminModuleVisibility,
  updateAdminUserModuleAccess,
  uploadAdminThresholdLearningArchive,
  uploadAdminLearningObject,
  updateAdminLearningExpertRule,
  updateAdminLearningProfile,
} from "../../services/api";
import { learningProfileRowState } from "../../services/learningProfileReview";
import { useAuth } from "../../App";
import useIsMobile from "../../hooks/useIsMobile";
import { SampleLookupPanel } from "./SampleLookupPage";
import ReportReviewModal from "./ReportReviewModal";

const s = {
  page: { maxWidth: "var(--dashboard-max-width)", margin: "0 auto", padding: "32px 24px" },
  title: { fontFamily: "var(--font-heading)", fontSize: 30, fontWeight: 800, color: "var(--text-primary)", marginBottom: 18 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 24 },
  metricGroup: { gridColumn: "1 / -1", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "var(--dashboard-card-shadow)", overflow: "hidden" },
  metricGroupSummary: { cursor: "pointer", padding: "14px 16px", color: "var(--text-primary)", fontSize: 15, fontWeight: 900, background: "var(--dashboard-selector-bg)", borderBottom: "1px solid var(--border)" },
  metricGroupGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, padding: 12 },
  card: { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, padding: 16, boxShadow: "var(--dashboard-card-shadow)" },
  label: { fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" },
  value: { fontSize: 28, fontWeight: 900, color: "var(--text-primary)", marginTop: 4 },
  section: { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, padding: 16, marginBottom: 18, boxShadow: "var(--dashboard-card-shadow)" },
  sectionTitle: { fontSize: 17, fontWeight: 900, color: "var(--text-primary)", marginBottom: 12 },
  subTitle: { fontSize: 14, fontWeight: 900, color: "var(--text-primary)", margin: "16px 0 8px" },
  loadingBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 360, gap: 16 },
  loadingCard: { width: "min(540px, 100%)", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "var(--dashboard-card-shadow)", padding: "26px 24px", textAlign: "center" },
  spinner: { width: 34, height: 34, border: "3px solid var(--border)", borderTopColor: "var(--teal)", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loadingTitle: { fontSize: 18, fontWeight: 900, color: "var(--text-primary)", marginTop: 14 },
  loadingText: { fontSize: 14, color: "var(--text-muted)", marginTop: 6, lineHeight: 1.5 },
  loadingTrack: { position: "relative", height: 10, overflow: "hidden", borderRadius: 999, background: "var(--border-subtle)", border: "1px solid var(--border)", marginTop: 18 },
  loadingFill: { position: "absolute", inset: "0 auto 0 0", width: "46%", borderRadius: 999, background: "linear-gradient(90deg, color-mix(in srgb, var(--teal) 38%, transparent), var(--teal), color-mix(in srgb, var(--teal) 38%, transparent))" },
  loadingMeta: { fontSize: 12, color: "var(--text-faint)", marginTop: 10, fontWeight: 700 },
  row: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  btn: { border: "none", borderRadius: 6, padding: "8px 12px", background: "var(--teal)", color: "#fff", fontWeight: 800, cursor: "pointer" },
  secondary: { border: "1px solid var(--border)", borderRadius: 6, padding: "8px 12px", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", fontWeight: 800, cursor: "pointer" },
  danger: { border: "1px solid #fecaca", borderRadius: 6, padding: "8px 12px", background: "#fef2f2", color: "#b91c1c", fontWeight: 800, cursor: "pointer" },
  input: { border: "1.5px solid var(--border)", borderRadius: 6, background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", padding: "8px 10px" },
  modalBackdrop: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15, 23, 42, 0.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 },
  modal: { position: "relative", width: "min(1120px, 96vw)", maxHeight: "88vh", overflow: "auto", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 24px 80px rgba(15, 23, 42, 0.35)", padding: 16 },
  detailModal: { width: "min(980px, 96vw)" },
  backToTop: { position: "sticky", left: 0, bottom: 12, zIndex: 1002, display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2, border: "1px solid rgba(255, 255, 255, 0.22)", borderRadius: 10, background: "rgba(15, 23, 42, 0.78)", color: "#fff", boxShadow: "0 12px 34px rgba(15, 23, 42, 0.26)", padding: "10px 14px", fontWeight: 900, cursor: "pointer", opacity: 0, pointerEvents: "none", transform: "translateY(6px)", transition: "opacity 180ms ease, transform 180ms ease" },
  backToTopVisible: { opacity: 0.9, pointerEvents: "auto", transform: "translateY(0)" },
  tabBar: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 },
  tab: { border: "1px solid var(--border)", borderRadius: 6, padding: "9px 12px", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", fontWeight: 900, cursor: "pointer" },
  tabActive: { border: "1px solid var(--teal)", borderRadius: 6, padding: "9px 12px", background: "var(--teal)", color: "#fff", fontWeight: 900, cursor: "pointer" },
  toolbar: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", padding: 12, background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 8, marginBottom: 14 },
  panel: { border: "1px solid var(--border)", borderRadius: 8, padding: 14, background: "var(--bg-card)" },
  pill: { display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "5px 9px", background: "var(--dashboard-selector-bg)", color: "var(--text-muted)", fontSize: 12, fontWeight: 800 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", borderBottom: "1px solid var(--border)", padding: "8px", color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" },
  thContent: { display: "inline-flex", alignItems: "center", gap: 6 },
  infoWrap: { position: "relative", display: "inline-flex", alignItems: "center" },
  infoButton: { width: 18, height: 18, borderRadius: 999, border: "1px solid color-mix(in srgb, var(--teal) 35%, transparent)", background: "color-mix(in srgb, var(--teal) 12%, transparent)", color: "var(--teal-dark)", fontSize: 12, fontWeight: 900, lineHeight: 1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 },
  infoPopover: { position: "fixed", zIndex: 1400, width: 320, maxWidth: "min(320px, 78vw)", padding: 12, border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-card)", color: "var(--text-primary)", boxShadow: "0 18px 50px rgba(15, 23, 42, 0.22)", textTransform: "none", letterSpacing: 0, fontSize: 12, lineHeight: 1.45 },
  sortHeader: { width: "100%", border: "none", background: "transparent", color: "inherit", padding: 0, font: "inherit", fontWeight: 900, textAlign: "left", textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 },
  sortIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 18, height: 18, borderRadius: 999, background: "color-mix(in srgb, var(--teal) 12%, transparent)", color: "var(--teal-dark)", fontSize: 15, fontWeight: 900, lineHeight: 1 },
  td: { borderBottom: "1px solid var(--border)", padding: "8px", color: "var(--text-primary)", verticalAlign: "middle" },
  status: { fontSize: 13, fontWeight: 800, marginTop: 10 },
  hint: { fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 },
  explainer: { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", margin: "0 0 18px", boxShadow: "var(--dashboard-card-shadow)" },
  explainerSummary: { cursor: "pointer", color: "var(--text-primary)", fontWeight: 900, fontSize: 14 },
  explainerText: { color: "var(--text-muted)", fontSize: 13, lineHeight: 1.6, margin: "10px 0 0" },
  ok: { color: "#16a34a" },
  err: { color: "#dc2626" },
};

function clone(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

function normalizeLegacyThresholdTypes(value) {
  if (Array.isArray(value)) return value.map(normalizeLegacyThresholdTypes);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [
    key,
    key === "type" && child === "ICON" ? "GT" : normalizeLegacyThresholdTypes(child),
  ]));
}

function cloneThresholdConfig(value) {
  return normalizeLegacyThresholdTypes(clone(value));
}

function thresholdTypeLabel(type) {
  return type === "GT" || type === "ICON" ? "Greater Than" : type;
}

function metricEntries(config, templateName) {
  const template = config?.templates?.[templateName] || {};
  return Object.entries(template);
}

function normalizeForMatch(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function cloneTemplate(template) {
  return template ? JSON.parse(JSON.stringify(template)) : {};
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const LEARNING_LAB_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

function validateLearningLabFile(file) {
  if (Number(file?.size || 0) <= LEARNING_LAB_MAX_UPLOAD_BYTES) return null;
  return `“${file.name}” is ${formatBytes(file.size)}. Learning Lab files must be 50 MB or smaller. Split the PDFs into two or more ZIP files and upload each batch separately.`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",").pop() : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Unable to read upload file."));
    reader.readAsDataURL(file);
  });
}

function formatDateOnly(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return formatDate(value);
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function formatReportTypeBreakdown(counts = {}) {
  const entries = Object.entries(counts || {})
    .map(([type, count]) => ({ type: String(type || "Unknown").trim() || "Unknown", count: Number(count || 0) }))
    .filter((entry) => entry.count > 0);
  const sortedEntries = entries
    .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));

  return (
    <>
      {sortedEntries.map((entry) => (
        <div key={entry.type}>{entry.type}: {formatCount(entry.count)}</div>
      ))}
    </>
  );
}

function toggleSort(sort, field) {
  if (sort.field === field) {
    return { field, direction: sort.direction === "asc" ? "desc" : "asc" };
  }
  return { field, direction: "asc" };
}

function SortableTh({ label, field, sort, onSort }) {
  const active = sort?.field === field;
  const direction = active ? sort.direction : "";
  return (
    <th style={s.th}>
      <button type="button" style={s.sortHeader} onClick={() => onSort(field)} aria-label={`Sort by ${label}`}>
        <span>{label}</span>
        <span style={s.sortIcon}>{active ? (direction === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

function ThresholdTypeInfo() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const show = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    setAnchor(rect ? {
      top: Math.max(16, Math.min(rect.bottom + 8, window.innerHeight - 220)),
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 340)),
    } : null);
    setOpen(true);
  };
  return (
    <span style={s.infoWrap} onMouseEnter={show} onMouseLeave={() => setOpen(false)}>
      <button ref={buttonRef} type="button" style={s.infoButton} aria-label="Threshold type information" onClick={(event) => {
        event.stopPropagation();
        if (open) setOpen(false);
        else show();
      }}>i</button>
      {open && (
        <span style={{ ...s.infoPopover, ...(anchor || { top: 180, left: 24 }) }}>
          <strong>Threshold Type</strong>
          <br />
          <strong>Higher value is worse:</strong> standard numeric evaluation. Values at or above Warn show Watch; values at or above Fail show Critical.
          <br />
          <strong>Detected / not detected:</strong> non-numeric flag values such as water/glycol. Numeric pass/warn/fail fields are not used.
          <br />
          <strong>ISO Particle Count:</strong> a three-part global rule. Good is at or below 23/20/17, fail starts at 26/23/20, and the worst component controls the result.
          <br />
          Viscosity range behavior is controlled by the metric and threshold shape, so internal viscosity types are hidden from this menu.
        </span>
      )}
    </span>
  );
}

function InfoTip({ title, children, label }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const show = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    setAnchor(rect ? {
      top: Math.max(16, Math.min(rect.bottom + 8, window.innerHeight - 240)),
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 340)),
    } : null);
    setOpen(true);
  };
  return (
    <span style={s.infoWrap} onMouseEnter={show} onMouseLeave={() => setOpen(false)}>
      <button ref={buttonRef} type="button" style={s.infoButton} aria-label={label || `${title} information`} onClick={(event) => {
        event.stopPropagation();
        if (open) setOpen(false);
        else show();
      }}>i</button>
      {open && (
        <span style={{ ...s.infoPopover, ...(anchor || { top: 180, left: 24 }) }}>
          <strong>{title}</strong>
          <br />
          {children}
        </span>
      )}
    </span>
  );
}

function HelpTitle({ children, help }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span>{children}</span>
      <InfoTip title={children}>{help}</InfoTip>
    </div>
  );
}

function LearningLabIntro() {
  return (
    <details style={s.explainer}>
      <summary style={s.explainerSummary}>How authoritative evidence becomes validated scoring knowledge</summary>
      <p style={s.explainerText}>
        Released expert PDFs define the intended metric colors. The Learning Lab matches each PDF to a pinned report snapshot for identity and full engine context, then compiles clean observations into reusable threshold and conditional-rule updates. It never queries or falls back to the retired live source.
      </p>
      <p style={s.explainerText}>
        Clean evidence does not require individual approval. Only unmatched samples, numeric disagreements, ambiguous extraction or context, contradictory PDFs, and non-inferable rules enter expert review. Scoring changes remain inactive until an Admin validates and publishes them in Threshold Control Panel.
      </p>
    </details>
  );
}

function TypeHeaderCell({ style }) {
  return (
    <th style={{ ...s.th, ...style }}>
      <span style={s.thContent}>Type <ThresholdTypeInfo /></span>
    </th>
  );
}

function compareValues(left, right) {
  const leftEmpty = left === null || left === undefined || left === "";
  const rightEmpty = right === null || right === undefined || right === "";
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;
  const leftDate = typeof left === "string" && /^\d{4}-\d{2}-\d{2}/.test(left) ? new Date(left).getTime() : NaN;
  const rightDate = typeof right === "string" && /^\d{4}-\d{2}-\d{2}/.test(right) ? new Date(right).getTime() : NaN;
  if (!Number.isNaN(leftDate) && !Number.isNaN(rightDate)) return leftDate - rightDate;
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true });
}

function sortRows(rows, sort, accessors) {
  if (!sort?.field || !accessors[sort.field]) return rows;
  const direction = sort.direction === "desc" ? -1 : 1;
  return [...rows].sort((left, right) => compareValues(accessors[sort.field](left), accessors[sort.field](right)) * direction);
}

function defaultDraftName(configType) {
  return configType === "base" ? "Base threshold draft" : "Conditional rules draft";
}

const THRESHOLD_TYPE_OPTIONS = [
  ["GT", "Higher value is worse"],
  ["BOOL", "Detected / not detected"],
];

function editableThresholdType(type) {
  const normalized = type === "ICON" ? "GT" : type || "GT";
  return THRESHOLD_TYPE_OPTIONS.some(([value]) => value === normalized) ? normalized : "GT";
}

function resolvePreviewTemplate(baseConfig, sample) {
  const templates = baseConfig?.templates || {};
  const mapping = baseConfig?.template_selection_rules?.viscosity_class_mapping || {};
  const component = String(sample.component_type || "").trim();
  const fuel = String(sample.vehicle_fuel_type || "").trim();
  const viscosityClass = mapping[sample.oil_viscosity] || "30";

  if (component === "Transmission") return "ATF-PSF";
  if (component === "Gear Box") return viscosityClass === "140" ? "GEAROIL-140" : "GEAROIL-90";
  if (fuel === "Diesel") return templates[`DIESEL-${viscosityClass}`] ? `DIESEL-${viscosityClass}` : "DIESEL-40";
  if (fuel.includes("Leaded") || fuel.includes("Race Fuel")) return templates[`Leaded-${viscosityClass}`] ? `Leaded-${viscosityClass}` : "Leaded-50";
  return templates[`ENGINE-${viscosityClass}`] ? `ENGINE-${viscosityClass}` : "ENGINE-30";
}

function applyPreviewOverrides(template, conditionalConfig, sample) {
  const activeRules = conditionalConfig?.active_rules || {};
  const next = cloneTemplate(template);
  const apply = (metric, threshold, source) => {
    if (!next[metric] || (next[metric].pass === 0 && next[metric].warn === 0 && next[metric].fail === 0)) return;
    next[metric] = { ...next[metric], ...threshold, _conditionalSource: source };
  };

  if (activeRules.FUEL_DILUTION) {
    const fuelRule = String(sample.vehicle_fuel_type || "").toLowerCase().includes("diesel")
      ? activeRules.FUEL_DILUTION.diesel
      : activeRules.FUEL_DILUTION.gasoline;
    apply("FUEL_DILUTION", fuelRule, activeRules.FUEL_DILUTION.source);
  }
  if (activeRules.TIN && sample.molybdenum !== "") {
    const molybdenum = Number(sample.molybdenum);
    if (!Number.isNaN(molybdenum)) {
      apply("TIN", molybdenum > 100 ? activeRules.TIN.molybdenum_gt_100 : activeRules.TIN.molybdenum_lte_100, activeRules.TIN.source);
    }
  }
  for (const metric of ["OXIDATION", "TITANIUM"]) {
    const rule = activeRules[metric];
    if (!rule) continue;
    const brand = normalizeForMatch(sample.oil_brand);
    const brandRule = Object.entries(rule.brand_thresholds || {}).find(([brandName]) =>
      brand.includes(normalizeForMatch(brandName))
    )?.[1];
    if (metric === "OXIDATION") {
      const warn = brandRule?.warn ?? rule.default?.warn ?? rule.warn;
      const fail = brandRule?.fail ?? rule.default?.fail ?? rule.default_fail;
      apply(metric, { pass: 0, warn, fail, type: "GT" }, rule.source);
    } else if (brandRule) {
      apply(metric, { ...brandRule, type: "GT" }, rule.source);
    }
  }
  const wearRateRule = activeRules.WEAR_RATE_PER_1000_MILES;
  const sampleMiles = Number(sample.sample_miles);
  if (wearRateRule && Number.isFinite(sampleMiles) && sampleMiles >= 0) {
    const band = (wearRateRule.bands || []).find((candidate) => sampleMiles >= Number(candidate.min_inclusive || 0)
      && (candidate.max_exclusive === null || candidate.max_exclusive === undefined || sampleMiles < Number(candidate.max_exclusive)));
    if (band) {
      apply("WEAR_PER_THOUSAND", {
        pass: 0,
        warn: Number(band.normal_upper),
        fail: Number(band.warning_upper),
        type: "GT",
        boundaryMode: wearRateRule.boundary_mode || "upper_limits",
        mileageBand: band.label || band.key,
      }, wearRateRule.source);
    }
  }
  return next;
}

export default function AdminDashboard() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isPowerAdmin = Boolean(user?.canViewAdminDashboard);
  const canManageAlphaTesters = isPowerAdmin || user?.role === "admin";
  const isLearningLabPage = location.pathname === "/admin/learning-lab";
  const [modules, setModules] = useState(null);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showOutstandingModal, setShowOutstandingModal] = useState(false);
  const [outstandingSamples, setOutstandingSamples] = useState([]);
  const [outstandingTotal, setOutstandingTotal] = useState(0);
  const [outstandingLimit, setOutstandingLimit] = useState(10);
  const [outstandingPage, setOutstandingPage] = useState(0);
  const [outstandingSearch, setOutstandingSearch] = useState("");
  const [outstandingSort, setOutstandingSort] = useState({ field: "", direction: "asc" });
  const [selectedOutstandingSample, setSelectedOutstandingSample] = useState(null);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archivedSamples, setArchivedSamples] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [alertStatus, setAlertStatus] = useState("review_pending");
  const [alertType, setAlertType] = useState("all");
  const [thresholds, setThresholds] = useState(null);
  const [activeType, setActiveType] = useState("base");
  const [thresholdPanel, setThresholdPanel] = useState("base");
  const [baseDraft, setBaseDraft] = useState(null);
  const [conditionalDraft, setConditionalDraft] = useState(null);
  const [draftNames, setDraftNames] = useState({ base: "Base threshold draft", conditional: "Conditional rules draft" });
  const [activeDraftIds, setActiveDraftIds] = useState({ base: "", conditional: "" });
  const [thresholdSaveNotice, setThresholdSaveNotice] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [previewSample, setPreviewSample] = useState({
    component_type: "Engine",
    vehicle_fuel_type: "Gasoline",
    oil_viscosity: "5W-30",
    oil_brand: "AMSOIL",
    molybdenum: 150,
    sample_miles: 5200,
  });
  const [note, setNote] = useState("");
  const [message, setMessage] = useState(null);
  const [dashboardModal, setDashboardModal] = useState(null);
  const [listModal, setListModal] = useState(null);
  const [customerRows, setCustomerRows] = useState([]);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerLimit, setCustomerLimit] = useState(10);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerSort, setCustomerSort] = useState({ field: "", direction: "asc" });
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomerDetail, setSelectedCustomerDetail] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportLimit, setReportLimit] = useState(10);
  const [reportPage, setReportPage] = useState(0);
  const [reportSearch, setReportSearch] = useState("");
  const [reportComponentType, setReportComponentType] = useState("");
  const [reportSort, setReportSort] = useState({ field: "", direction: "asc" });
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedReportDetail, setSelectedReportDetail] = useState(null);
  const [reviewQueueRows, setReviewQueueRows] = useState([]);
  const [reviewQueueTotal, setReviewQueueTotal] = useState(0);
  const [reviewQueueLimit, setReviewQueueLimit] = useState(10);
  const [reviewQueuePage, setReviewQueuePage] = useState(0);
  const [reviewQueueSearch, setReviewQueueSearch] = useState("");
  const [reviewQueueSort, setReviewQueueSort] = useState({ field: "queued", direction: "desc" });
  const [reviewQueueLoading, setReviewQueueLoading] = useState(false);
  const [reviewQueueSettings, setReviewQueueSettings] = useState(null);
  const [reviewStartDateInput, setReviewStartDateInput] = useState("");
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [alphaTesters, setAlphaTesters] = useState([]);
  const [alphaTestersLoading, setAlphaTestersLoading] = useState(false);
  const [alphaInviteEmail, setAlphaInviteEmail] = useState("");
  const [alphaManualInvite, setAlphaManualInvite] = useState(null);
  const [alphaCustomerSearch, setAlphaCustomerSearch] = useState("");
  const [alphaCustomerResults, setAlphaCustomerResults] = useState([]);
  const [alphaCustomerSearchHasRun, setAlphaCustomerSearchHasRun] = useState(false);
  const [alphaCustomerSearchLoading, setAlphaCustomerSearchLoading] = useState(false);
  const [alphaCustomerInvitingEmail, setAlphaCustomerInvitingEmail] = useState("");
  const [customerAlphaInvitingEmail, setCustomerAlphaInvitingEmail] = useState("");
  const [customerAlphaInviteNotice, setCustomerAlphaInviteNotice] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteAccess, setInviteAccess] = useState({});
  const [excelArtemisRows, setExcelArtemisRows] = useState([]);
  const [excelArtemisTotal, setExcelArtemisTotal] = useState(0);
  const [excelArtemisDecisionSummary, setExcelArtemisDecisionSummary] = useState({});
  const [excelArtemisLimit, setExcelArtemisLimit] = useState(10);
  const [excelArtemisPage, setExcelArtemisPage] = useState(0);
  const [excelArtemisSearch, setExcelArtemisSearch] = useState("");
  const [excelArtemisIssueType, setExcelArtemisIssueType] = useState("all");
  const [excelArtemisDecisionStatus, setExcelArtemisDecisionStatus] = useState("all");
  const [excelArtemisLoading, setExcelArtemisLoading] = useState(false);
  const [selectedExcelArtemisDetail, setSelectedExcelArtemisDetail] = useState(null);
  const [thresholdLearning, setThresholdLearning] = useState(null);
  const [thresholdLearningLoading, setThresholdLearningLoading] = useState(false);
  const [learningOperations, setLearningOperations] = useState(null);
  const [learningOperationsLoading, setLearningOperationsLoading] = useState(false);
  const [guidanceTitle, setGuidanceTitle] = useState("");
  const [guidanceCategory, setGuidanceCategory] = useState("general");
  const [guidanceText, setGuidanceText] = useState("");
  const [learningUploadFile, setLearningUploadFile] = useState(null);
  const [learningUploadLimit, setLearningUploadLimit] = useState("250");
  const [learningUploadAiEnabled, setLearningUploadAiEnabled] = useState(false);
  const [proposalReviewNotes, setProposalReviewNotes] = useState({});
  const [thresholdDraftPreview, setThresholdDraftPreview] = useState(null);
  const [reportSnapshot, setReportSnapshot] = useState(null);
  const [reportDataSource, setReportDataSource] = useState(null);
  const [reportSnapshotLoading, setReportSnapshotLoading] = useState(false);
  const [snapshotSchedule, setSnapshotSchedule] = useState(null);
  const [savedSnapshotSchedule, setSavedSnapshotSchedule] = useState(null);
  const [snapshotScheduleDirty, setSnapshotScheduleDirty] = useState(false);
  const [releaseQueue,setReleaseQueue]=useState({reports:[],total:0});
  const [historyRows,setHistoryRows]=useState([]);
  const snapshotScheduleDirtyRef = useRef(false);
  useEffect(() => { snapshotScheduleDirtyRef.current = snapshotScheduleDirty; }, [snapshotScheduleDirty]);

  const activeDraft = activeType === "base" ? baseDraft : conditionalDraft;
  const activeDraftList = thresholds?.draftLists?.[activeType] || [];
  const activeSelectionId = activeDraftIds[activeType] || "";
  const activeSelectionIsPublished = typeof activeSelectionId === "string" && activeSelectionId.startsWith("published:");
  const activeDraftId = activeSelectionIsPublished ? "" : activeSelectionId;
  const activeDraftMeta = activeDraftList.find((draft) => draft.id === activeDraftId) || null;
  const activePublishedMeta = thresholds?.published?.[activeType];
  const activePublishedOptionId = activePublishedMeta?.id ? `published:${activePublishedMeta.id}` : `published:${activeType}`;
  const activeDraftName = draftNames[activeType] || "";
  const templateNames = useMemo(() => Object.keys(baseDraft?.templates || {}), [baseDraft]);
  const previewTemplateName = useMemo(() => resolvePreviewTemplate(baseDraft, previewSample), [baseDraft, previewSample]);
  const previewThresholds = useMemo(() => {
    const template = baseDraft?.templates?.[previewTemplateName];
    return applyPreviewOverrides(template, conditionalDraft, previewSample);
  }, [baseDraft, conditionalDraft, previewSample, previewTemplateName]);
  const moduleMap = useMemo(() => {
    const map = {};
    (modules || []).forEach((module) => { map[module.key] = Boolean(module.enabledForAdmin); });
    return map;
  }, [modules]);
  const canSeeModule = (moduleKey) => isPowerAdmin || Boolean(moduleMap[moduleKey]);

  async function loadAll() {
    const moduleRes = await getAdminModules();
    const nextModules = moduleRes.data.modules || [];
    const nextModuleMap = {};
    nextModules.forEach((module) => { nextModuleMap[module.key] = Boolean(module.enabledForAdmin); });
    const shouldLoad = (moduleKey) => isPowerAdmin || Boolean(nextModuleMap[moduleKey]);
    setModules(nextModules);

    const [summaryRes, alertsRes, thresholdRes, adminUsersRes, reviewQueueRes, learningRes, snapshotRes, sourceRes, alphaRes, operationsRes] = await Promise.all([
      (shouldLoad("general_stats") || shouldLoad("excel_artemis_review") || shouldLoad("threshold_learning_lab")) ? getAdminSummary() : Promise.resolve({ data: null }),
      shouldLoad("alerts") ? getAdminAlerts(alertStatus, alertType) : Promise.resolve({ data: { alerts: [] } }),
      shouldLoad("thresholds") ? getAdminThresholds() : Promise.resolve({ data: null }),
      shouldLoad("admin_users") ? getAdminUsers() : Promise.resolve({ data: { users: [] } }),
      shouldLoad("report_review_queue")
        ? getAdminReportReviewQueue(reviewQueueLimit, 0, "", reviewQueueSort)
        : Promise.resolve({ data: { settings: null, reports: [], total: 0 } }),
      shouldLoad("threshold_learning_lab") ? getAdminThresholdLearningLab() : Promise.resolve({ data: null }),
      shouldLoad("report_snapshot") ? getAdminReportSnapshot() : Promise.resolve({ data: null }),
      getAdminReportDataSource(),
      canManageAlphaTesters ? getAdminAlphaTesters() : Promise.resolve({ data: { testers: [] } }),
      isPowerAdmin && shouldLoad("threshold_learning_lab") ? getAdminLearningOperations() : Promise.resolve({ data: null }),
    ]);
    setSummary(summaryRes.data);
    setAlerts(alertsRes.data?.alerts || []);
    setAdminUsers(adminUsersRes.data?.users || []);
    setAlphaTesters(alphaRes.data?.testers || []);
    setReviewQueueRows(reviewQueueRes.data?.reports || []);
    setReviewQueueTotal(reviewQueueRes.data?.total || 0);
    setReviewQueuePage(0);
    setReviewQueueSearch("");
    setReviewQueueSettings(reviewQueueRes.data?.settings || null);
    setReviewStartDateInput(reviewQueueRes.data?.settings?.reviewStartDate || "");
    setThresholdLearning(learningRes.data || null);
    setLearningOperations(operationsRes.data || null);
    setReportSnapshot(snapshotRes.data || null);
    setReportDataSource(sourceRes.data || null);
    setSnapshotSchedule(snapshotRes.data?.settings || null);
    setSavedSnapshotSchedule(snapshotRes.data?.settings || null);
    setSnapshotScheduleDirty(false);
    const thresholdData = thresholdRes.data ? cloneThresholdConfig(thresholdRes.data) : null;
    setThresholds(thresholdData);
    if (!thresholdData) {
      setBaseDraft(null);
      setConditionalDraft(null);
      return;
    }
    const base = cloneThresholdConfig(thresholdData.drafts?.base?.config || thresholdData.published?.base?.config);
    const conditional = cloneThresholdConfig(thresholdData.drafts?.conditional?.config || thresholdData.published?.conditional?.config);
    const baseDraftList = thresholdData.draftLists?.base || [];
    const conditionalDraftList = thresholdData.draftLists?.conditional || [];
    setBaseDraft(base);
    setConditionalDraft(conditional);
    setDraftNames({
      base: baseDraftList[0]?.draftName || "Base threshold draft",
      conditional: conditionalDraftList[0]?.draftName || "Conditional rules draft",
    });
    setActiveDraftIds({
      base: baseDraftList[0]?.id || "",
      conditional: conditionalDraftList[0]?.id || "",
    });
    setTemplateName(Object.keys(base.templates || {})[0] || "");
  }

  useEffect(() => {
    loadAll().catch(err => setMessage({ type: "err", text: err.response?.data?.error || "Failed to load admin dashboard." }));
  }, []);

  useEffect(() => {
    if (!isLearningLabPage && new URLSearchParams(location.search).get("section") === "reports") setDashboardModal("reports");
  }, [isLearningLabPage, location.search]);

  useEffect(() => {
    if (!canSeeModule("report_snapshot")) return undefined;
    let timer;
    let cancelled = false;
    const poll = async () => {
      if (document.visibilityState === "hidden") {
        timer = window.setTimeout(poll, 60000);
        return;
      }
      try {
        const res = await getAdminReportSnapshot();
        if (cancelled) return;
        setReportSnapshot(res.data);
        setSavedSnapshotSchedule(res.data.settings);
        if (!snapshotScheduleDirtyRef.current) setSnapshotSchedule(res.data.settings);
      } catch (_) {
        // Keep the existing status visible; the next poll can recover.
      }
      if (!cancelled) timer = window.setTimeout(poll, reportSnapshot?.currentJob ? 5000 : 60000);
    };
    timer = window.setTimeout(poll, reportSnapshot?.currentJob ? 5000 : 60000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [moduleMap.report_snapshot, isPowerAdmin, reportSnapshot?.currentJob?.id]);

  async function saveSnapshotSchedule() {
    setReportSnapshotLoading(true);
    setMessage(null);
    try {
      const res = await updateAdminReportSnapshotSchedule(snapshotSchedule);
      setSnapshotSchedule(res.data.settings);
      setSavedSnapshotSchedule(res.data.settings);
      setSnapshotScheduleDirty(false);
      setReportSnapshot((current) => ({ ...current, settings: res.data.settings }));
      setMessage({ type: "ok", text: "Report snapshot schedule updated." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to update snapshot schedule." });
    } finally {
      setReportSnapshotLoading(false);
    }
  }

  async function openReleaseQueue(){setDashboardModal("release");try{const res=await getAdminReleaseQueue();setReleaseQueue(res.data);}catch(err){setMessage({type:"err",text:err.response?.data?.error||"Unable to load the release queue."});}}
  async function releaseOne(sampleId){try{await releaseAdminReport(sampleId);await openReleaseQueue();setMessage({type:"ok",text:`${sampleId} released.`});}catch(err){setMessage({type:"err",text:err.response?.data?.error||"Unable to release report."});}}
  async function releaseAll(){const ids=releaseQueue.reports.map((r)=>r.labSampleId);if(!ids.length)return;if(!window.confirm(`Release all ${ids.length} reports currently shown to customers?`))return;try{const res=await releaseAdminReportBatch(ids);await openReleaseQueue();setMessage({type:"ok",text:`Released ${res.data.released.length} report(s).`});}catch(err){setMessage({type:"err",text:err.response?.data?.error||"Batch release failed."});}}
  async function openHistory(){setDashboardModal("history");try{const res=await getAdminHistory({limit:200});setHistoryRows(res.data.events||[]);}catch(err){setMessage({type:"err",text:err.response?.data?.error||"Unable to load History."});}}
  async function restoreHistoryEvent(id){if(!window.confirm("Validate this historical configuration against current evidence and publish it as a new version?"))return;try{await restoreAdminHistory(id);await openHistory();setMessage({type:"ok",text:"Historical configuration validated and restored as a new published version."});}catch(err){setMessage({type:"err",text:err.response?.data?.error||"This historical version cannot be restored."});}}

  async function updateReportSnapshotNow() {
    setReportSnapshotLoading(true);
    setMessage(null);
    try {
      await createAdminReportSnapshotJob();
      const res = await getAdminReportSnapshot();
      setReportSnapshot(res.data);
      setSavedSnapshotSchedule(res.data.settings);
      if (!snapshotScheduleDirtyRef.current) setSnapshotSchedule(res.data.settings);
      setMessage({ type: "ok", text: "Snapshot update queued." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to queue snapshot update." });
    } finally {
      setReportSnapshotLoading(false);
    }
  }

  async function toggleSnapshotPin() {
    if (!isPowerAdmin) return;
    setReportSnapshotLoading(true);
    setMessage(null);
    try {
      const enable = !reportSnapshot?.sourceStatus?.pinned;
      const res = await updateAdminReportSnapshotPin(enable);
      const refreshed = await getAdminReportSnapshot();
      setReportSnapshot(refreshed.data);
      setSavedSnapshotSchedule(refreshed.data.settings);
      if (!snapshotScheduleDirtyRef.current) setSnapshotSchedule(refreshed.data.settings);
      setReportDataSource(res.data.sourceStatus);
      setMessage({ type: "ok", text: enable ? "Snapshot pin enabled." : "Snapshot pin released." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to update snapshot pin." });
    } finally {
      setReportSnapshotLoading(false);
    }
  }

  async function reloadAlerts(nextStatus = alertStatus, nextType = alertType) {
    setAlertStatus(nextStatus);
    setAlertType(nextType);
    const res = await getAdminAlerts(nextStatus, nextType);
    setAlerts(res.data.alerts || []);
  }

  async function sendAlphaFeedbackReply(alert, reply) {
    const res = await replyToAdminAlphaFeedback(alert.id, reply);
    setSelectedAlert((current) => current?.id === alert.id ? { ...current, ...res.data } : current);
    setAlerts((current) => current.map((item) => item.id === alert.id ? { ...item, ...res.data } : item));
    setSummary((current) => current ? { ...current, pendingAlerts: Math.max(0, Number(current.pendingAlerts || 0) - (alert.status === "review_pending" ? 1 : 0)) } : current);
    setMessage({ type: "ok", text: `Reply sent to ${alert.email}.` });
    return res.data;
  }

  async function recordLearningOperationsAudit() {
    setLearningOperationsLoading(true);
    setMessage(null);
    try {
      await recordAdminLearningOperationsAudit();
      const res = await getAdminLearningOperations();
      setLearningOperations(res.data);
      setMessage({ type: "ok", text: "Learning Lab operational audit recorded." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to record the Learning Lab operational audit." });
    } finally { setLearningOperationsLoading(false); }
  }

  async function toggleAdminDashboardAccess(adminUser) {
    if (!isPowerAdmin) return;
    setAdminUsersLoading(true);
    setMessage(null);
    try {
      const res = await updateAdminDashboardAccess(adminUser.email, {
        canViewAdminDashboard: !adminUser.canViewAdminDashboard,
      });
      setAdminUsers((users) => users.map((user) => user.email === adminUser.email ? res.data.user : user));
      setMessage({ type: "ok", text: `${res.data.user.email} access updated.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to update admin access." });
    } finally {
      setAdminUsersLoading(false);
    }
  }

  async function sendAdminInvite(event) {
    event.preventDefault();
    if (!isPowerAdmin) return;
    setAdminUsersLoading(true);
    setMessage(null);
    try {
      const moduleAccess = (modules || []).map((module) => ({
        key: module.key,
        enabled: Boolean(inviteAccess[module.key]),
      }));
      const res = await inviteAdminUser({
        email: inviteEmail,
        customerName: inviteName,
        moduleAccess,
      });
      setAdminUsers((users) => {
        const existing = users.some((user) => user.email === res.data.user.email);
        return existing
          ? users.map((user) => user.email === res.data.user.email ? res.data.user : user)
          : [...users, res.data.user].sort((a, b) => String(a.email).localeCompare(String(b.email)));
      });
      setInviteEmail("");
      setInviteName("");
      setInviteAccess({});
      setMessage({ type: "ok", text: `Invitation sent to ${res.data.user.email}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to send collaborator invite." });
    } finally {
      setAdminUsersLoading(false);
    }
  }

  async function toggleAdminUserModule(adminUser, moduleKey, enabled) {
    if (!isPowerAdmin || adminUser.canViewAdminDashboard) return;
    setAdminUsersLoading(true);
    setMessage(null);
    try {
      const res = await updateAdminUserModuleAccess(adminUser.email, moduleKey, { enabled });
      setAdminUsers((users) => users.map((user) => user.email === adminUser.email ? res.data.user : user));
      setMessage({ type: "ok", text: `${res.data.user.email} section access updated.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to update section access." });
    } finally {
      setAdminUsersLoading(false);
    }
  }

  async function toggleAdminModule(module) {
    if (!isPowerAdmin) return;
    setModulesLoading(true);
    setMessage(null);
    try {
      const res = await updateAdminModuleVisibility(module.key, {
        enabledForAdmin: !module.enabledForAdmin,
      });
      setModules((current) => (current || []).map((item) => item.key === module.key ? res.data.module : item));
      setMessage({ type: "ok", text: `${res.data.module.label} visibility updated.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to update module visibility." });
    } finally {
      setModulesLoading(false);
    }
  }

  async function openCustomers(limit = customerLimit, page = customerPage, search = customerSearch, sort = customerSort) {
    setListModal("customers");
    setCustomerLoading(true);
    setMessage(null);
    try {
      const res = await getAdminCustomers(limit, page * limit, search, sort);
      setCustomerRows(res.data.customers || []);
      setCustomerTotal(res.data.total || 0);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load customers." });
    } finally {
      setCustomerLoading(false);
    }
  }

  async function inviteCustomerAsAlpha(customer) {
    const email = String(customer?.customerEmail || "").trim().toLowerCase();
    if (!email || customer?.account?.isAlphaTester || (customer?.account && customer.account.role !== "customer")) return;
    if (!window.confirm(`Invite ${email} as an Alpha Tester? This will grant alpha access and send the password-setup email.`)) return;
    setCustomerAlphaInvitingEmail(email);
    setCustomerAlphaInviteNotice(null);
    setMessage(null);
    try {
      const res = await inviteAdminAlphaTester({ email, customerName: customer.customerName || "" });
      const tester = res.data.tester;
      setCustomerRows((rows) => rows.map((row) => row.customerEmail.toLowerCase() === email ? { ...row, account: tester } : row));
      setAlphaTesters((current) => [tester, ...current.filter((item) => item.email !== tester.email)]);
      setCustomerAlphaInviteNotice({
        type: res.data.inviteLink ? "err" : "ok",
        text: res.data.message || `Alpha invitation sent to ${tester.email}.`,
        email: tester.email,
        link: res.data.inviteLink || "",
      });
      setMessage({ type: res.data.inviteLink ? "err" : "ok", text: res.data.message || `Alpha invitation sent to ${tester.email}.` });
    } catch (err) {
      const text = err.response?.data?.error || "Failed to send alpha invitation.";
      setCustomerAlphaInviteNotice({ type: "err", text, email, link: "" });
      setMessage({ type: "err", text });
    } finally {
      setCustomerAlphaInvitingEmail("");
    }
  }

  function updateCustomerLimit(value) {
    const nextLimit = Number(value) || 10;
    setCustomerLimit(nextLimit);
    setCustomerPage(0);
    openCustomers(nextLimit, 0, customerSearch, customerSort);
  }

  function updateCustomerPage(nextPage) {
    const safePage = Math.max(nextPage, 0);
    setCustomerPage(safePage);
    openCustomers(customerLimit, safePage, customerSearch, customerSort);
  }

  function updateCustomerSearch(value) {
    setCustomerSearch(value);
    setCustomerPage(0);
    openCustomers(customerLimit, 0, value, customerSort);
  }

  function updateCustomerSort(field) {
    const nextSort = toggleSort(customerSort, field);
    setCustomerSort(nextSort);
    setCustomerPage(0);
    openCustomers(customerLimit, 0, customerSearch, nextSort);
  }

  async function openCustomerDetail(email) {
    setCustomerLoading(true);
    setMessage(null);
    try {
      const res = await getAdminCustomerDetail(email);
      setSelectedCustomerDetail(res.data);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load customer details." });
    } finally {
      setCustomerLoading(false);
    }
  }

  async function saveCustomerAccount(email, role, status) {
    setMessage(null);
    try {
      const res = await updateAdminCustomerAccount(email, { role, status });
      setSelectedCustomerDetail((current) => current ? {
        ...current,
        customer: { ...current.customer, account: res.data.account },
      } : current);
      setCustomerRows((rows) => rows.map((row) => row.customerEmail === email ? { ...row, account: res.data.account } : row));
      setMessage({ type: "ok", text: "Customer account updated." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to update customer account." });
    }
  }

  async function sendAlphaInvite() {
    setAlphaTestersLoading(true);
    setMessage(null);
    setAlphaManualInvite(null);
    try {
      const res = await inviteAdminAlphaTester({ email: alphaInviteEmail });
      setAlphaTesters((current) => [res.data.tester, ...current.filter((tester) => tester.email !== res.data.tester.email)]);
      setAlphaInviteEmail("");
      setAlphaManualInvite(res.data.inviteLink ? { email: res.data.tester.email, link: res.data.inviteLink } : null);
      setMessage({ type: res.data.inviteLink ? "err" : "ok", text: res.data.message || `Alpha invitation sent to ${res.data.tester.email}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to send alpha invitation." });
    } finally {
      setAlphaTestersLoading(false);
    }
  }

  async function searchAlphaCustomers() {
    const search = alphaCustomerSearch.trim();
    if (!search) {
      setAlphaCustomerResults([]);
      setAlphaCustomerSearchHasRun(false);
      return;
    }
    setAlphaCustomerSearchLoading(true);
    setAlphaCustomerSearchHasRun(false);
    setMessage(null);
    try {
      const res = await searchAdminAlphaTesterCustomers(search);
      setAlphaCustomerResults(res.data.customers || []);
      setAlphaCustomerSearchHasRun(true);
    } catch (err) {
      setAlphaCustomerResults([]);
      setAlphaCustomerSearchHasRun(false);
      setMessage({ type: "err", text: err.response?.data?.error || "Customer search failed." });
    } finally {
      setAlphaCustomerSearchLoading(false);
    }
  }

  async function inviteAlphaSearchCustomer(customer) {
    const email = String(customer?.customerEmail || "").trim().toLowerCase();
    if (!email || customer?.account?.isAlphaTester || (customer?.account && customer.account.role !== "customer")) return;
    if (!window.confirm(`Invite ${email} as an Alpha Tester? This will grant alpha access and send the password-setup email.`)) return;
    setAlphaCustomerInvitingEmail(email);
    setAlphaManualInvite(null);
    setMessage(null);
    try {
      const res = await inviteAdminAlphaTester({ email });
      const tester = res.data.tester;
      setAlphaTesters((current) => [tester, ...current.filter((item) => item.email !== tester.email)]);
      setAlphaCustomerResults((current) => current.map((row) => row.customerEmail.toLowerCase() === email ? { ...row, account: tester } : row));
      setCustomerRows((current) => current.map((row) => row.customerEmail.toLowerCase() === email ? { ...row, account: tester } : row));
      setAlphaManualInvite(res.data.inviteLink ? { email: tester.email, link: res.data.inviteLink } : null);
      setMessage({ type: res.data.inviteLink ? "err" : "ok", text: res.data.message || `Alpha invitation sent to ${tester.email}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to send alpha invitation." });
    } finally {
      setAlphaCustomerInvitingEmail("");
    }
  }

  async function changeAlphaAccess(email, status) {
    setAlphaTestersLoading(true);
    setMessage(null);
    setAlphaManualInvite(null);
    try {
      const res = await updateAdminAlphaTesterStatus(email, status);
      setAlphaTesters((current) => current.map((tester) => tester.email === email ? res.data.tester : tester));
      setMessage({ type: "ok", text: `${email} is now ${status}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to update alpha access." });
    } finally {
      setAlphaTestersLoading(false);
    }
  }

  async function holdAllAlphaAccess() {
    const eligibleCount = alphaTesters.filter((tester) => ["invited", "active"].includes(tester.status)).length;
    if (eligibleCount === 0) return;
    if (!window.confirm(`Put all ${eligibleCount} active or invited Alpha Tester${eligibleCount === 1 ? "" : "s"} on hold? This blocks dashboard access on their next authenticated request. Testers already held or revoked will remain unchanged.`)) return;
    setAlphaTestersLoading(true);
    setMessage(null);
    setAlphaManualInvite(null);
    try {
      const res = await holdAllAdminAlphaTesters();
      setAlphaTesters(res.data.testers || []);
      setMessage({ type: "ok", text: res.data.message || `${res.data.heldCount || 0} Alpha Testers placed on hold.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to place all Alpha Testers on hold." });
    } finally {
      setAlphaTestersLoading(false);
    }
  }

  async function resendAlphaInvite(email) {
    setAlphaTestersLoading(true);
    setMessage(null);
    setAlphaManualInvite(null);
    try {
      const res = await resendAdminAlphaTesterInvite(email);
      setAlphaManualInvite(res.data.inviteLink ? { email, link: res.data.inviteLink } : null);
      setMessage({ type: res.data.inviteLink ? "err" : "ok", text: res.data.message || `A new 72-hour invitation was sent to ${email}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to resend alpha invitation." });
    } finally {
      setAlphaTestersLoading(false);
    }
  }

  const reportComponentOptions = useMemo(() => Object.entries(summary?.reportTypeCounts || {})
    .map(([type, count]) => ({ type, count: Number(count || 0) }))
    .filter((entry) => entry.type && entry.count > 0)
    .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type)), [summary?.reportTypeCounts]);

  async function openReports(limit = reportLimit, page = reportPage, search = reportSearch, sort = reportSort, componentType = reportComponentType) {
    setListModal("reports");
    setReportLoading(true);
    setMessage(null);
    try {
      const res = await getAdminReports(limit, page * limit, search, sort, componentType);
      setReportRows(res.data.reports || []);
      setReportTotal(res.data.total || 0);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load reports." });
    } finally {
      setReportLoading(false);
    }
  }

  async function openExcelArtemisReview(
    limit = excelArtemisLimit,
    page = excelArtemisPage,
    search = excelArtemisSearch,
    issueType = excelArtemisIssueType,
    decisionStatus = excelArtemisDecisionStatus
  ) {
    setListModal("excelArtemis");
    setExcelArtemisLoading(true);
    setMessage(null);
    try {
      const res = await getAdminExcelArtemisReview(limit, page * limit, search, issueType, decisionStatus);
      setExcelArtemisRows(res.data.items || []);
      setExcelArtemisTotal(res.data.total || 0);
      setExcelArtemisDecisionSummary(res.data.decisionSummary || {});
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load PDF / Snapshot comparison review." });
    } finally {
      setExcelArtemisLoading(false);
    }
  }

  function openExcelArtemisReviewFiltered({ issueType = "all", decisionStatus = "all", search = excelArtemisSearch } = {}) {
    setExcelArtemisIssueType(issueType);
    setExcelArtemisDecisionStatus(decisionStatus);
    setExcelArtemisSearch(search);
    setExcelArtemisPage(0);
    openExcelArtemisReview(excelArtemisLimit, 0, search, issueType, decisionStatus);
  }

  function updateExcelArtemisLimit(value) {
    const nextLimit = Number(value) || 10;
    setExcelArtemisLimit(nextLimit);
    setExcelArtemisPage(0);
    openExcelArtemisReview(nextLimit, 0, excelArtemisSearch, excelArtemisIssueType, excelArtemisDecisionStatus);
  }

  function updateExcelArtemisPage(nextPage) {
    const safePage = Math.max(nextPage, 0);
    setExcelArtemisPage(safePage);
    openExcelArtemisReview(excelArtemisLimit, safePage, excelArtemisSearch, excelArtemisIssueType, excelArtemisDecisionStatus);
  }

  function updateExcelArtemisSearch(value) {
    setExcelArtemisSearch(value);
    setExcelArtemisPage(0);
    openExcelArtemisReview(excelArtemisLimit, 0, value, excelArtemisIssueType, excelArtemisDecisionStatus);
  }

  function updateExcelArtemisIssueType(value) {
    setExcelArtemisIssueType(value);
    setExcelArtemisPage(0);
    openExcelArtemisReview(excelArtemisLimit, 0, excelArtemisSearch, value, excelArtemisDecisionStatus);
  }

  function updateExcelArtemisDecisionStatus(value) {
    setExcelArtemisDecisionStatus(value);
    setExcelArtemisPage(0);
    openExcelArtemisReview(excelArtemisLimit, 0, excelArtemisSearch, excelArtemisIssueType, value);
  }

  async function openExcelArtemisDetail(id) {
    setExcelArtemisLoading(true);
    setMessage(null);
    try {
      const res = await getAdminExcelArtemisReviewDetail(id);
      setSelectedExcelArtemisDetail(res.data.item);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load PDF / Snapshot review detail." });
    } finally {
      setExcelArtemisLoading(false);
    }
  }

  async function saveExcelArtemisAnnotation(id, data) {
    setExcelArtemisLoading(true);
    setMessage(null);
    try {
      const res = await saveAdminExcelArtemisReviewAnnotation(id, data);
      setSelectedExcelArtemisDetail((current) => current ? { ...current, annotation: res.data.annotation } : current);
      setExcelArtemisRows((rows) => rows.map((row) => row.id === id ? { ...row, annotation: res.data.annotation } : row));
      await openExcelArtemisReview(excelArtemisLimit, excelArtemisPage, excelArtemisSearch, excelArtemisIssueType, excelArtemisDecisionStatus);
      await reloadThresholdLearning();
      setMessage({ type: "ok", text: "PDF / Snapshot review note saved." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to save PDF / Snapshot review note." });
    } finally {
      setExcelArtemisLoading(false);
    }
  }

  async function reloadThresholdLearning() {
    if (!canSeeModule("threshold_learning_lab")) return;
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const res = await getAdminThresholdLearningLab();
      setThresholdLearning(res.data);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load threshold learning lab." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function saveLearningGuidance(event) {
    event?.preventDefault?.();
    if (!canSeeModule("threshold_learning_lab")) return;
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const res = await saveAdminThresholdLearningGuidance({
        title: guidanceTitle,
        category: guidanceCategory,
        guidanceText,
      });
      setThresholdLearning((current) => current ? {
        ...current,
        guidance: [res.data.guidance, ...(current.guidance || [])],
      } : current);
      setGuidanceTitle("");
      setGuidanceText("");
      setGuidanceCategory("general");
      setMessage({ type: "ok", text: "Expert guidance saved for future threshold-learning analysis." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to save expert guidance." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function uploadThresholdLearningArchive(event) {
    event?.preventDefault?.();
    if (!canSeeModule("threshold_learning_lab")) return;
    if (!learningUploadFile) {
      setMessage({ type: "err", text: "Choose a .zip archive of completed PDF reports before starting a comparison run." });
      return;
    }
    const fileError=validateLearningLabFile(learningUploadFile);
    if(fileError){setMessage({type:"err",text:fileError});return;}
    setThresholdLearningLoading(true);
    setMessage({ type: "ok", text: "Uploading archive and starting threshold comparison..." });
    try {
      const fileBase64 = await fileToBase64(learningUploadFile);
      await uploadAdminThresholdLearningArchive({
        fileName: learningUploadFile.name,
        fileBase64,
        runLimit: Number(learningUploadLimit) || 250,
        aiEnabled: learningUploadAiEnabled,
        mode: "dry_run",
      });
      setLearningUploadFile(null);
      setLearningUploadLimit("250");
      setLearningUploadAiEnabled(false);
      await reloadThresholdLearning();
      setMessage({ type: "ok", text: "Comparison job started. The lab will show progress while it runs." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || err.message || "Failed to start comparison run." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function rebuildAuthoritativePdfLearning() {
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const res = await rebuildAdminPdfLearning();
      await reloadThresholdLearning();
      setMessage({ type: "ok", text: "Authoritative PDF inference rebuilt from all effective evidence." });
      return res.data;
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to rebuild PDF-learned rules." });
      throw err;
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function createAuthoritativePdfDrafts() {
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const runId = thresholdLearning?.learnedRulePreview?.run?.id;
      const res = await createAdminPdfLearningDrafts(runId);
      await reloadThresholdLearning();
      setMessage({ type: "ok", text: `Created ${res.data.drafts?.length || 0} draft configuration${res.data.drafts?.length === 1 ? "" : "s"}. Review and publish them in Threshold Control Panel.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to create PDF-learned threshold drafts." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function generateThresholdLearningProposals() {
    if (!canSeeModule("threshold_learning_lab")) return;
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const res = await generateAdminThresholdLearningProposals();
      await reloadThresholdLearning();
      const created = res.data.created?.length || 0;
      const skipped = res.data.skipped?.length || 0;
      setMessage({ type: "ok", text: `Generated ${created} proposal${created === 1 ? "" : "s"}${skipped ? `; skipped ${skipped} existing proposal${skipped === 1 ? "" : "s"}.` : "."}` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to generate threshold proposals." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function reviewThresholdLearningProposal(id, status) {
    if (!canSeeModule("threshold_learning_lab")) return;
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const res = await reviewAdminThresholdLearningProposal(id, {
        status,
        note: proposalReviewNotes[id] || "",
      });
      setThresholdLearning((current) => current ? {
        ...current,
        proposals: (current.proposals || []).map((proposal) => proposal.id === id ? res.data.proposal : proposal),
      } : current);
      setMessage({ type: "ok", text: `Proposal marked ${status}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to review proposal." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function previewThresholdDraftFromAcceptedProposals() {
    if (!canSeeModule("threshold_learning_lab")) return;
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const res = await previewAdminThresholdDraftFromAcceptedProposals();
      setThresholdDraftPreview(res.data);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to preview threshold draft changes." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  async function createThresholdDraftFromAcceptedProposals() {
    if (!canSeeModule("threshold_learning_lab")) return;
    setThresholdLearningLoading(true);
    setMessage(null);
    try {
      const res = await createAdminThresholdDraftFromAcceptedProposals();
      await reloadThresholdLearning();
      const thresholdRes = canSeeModule("thresholds") ? await getAdminThresholds() : null;
      if (thresholdRes?.data) {
        const thresholdData = cloneThresholdConfig(thresholdRes.data);
        setThresholds(thresholdData);
        const generatedDraftId = res.data.drafts?.[0]?.id || res.data.draft?.id;
        const generatedDraft = [...(thresholdData.draftLists?.base || []), ...(thresholdData.draftLists?.conditional || [])]
          .find((draft) => draft.id === generatedDraftId);
        if (generatedDraft) {
          setActiveType(generatedDraft.configType);
          setThresholdPanel(generatedDraft.configType === "base" ? "base" : "conditional");
          if (generatedDraft.configType === "base") {
            setBaseDraft(generatedDraft.config);
            setTemplateName(Object.keys(generatedDraft.config?.templates || {})[0] || "");
          } else {
            setConditionalDraft(generatedDraft.config);
          }
          setActiveDraftIds((current) => ({ ...current, [generatedDraft.configType]: generatedDraft.id }));
          setDraftNames((current) => ({ ...current, [generatedDraft.configType]: generatedDraft.draftName || "Threshold Learning draft" }));
        }
      }
      setThresholdDraftPreview(null);
      setMessage({ type: "ok", text: `Created ${res.data.drafts?.length || 1} threshold draft${(res.data.drafts?.length || 1) === 1 ? "" : "s"} from ${res.data.proposalCount} accepted proposal${res.data.proposalCount === 1 ? "" : "s"} with ${res.data.changes.length} concrete change${res.data.changes.length === 1 ? "" : "s"}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to create threshold draft from accepted proposals." });
    } finally {
      setThresholdLearningLoading(false);
    }
  }

  useEffect(() => {
    const jobs = thresholdLearning?.uploadJobs || [];
    const hasActiveJob = jobs.some((job) => ["queued", "running"].includes(job.status));
    if (!hasActiveJob || !canSeeModule("threshold_learning_lab")) return undefined;
    const timer = setInterval(() => {
      reloadThresholdLearning().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [thresholdLearning?.uploadJobs, modules]);

  function updateReportLimit(value) {
    const nextLimit = Number(value) || 10;
    setReportLimit(nextLimit);
    setReportPage(0);
    openReports(nextLimit, 0, reportSearch, reportSort, reportComponentType);
  }

  function updateReportPage(nextPage) {
    const safePage = Math.max(nextPage, 0);
    setReportPage(safePage);
    openReports(reportLimit, safePage, reportSearch, reportSort, reportComponentType);
  }

  function updateReportSearch(value) {
    setReportSearch(value);
    setReportPage(0);
    openReports(reportLimit, 0, value, reportSort, reportComponentType);
  }

  function updateReportComponentType(value) {
    setReportComponentType(value);
    setReportPage(0);
    openReports(reportLimit, 0, reportSearch, reportSort, value);
  }

  function updateReportSort(field) {
    const nextSort = toggleSort(reportSort, field);
    setReportSort(nextSort);
    setReportPage(0);
    openReports(reportLimit, 0, reportSearch, nextSort, reportComponentType);
  }

  async function loadReviewQueue(limit = reviewQueueLimit, page = reviewQueuePage, search = reviewQueueSearch, sort = reviewQueueSort) {
    setReviewQueueLoading(true);
    setMessage(null);
    try {
      const res = await getAdminReportReviewQueue(limit, page * limit, search, sort);
      setReviewQueueRows(res.data.reports || []);
      setReviewQueueTotal(res.data.total || 0);
      if (res.data.settings) {
        setReviewQueueSettings(res.data.settings);
        setReviewStartDateInput(res.data.settings.reviewStartDate || "");
      }
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load reports review queue." });
    } finally {
      setReviewQueueLoading(false);
    }
  }

  function updateReviewQueueLimit(value) {
    const nextLimit = Number(value) || 10;
    setReviewQueueLimit(nextLimit);
    setReviewQueuePage(0);
    loadReviewQueue(nextLimit, 0, reviewQueueSearch, reviewQueueSort);
  }

  function updateReviewQueuePage(nextPage) {
    const safePage = Math.max(nextPage, 0);
    setReviewQueuePage(safePage);
    loadReviewQueue(reviewQueueLimit, safePage, reviewQueueSearch, reviewQueueSort);
  }

  function updateReviewQueueSearch(value) {
    setReviewQueueSearch(value);
    setReviewQueuePage(0);
    loadReviewQueue(reviewQueueLimit, 0, value, reviewQueueSort);
  }

  function updateReviewQueueSort(field) {
    const nextSort = toggleSort(reviewQueueSort, field);
    setReviewQueueSort(nextSort);
    setReviewQueuePage(0);
    loadReviewQueue(reviewQueueLimit, 0, reviewQueueSearch, nextSort);
  }

  async function saveReviewStartDate() {
    if (!canSeeModule("report_review_queue")) return;
    setReviewQueueLoading(true);
    setMessage({ type: "ok", text: "Saving review start date and refreshing reports..." });
    try {
      const res = await updateAdminReportReviewSettings({ reviewStartDate: reviewStartDateInput || null });
      const settings = res.data.settings || null;
      setReviewQueueSettings(settings);
      setReviewStartDateInput(settings?.reviewStartDate || "");
      setReviewQueuePage(0);
      setReviewQueueSearch("");
      const queueRes = await getAdminReportReviewQueue(reviewQueueLimit, 0, "", reviewQueueSort);
      setReviewQueueRows(queueRes.data.reports || []);
      setReviewQueueTotal(queueRes.data.total || 0);
      if (queueRes.data.settings) {
        setReviewQueueSettings(queueRes.data.settings);
        setReviewStartDateInput(queueRes.data.settings.reviewStartDate || "");
      }
      setSummary((current) => current ? {
        ...current,
        pendingReportReviews: queueRes.data.total || 0,
        reportReviewStartDate: queueRes.data.settings?.reviewStartDate || settings?.reviewStartDate || null,
      } : current);
      setMessage({ type: "ok", text: `Review start date saved. Sync scanned ${res.data.sync?.scanned || 0} report(s) and queued ${res.data.sync?.queued || 0}.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to save review start date." });
    } finally {
      setReviewQueueLoading(false);
    }
  }

  async function clearReviewStartDate() {
    if (!canSeeModule("report_review_queue")) return;
    setReviewQueueLoading(true);
    setMessage({ type: "ok", text: "Clearing review start date and refreshing reports..." });
    try {
      const res = await updateAdminReportReviewSettings({ reviewStartDate: null });
      const settings = res.data.settings || null;
      setReviewQueueSettings(settings);
      setReviewStartDateInput("");
      setReviewQueuePage(0);
      setReviewQueueSearch("");
      const queueRes = await getAdminReportReviewQueue(reviewQueueLimit, 0, "", reviewQueueSort);
      setReviewQueueRows(queueRes.data.reports || []);
      setReviewQueueTotal(queueRes.data.total || 0);
      if (queueRes.data.settings) {
        setReviewQueueSettings(queueRes.data.settings);
        setReviewStartDateInput(queueRes.data.settings.reviewStartDate || "");
      }
      setSummary((current) => current ? {
        ...current,
        pendingReportReviews: queueRes.data.total || 0,
        reportReviewStartDate: null,
      } : current);
      setMessage({ type: "ok", text: "Review start date cleared. Showing all queued reports to review." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to clear review start date." });
    } finally {
      setReviewQueueLoading(false);
    }
  }

  async function syncReviewQueue() {
    if (!isPowerAdmin) return;
    setReviewQueueLoading(true);
    setMessage(null);
    try {
      const res = await syncAdminReportReviewQueue();
      await loadReviewQueue(reviewQueueLimit, reviewQueuePage, reviewQueueSearch, reviewQueueSort);
      await loadAll();
      setMessage({ type: "ok", text: `Review queue synced. ${res.data.sync?.queued || 0} report(s) queued from ${res.data.sync?.scanned || 0} scanned.` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to sync review queue." });
    } finally {
      setReviewQueueLoading(false);
    }
  }

  async function approveQueuedReport(sampleId, disposition = "release") {
    setReviewQueueLoading(true);
    setMessage(null);
    try {
      await approveAdminReport(sampleId, disposition);
      await loadReviewQueue(reviewQueueLimit, reviewQueuePage, reviewQueueSearch, reviewQueueSort);
      await loadAll();
      setMessage({ type: "ok", text: disposition === "save" ? "Report approved and added to the customer release queue." : "Report approved and released." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to approve report." });
    } finally {
      setReviewQueueLoading(false);
    }
  }

  async function openReportDetail(sampleId) {
    if (sampleId && typeof sampleId === "object") {
      navigate(`${dashboardUrl(sampleId.customerEmail,sampleId.labSampleId)}&review=1`);
      return;
    }
    setReportLoading(true);
    setMessage(null);
    try {
      const res = await getAdminReportDetail(sampleId);
      setSelectedReportDetail(res.data);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load report details." });
    } finally {
      setReportLoading(false);
    }
  }

  async function openOutstandingSamples(limit = outstandingLimit, page = outstandingPage, search = outstandingSearch, sort = outstandingSort) {
    setShowOutstandingModal(true);
    setOutstandingLoading(true);
    setMessage(null);
    try {
      const res = await getAdminOutstandingSamples(limit, page * limit, search, sort);
      setOutstandingSamples(res.data.samples || []);
      setOutstandingTotal(res.data.total || 0);
      setSelectedOutstandingSample(null);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load outstanding samples." });
    } finally {
      setOutstandingLoading(false);
    }
  }

  function updateOutstandingLimit(value) {
    const nextLimit = Number(value) || 10;
    setOutstandingLimit(nextLimit);
    setOutstandingPage(0);
    openOutstandingSamples(nextLimit, 0, outstandingSearch, outstandingSort);
  }

  function updateOutstandingPage(nextPage) {
    const safePage = Math.max(nextPage, 0);
    setOutstandingPage(safePage);
    openOutstandingSamples(outstandingLimit, safePage, outstandingSearch, outstandingSort);
  }

  function updateOutstandingSearch(value) {
    setOutstandingSearch(value);
    setOutstandingPage(0);
    openOutstandingSamples(outstandingLimit, 0, value, outstandingSort);
  }

  function updateOutstandingSort(field) {
    const nextSort = toggleSort(outstandingSort, field);
    setOutstandingSort(nextSort);
    setOutstandingPage(0);
    openOutstandingSamples(outstandingLimit, 0, outstandingSearch, nextSort);
  }

  async function openOutstandingDetail(sampleId) {
    setOutstandingLoading(true);
    setMessage(null);
    try {
      const res = await getAdminOutstandingSampleDetail(sampleId);
      setSelectedOutstandingSample(res.data.sample);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load sample details." });
    } finally {
      setOutstandingLoading(false);
    }
  }

  async function archiveOutstandingSample(sampleId) {
    const previousSamples = outstandingSamples;
    const previousTotal = outstandingTotal;
    const previousPage = outstandingPage;
    const nextSamples = previousSamples.filter((sample) => sample.labSampleId !== sampleId);
    const nextPage = nextSamples.length === 0 && previousPage > 0 ? previousPage - 1 : previousPage;

    setOutstandingSamples(nextSamples);
    setOutstandingTotal((current) => Math.max(current - 1, 0));
    if (nextPage !== previousPage) setOutstandingPage(nextPage);
    setOutstandingLoading(true);
    setMessage(null);
    try {
      await archiveAdminOutstandingSample(sampleId);
      await loadAll();
      if (nextPage !== previousPage) await openOutstandingSamples(outstandingLimit, nextPage, outstandingSearch, outstandingSort);
      setMessage({ type: "ok", text: "Outstanding sample archived." });
      return true;
    } catch (err) {
      setOutstandingSamples(previousSamples);
      setOutstandingTotal(previousTotal);
      setOutstandingPage(previousPage);
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to archive sample." });
      return false;
    } finally {
      setOutstandingLoading(false);
    }
  }

  async function archiveOutstandingSampleFromDetail(sampleId) {
    const previousSample = selectedOutstandingSample;
    setSelectedOutstandingSample(null);
    const archived = await archiveOutstandingSample(sampleId);
    if (!archived) setSelectedOutstandingSample(previousSample);
  }

  async function openArchiveModal() {
    setShowArchiveModal(true);
    setArchiveLoading(true);
    setMessage(null);
    try {
      const res = await getAdminArchivedSamples();
      setArchivedSamples(res.data.samples || []);
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to load archive." });
    } finally {
      setArchiveLoading(false);
    }
  }

  async function restoreArchivedSample(sampleId) {
    setArchiveLoading(true);
    setMessage(null);
    try {
      await restoreAdminArchivedSample(sampleId);
      await openArchiveModal();
      await loadAll();
      if (showOutstandingModal) await openOutstandingSamples(outstandingLimit, outstandingPage, outstandingSearch, outstandingSort);
      setMessage({ type: "ok", text: "Archived sample restored." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Failed to restore sample." });
    } finally {
      setArchiveLoading(false);
    }
  }

  async function doAction(fn, success) {
    setMessage(null);
    try {
      await fn();
      setMessage({ type: "ok", text: success });
      await loadAll();
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Admin action failed." });
    }
  }

  function updateMetric(metricKey, field, rawValue) {
    const next = clone(baseDraft);
    next.templates[templateName][metricKey][field] = field === "type" ? rawValue : Number(rawValue);
    setBaseDraft(next);
  }

  function updateMetricAcrossTemplates(metricKey, sourceThreshold, targetTemplates) {
    const next = clone(baseDraft);
    const valueFields = ["pass", "warn", "fail", "type"];
    targetTemplates.forEach((name) => {
      const threshold = next.templates?.[name]?.[metricKey];
      if (!threshold) return;
      valueFields.forEach((field) => {
        if (sourceThreshold[field] === undefined) delete threshold[field];
        else threshold[field] = sourceThreshold[field];
      });
    });
    setBaseDraft(next);
    setMessage({ type: "ok", text: `Applied ${metricKey} threshold values to ${targetTemplates.length} template${targetTemplates.length === 1 ? "" : "s"}. Save the draft to keep this change.` });
  }

  function updateConditional(path, rawValue) {
    const next = clone(conditionalDraft);
    let target = next.active_rules;
    for (let i = 0; i < path.length - 1; i += 1) target = target[path[i]];
    target[path[path.length - 1]] = Number(rawValue);
    setConditionalDraft(next);
  }

  function updateViscosityMapping(viscosity, rawValue) {
    const next = clone(baseDraft);
    next.template_selection_rules.viscosity_class_mapping[viscosity] = rawValue;
    setBaseDraft(next);
  }

  function updatePreview(field, value) {
    setPreviewSample(current => ({ ...current, [field]: value }));
  }

  function setActiveDraftName(value) {
    setDraftNames(current => ({ ...current, [activeType]: value }));
  }

  function setActiveDraftId(value) {
    setActiveDraftIds(current => ({ ...current, [activeType]: value }));
  }

  function loadSavedThresholdConfig(selectionId) {
    if (typeof selectionId === "string" && selectionId.startsWith("published:")) {
      loadPublishedConfig(selectionId);
      return;
    }
    loadDraftById(selectionId);
  }

  function loadDraftById(draftId) {
    setActiveDraftId(draftId);
    setThresholdSaveNotice(null);
    const selected = activeDraftList.find((draft) => draft.id === draftId);
    if (!selected) {
      setActiveDraftName(defaultDraftName(activeType));
      setMessage({ type: "ok", text: "New draft selected. Save it when you are ready." });
      return;
    }
    setActiveDraftName(selected.draftName || defaultDraftName(activeType));
    const next = cloneThresholdConfig(selected.config);
    if (activeType === "base") {
      setBaseDraft(next);
      setTemplateName(Object.keys(next.templates || {})[0] || "");
    } else {
      setConditionalDraft(next);
    }
    setMessage({ type: "ok", text: "Saved draft loaded." });
  }

  function loadPublishedConfig(selectionId = activePublishedOptionId) {
    const nextSelectionId = typeof selectionId === "string" ? selectionId : activePublishedOptionId;
    const published = cloneThresholdConfig(thresholds?.published?.[activeType]?.config);
    if (!published || Object.keys(published).length === 0) {
      setMessage({ type: "err", text: "No published config is available to load." });
      return;
    }
    if (activeType === "base") {
      setBaseDraft(published);
      setTemplateName(Object.keys(published.templates || {})[0] || "");
    } else {
      setConditionalDraft(published);
    }
    setActiveDraftId(nextSelectionId);
    setActiveDraftName(activePublishedMeta?.draftName || defaultDraftName(activeType));
    setThresholdSaveNotice(null);
    setMessage({ type: "ok", text: "Published template loaded into the editor. Save it as a new draft before publishing changes." });
  }

  async function saveActiveThresholdDraft() {
    setMessage(null);
    setThresholdSaveNotice(null);
    try {
      const res = await saveThresholdDraft({
        configType: activeType,
        config: activeDraft,
        note,
        draftName: activeDraftName,
        draftId: activeDraftId || undefined,
      });
      const savedName = res.data.draftName || activeDraftName || defaultDraftName(activeType);
      await loadAll();
      setActiveType(res.data.configType || activeType);
      setActiveDraftIds((current) => ({ ...current, [res.data.configType || activeType]: res.data.id }));
      setDraftNames((current) => ({ ...current, [res.data.configType || activeType]: savedName }));
      setThresholdSaveNotice({ type: "ok", text: `Saved template "${savedName}".` });
      setMessage({ type: "ok", text: `Saved template "${savedName}".` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to save threshold draft." });
    }
  }

  async function publishActiveThresholdDraft() {
    setMessage(null);
    setThresholdSaveNotice(null);
    try {
      const res = await publishThresholdDraft({ configType: activeType, note, draftId: activeDraftId });
      const publishedName = res.data.draftName || activeDraftName || defaultDraftName(activeType);
      await loadAll();
      const publishedSelectionId = res.data.id ? `published:${res.data.id}` : `published:${res.data.configType || activeType}`;
      setActiveType(res.data.configType || activeType);
      setActiveDraftIds((current) => ({ ...current, [res.data.configType || activeType]: publishedSelectionId }));
      setDraftNames((current) => ({ ...current, [res.data.configType || activeType]: publishedName }));
      if (res.data.configType === "base" || activeType === "base") {
        setBaseDraft(cloneThresholdConfig(res.data.config));
        setTemplateName(Object.keys(res.data.config?.templates || {})[0] || "");
      } else {
        setConditionalDraft(cloneThresholdConfig(res.data.config));
      }
      setThresholdSaveNotice({ type: "ok", text: `Published template "${publishedName}". It remains available in the saved template dropdown.` });
      setMessage({ type: "ok", text: `Published template "${publishedName}".` });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to publish threshold draft." });
    }
  }

  async function saveAndPublishActiveThresholds(){setMessage(null);setThresholdSaveNotice(null);try{const res=await saveAndPublishThresholdConfig({configType:activeType,config:activeDraft,note,draftName:activeDraftName});const publishedName=res.data.draftName||activeDraftName||defaultDraftName(activeType);await loadAll();setActiveDraftIds((current)=>({...current,[activeType]:`published:${res.data.id}`}));setThresholdSaveNotice({type:"ok",text:`Saved and published “${publishedName}”. The previous version is available in History.`});setMessage({type:"ok",text:`Saved and published “${publishedName}”.`});}catch(err){setMessage({type:"err",text:err.response?.data?.error||"Unable to validate and publish thresholds."});}}

  async function renameActiveDraft() {
    setMessage(null);
    try {
      const res = await renameThresholdDraft({ configType: activeType, draftName: activeDraftName, draftId: activeDraftId });
      setThresholds(current => ({
        ...current,
        draftLists: {
          ...current.draftLists,
          [activeType]: (current.draftLists?.[activeType] || []).map((draft) =>
            draft.id === activeDraftId ? { ...draft, draftName: res.data.draftName } : draft
          ),
        },
        drafts: {
          ...current.drafts,
          [activeType]: {
            ...(current.drafts?.[activeType] || {}),
            draftName: res.data.draftName,
          },
        },
      }));
      setActiveDraftName(res.data.draftName);
      setMessage({ type: "ok", text: "Draft renamed." });
    } catch (err) {
      setMessage({ type: "err", text: err.response?.data?.error || "Unable to rename draft." });
    }
  }

  const needsStats = !isLearningLabPage && (canSeeModule("general_stats") || canSeeModule("excel_artemis_review") || canSeeModule("threshold_learning_lab"));
  const needsThresholds = !isLearningLabPage && canSeeModule("thresholds");
  const needsReviewQueue = !isLearningLabPage && canSeeModule("report_review_queue");
  const needsThresholdLearning = canSeeModule("threshold_learning_lab");
  const needsSnapshot = !isLearningLabPage && canSeeModule("report_snapshot");
  const dashboardReady = modules && reportDataSource && (!needsStats || summary) && (!needsThresholds || (baseDraft && conditionalDraft)) && (!needsReviewQueue || reviewQueueSettings !== null) && (!needsThresholdLearning || thresholdLearning) && (!needsSnapshot || reportSnapshot) && (!isLearningLabPage || !isPowerAdmin || learningOperations);
  const reportReviewStartDate = reviewQueueSettings?.reviewStartDate || summary?.reportReviewStartDate || null;
  const reportReviewNote = (
    <>
      <div>{reportReviewStartDate ? `As of start date ${formatDateOnly(reportReviewStartDate)}` : "For All Dates"}</div>
      <div>(these do not include reports that haven't been lab tested yet).</div>
    </>
  );

  if (!dashboardReady) {
    return (
      <div style={{ ...s.page, padding: isMobile ? "18px 12px" : s.page.padding }}>
        <div style={s.loadingBox}>
          <div style={s.loadingCard} className="animate-fade-in-up">
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={s.spinner} aria-hidden="true"></div>
            </div>
            <div style={s.loadingTitle}>{isLearningLabPage ? "Loading Learning Lab" : "Loading Admin Dashboard"}</div>
            <div style={s.loadingText}>
              {isLearningLabPage
                ? "We are loading reconciliation findings, proposals, uploads, and threshold-learning review data."
                : "We are loading dashboard modules, counts, review queues, alerts, and threshold controls."}
            </div>
            <div style={s.loadingTrack} role="progressbar" aria-label={isLearningLabPage ? "Learning Lab loading" : "Admin Dashboard loading"}>
              <div style={s.loadingFill} className="sdx-dashboard-loading-bar"></div>
            </div>
            <div style={s.loadingMeta}>Still working...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...s.page, padding: isMobile ? "18px 12px" : s.page.padding }}>
      <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 18 }}>
        <div style={s.row}>
          <h1 style={{ ...s.title, marginBottom: 0 }}>
            {isLearningLabPage ? "Threshold Learning Lab" : isPowerAdmin ? "Power Admin Dashboard" : "Admin Dashboard"}
          </h1>
          {!isLearningLabPage && <DataSourcePill status={reportDataSource} />}
        </div>
        {isLearningLabPage && (
          <button type="button" style={s.secondary} onClick={() => navigate("/admin")}>Back to Admin Dashboard</button>
        )}
      </div>

      {isLearningLabPage && <LearningLabIntro />}

      {message && <div style={{ ...s.status, ...(message.type === "ok" ? s.ok : s.err), marginBottom: 12 }}>{message.text}</div>}

      {!isLearningLabPage && isPowerAdmin && (
        <AdminModulesPanel modules={modules || []} loading={modulesLoading} onToggle={toggleAdminModule} />
      )}

      {!isLearningLabPage && canSeeModule("report_snapshot") && (
        <ReportSnapshotSection
          state={reportSnapshot}
          schedule={snapshotSchedule}
          scheduleDirty={snapshotScheduleDirty}
          loading={reportSnapshotLoading}
          onScheduleChange={(next)=>{setSnapshotSchedule(next);setSnapshotScheduleDirty(true);}}
          onDiscardSchedule={()=>{setSnapshotSchedule(savedSnapshotSchedule);setSnapshotScheduleDirty(false);}}
          onSaveSchedule={saveSnapshotSchedule}
          onUpdateNow={updateReportSnapshotNow}
          onTogglePin={toggleSnapshotPin}
          canManage={isPowerAdmin}
        />
      )}

      {!isLearningLabPage && (canSeeModule("general_stats") || canSeeModule("sample_lookup") || canSeeModule("excel_artemis_review") || canSeeModule("threshold_learning_lab") || canSeeModule("report_review_queue") || canSeeModule("admin_users") || canSeeModule("thresholds")) && (
        <div style={s.grid}>
          {canSeeModule("general_stats") && (
            <>
              <details style={s.metricGroup}>
                <summary style={s.metricGroupSummary}>Customers / Reports Metrics</summary>
                <div style={s.metricGroupGrid}>
                  <Metric label="Customers" value={summary.totalCustomers} note="Unique Artemis customer IDs with valid emails" onClick={openCustomers} />
                  <Metric label="Dashboard Accounts" value={summary.dashboardAccounts} note="Users with dashboard logins" />
                  <Metric
                    label="Oil Analysis Reports"
                    value={summary.totalReports}
                    note={formatReportTypeBreakdown(summary.reportTypeCounts)}
                    onClick={openReports}
                  />
                  <Metric
                    label="Outstanding Samples"
                    value={summary.pendingSamples}
                    note={summary.oldestOutstandingSampleDate ? `Oldest: ${formatDate(summary.oldestOutstandingSampleDate)}` : "No outstanding sample date available"}
                    onClick={openOutstandingSamples}
                  />
                  <Metric label="Archive" value="View" note="Archived outstanding samples" onClick={openArchiveModal} />
                </div>
              </details>
              <Metric label="Alerts To Review" value={summary.pendingAlerts} />
            </>
          )}
          {canSeeModule("report_review_queue") && (
            <Metric
              label="Reports to Review"
              value={summary.pendingReportReviews}
              note={reportReviewNote}
              accent="amber"
              actionLabel="Open review queue"
              onClick={async () => {
                setDashboardModal("reports");
                await loadReviewQueue();
              }}
            />
          )}
          {canSeeModule("report_review_queue")&&<Metric label="Customer Release Queue" value="Open" note="Approved reports awaiting customer release" accent="amber" actionLabel="Open release queue" onClick={openReleaseQueue}/>}
          {canManageAlphaTesters && <Metric label="Alpha Testers" value={alphaTesters.length} note="Invite, grant, hold, or revoke customer preview access" accent="violet" actionLabel="Manage alpha access" onClick={() => setDashboardModal("alpha-testers")} />}
          <Metric label="History" value="Audit" note="Threshold, Learning Lab, report, release, and schedule activity" accent="violet" actionLabel="Open history" onClick={openHistory}/>
          {canSeeModule("admin_users") && (
            <Metric
              label="Team Members and Collaborators"
              value={adminUsers.length}
              note="Manage invitations, access, and administrator roles"
              accent="violet"
              actionLabel="Manage team"
              onClick={() => setDashboardModal("team")}
            />
          )}
          {canSeeModule("thresholds") && (
            <Metric
              label="Threshold Control Panel"
              value="Open"
              note="Validate proposed changes, test rules, and publish threshold configurations"
              accent="blue"
              actionLabel="Open control panel"
              onClick={() => setDashboardModal("thresholds")}
            />
          )}
          {canSeeModule("excel_artemis_review") && (
            <Metric
              label="PDF / Snapshot Comparison Review"
              value={summary.pendingExcelArtemisReviews || 0}
              note="Open historical reconciliation discrepancies"
              onClick={() => openExcelArtemisReview()}
            />
          )}
          {canSeeModule("threshold_learning_lab") && (
            <Metric
              label="Threshold Learning Lab"
              value={thresholdLearning?.summary?.processed || 0}
              note={`${thresholdLearning?.summary?.parityRate || 0}% parity after deterministic overrides`}
              onClick={() => navigate("/admin/learning-lab")}
            />
          )}
          {canSeeModule("sample_lookup") && (
            <SampleLookupMetric />
          )}
        </div>
      )}

      {!isLearningLabPage && dashboardModal === "reports" && canSeeModule("report_review_queue") && (
        <DashboardSectionModal title="Reports to Review" onClose={() => setDashboardModal(null)}>
          <ReportReviewQueueSection
          embedded
          reports={reviewQueueRows}
          total={reviewQueueTotal}
          limit={reviewQueueLimit}
          page={reviewQueuePage}
          search={reviewQueueSearch}
          loading={reviewQueueLoading}
          sort={reviewQueueSort}
          settings={reviewQueueSettings}
          startDateInput={reviewStartDateInput}
          canEditStartDate={canSeeModule("report_review_queue")}
          canSync={isPowerAdmin}
          onStartDateChange={setReviewStartDateInput}
          onSaveStartDate={saveReviewStartDate}
          onClearStartDate={clearReviewStartDate}
          onSync={syncReviewQueue}
          onLimitChange={updateReviewQueueLimit}
          onPageChange={updateReviewQueuePage}
          onSearchChange={updateReviewQueueSearch}
          onSortChange={updateReviewQueueSort}
          onOpenReport={openReportDetail}
          onApprove={approveQueuedReport}
          />
        </DashboardSectionModal>
      )}
      {!isLearningLabPage && dashboardModal === "release" && (
        <DashboardSectionModal title="Customer Release Queue" onClose={() => setDashboardModal(null)}>
          <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
            <div style={s.hint}>{releaseQueue.total || 0} approved report(s) awaiting release</div>
            <button style={s.btn} onClick={releaseAll} disabled={!releaseQueue.reports.length}>Release All Shown</button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr><th style={s.th}>Sample</th><th style={s.th}>Customer</th><th style={s.th}>Approved</th><th style={s.th}>Action</th></tr>
              </thead>
              <tbody>
                {releaseQueue.reports.map((report) => (
                  <tr key={report.labSampleId}>
                    <td style={s.td}>{report.labSampleId}</td>
                    <td style={s.td}>{report.customerEmail}</td>
                    <td style={s.td}>{formatDate(report.approvedAt)}</td>
                    <td style={s.td}>
                      <div style={{ ...s.row, flexWrap: "nowrap" }}>
                        <button type="button" style={s.secondary} onClick={() => openReportDetail(report)}>Review Report</button>
                        <button type="button" style={s.btn} onClick={() => releaseOne(report.labSampleId)}>Release</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!releaseQueue.reports.length && <tr><td style={s.td} colSpan="4">No reports are waiting for release.</td></tr>}
              </tbody>
            </table>
          </div>
        </DashboardSectionModal>
      )}
      {!isLearningLabPage&&dashboardModal==="history"&&<DashboardSectionModal title="History" onClose={()=>setDashboardModal(null)}><div style={{overflowX:"auto"}}><table style={s.table}><thead><tr><th style={s.th}>When</th><th style={s.th}>Area</th><th style={s.th}>Action</th><th style={s.th}>Item</th><th style={s.th}>Actor</th><th style={s.th}>Restore</th></tr></thead><tbody>{historyRows.map((event)=><tr key={event.id}><td style={s.td}>{formatDate(event.createdAt)}</td><td style={s.td}>{event.domain}</td><td style={s.td}>{String(event.action||"").replaceAll("_"," ")}</td><td style={s.td}>{event.entityKey||event.entityType}</td><td style={s.td}>{event.actorEmail}</td><td style={s.td}>{event.domain==="thresholds"?<button style={s.secondary} onClick={()=>restoreHistoryEvent(event.id)}>Validate &amp; Restore</button>:"Audit only"}</td></tr>)}</tbody></table></div></DashboardSectionModal>}

      {isLearningLabPage && canSeeModule("threshold_learning_lab") && (
        <>
          {isPowerAdmin && <LearningOperationsPanel data={learningOperations} loading={learningOperationsLoading} onRecord={recordLearningOperationsAudit} />}
          <ExpertKnowledgeLearningLabSection learning={thresholdLearning} loading={thresholdLearningLoading} onRefresh={reloadThresholdLearning} />
        </>
      )}

      {isLearningLabPage && !canSeeModule("threshold_learning_lab") && (
        <section style={s.section}>
          <div style={s.sectionTitle}>Threshold Learning Lab is not enabled</div>
          <div style={s.hint}>A Power Admin can enable this module for standard Admin users.</div>
        </section>
      )}

      {!isLearningLabPage && dashboardModal === "team" && canSeeModule("admin_users") && (
        <DashboardSectionModal title="Team Members and Collaborators" onClose={() => setDashboardModal(null)}>
          <AdminUsersPanel
          embedded
          users={adminUsers}
          modules={modules || []}
          loading={adminUsersLoading}
          inviteEmail={inviteEmail}
          inviteName={inviteName}
          inviteAccess={inviteAccess}
          onInviteEmailChange={setInviteEmail}
          onInviteNameChange={setInviteName}
          onInviteAccessChange={(moduleKey, enabled) => setInviteAccess((current) => ({ ...current, [moduleKey]: enabled }))}
          onInvite={sendAdminInvite}
          onToggle={toggleAdminDashboardAccess}
          onToggleModule={toggleAdminUserModule}
          canManage={canSeeModule("admin_users")}
          canManagePower={isPowerAdmin}
          />
        </DashboardSectionModal>
      )}

      {!isLearningLabPage && dashboardModal === "alpha-testers" && canManageAlphaTesters && (
        <DashboardSectionModal title="Alpha Tester Access" onClose={() => setDashboardModal(null)}>
          <AlphaTestersPanel
            testers={alphaTesters}
            loading={alphaTestersLoading}
            inviteEmail={alphaInviteEmail}
            manualInvite={alphaManualInvite}
            customerSearch={alphaCustomerSearch}
            customerResults={alphaCustomerResults}
            customerSearchHasRun={alphaCustomerSearchHasRun}
            customerSearchLoading={alphaCustomerSearchLoading}
            customerInvitingEmail={alphaCustomerInvitingEmail}
            onInviteEmailChange={setAlphaInviteEmail}
            onInvite={sendAlphaInvite}
            onCustomerSearchChange={(value) => {
              setAlphaCustomerSearch(value);
              setAlphaCustomerSearchHasRun(false);
            }}
            onCustomerSearch={searchAlphaCustomers}
            onInviteCustomer={inviteAlphaSearchCustomer}
            onHoldAll={holdAllAlphaAccess}
            onStatusChange={changeAlphaAccess}
            onResend={resendAlphaInvite}
            onOpenCustomer={canSeeModule("general_stats") ? openCustomerDetail : null}
          />
        </DashboardSectionModal>
      )}

      {showOutstandingModal && (
        <OutstandingSamplesModal
          samples={outstandingSamples}
          total={outstandingTotal}
          limit={outstandingLimit}
          page={outstandingPage}
          search={outstandingSearch}
          loading={outstandingLoading}
          sort={outstandingSort}
          onLimitChange={updateOutstandingLimit}
          onPageChange={updateOutstandingPage}
          onSearchChange={updateOutstandingSearch}
          onSortChange={updateOutstandingSort}
          onSelect={openOutstandingDetail}
          onArchive={archiveOutstandingSample}
          onClose={() => {
            setShowOutstandingModal(false);
            setSelectedOutstandingSample(null);
          }}
        />
      )}

      {showArchiveModal && (
        <ArchivedSamplesModal
          samples={archivedSamples}
          loading={archiveLoading}
          onSelect={setSelectedOutstandingSample}
          onRestore={restoreArchivedSample}
          onClose={() => setShowArchiveModal(false)}
        />
      )}

      {listModal === "customers" && (
        <CustomersModal
          customers={customerRows}
          total={customerTotal}
          limit={customerLimit}
          page={customerPage}
          search={customerSearch}
          loading={customerLoading}
          sort={customerSort}
          onLimitChange={updateCustomerLimit}
          onPageChange={updateCustomerPage}
          onSearchChange={updateCustomerSearch}
          onSortChange={updateCustomerSort}
          onSelect={openCustomerDetail}
          onInviteAlpha={canManageAlphaTesters ? inviteCustomerAsAlpha : null}
          invitingEmail={customerAlphaInvitingEmail}
          inviteNotice={customerAlphaInviteNotice}
          onClose={() => { setListModal(null); setCustomerAlphaInviteNotice(null); }}
        />
      )}

      {listModal === "reports" && (
        <ReportsModal
          reports={reportRows}
          total={reportTotal}
          limit={reportLimit}
          page={reportPage}
          search={reportSearch}
          componentType={reportComponentType}
          componentOptions={reportComponentOptions}
          loading={reportLoading}
          sort={reportSort}
          onLimitChange={updateReportLimit}
          onPageChange={updateReportPage}
          onSearchChange={updateReportSearch}
          onComponentTypeChange={updateReportComponentType}
          onSortChange={updateReportSort}
          onSelect={openReportDetail}
          onClose={() => setListModal(null)}
        />
      )}

      {listModal === "excelArtemis" && (
        <ExcelArtemisReviewModal
          items={excelArtemisRows}
          total={excelArtemisTotal}
          decisionSummary={excelArtemisDecisionSummary}
          limit={excelArtemisLimit}
          page={excelArtemisPage}
          search={excelArtemisSearch}
          issueType={excelArtemisIssueType}
          decisionStatus={excelArtemisDecisionStatus}
          loading={excelArtemisLoading}
          onLimitChange={updateExcelArtemisLimit}
          onPageChange={updateExcelArtemisPage}
          onSearchChange={updateExcelArtemisSearch}
          onIssueTypeChange={updateExcelArtemisIssueType}
          onDecisionStatusChange={updateExcelArtemisDecisionStatus}
          onSelect={openExcelArtemisDetail}
          onClose={() => {
            setListModal(null);
            setSelectedExcelArtemisDetail(null);
          }}
        />
      )}

      {selectedCustomerDetail && (
        <CustomerDetailModal
          detail={selectedCustomerDetail}
          onSaveAccount={saveCustomerAccount}
          onOpenReport={openReportDetail}
          onClose={() => setSelectedCustomerDetail(null)}
        />
      )}

      {selectedReportDetail && (
        <ReportDetailModal
          detail={selectedReportDetail}
          onSaved={async () => {
            await openReportDetail(selectedReportDetail.report.lab_sample_id || selectedReportDetail.report.labSampleId);
            if (canSeeModule("report_review_queue")) await loadReviewQueue(reviewQueueLimit, reviewQueuePage, reviewQueueSearch, reviewQueueSort);
            await loadAll();
          }}
          onClose={() => setSelectedReportDetail(null)}
        />
      )}

      {selectedExcelArtemisDetail && (
        <ExcelArtemisReviewDetailModal
          detail={selectedExcelArtemisDetail}
          loading={excelArtemisLoading}
          onSaveAnnotation={saveExcelArtemisAnnotation}
          onClose={() => setSelectedExcelArtemisDetail(null)}
        />
      )}

      {selectedOutstandingSample && (
        <OutstandingSampleDetailModal
          sample={selectedOutstandingSample}
          onArchive={selectedOutstandingSample.archiveStatus === "archived" ? null : archiveOutstandingSampleFromDetail}
          onClose={() => setSelectedOutstandingSample(null)}
        />
      )}

      {selectedAlert && (
        <AlphaFeedbackAlertModal
          alert={selectedAlert}
          onReply={sendAlphaFeedbackReply}
          onClose={() => setSelectedAlert(null)}
        />
      )}

      {!isLearningLabPage && canSeeModule("alerts") && <section style={s.section}>
        <div style={s.sectionTitle}>Alerts</div>
        <div style={{ ...s.row, marginBottom: 12 }}>
          {["review_pending", "resolved", "rejected", "all"].map(status => (
            <button key={status} type="button" style={status === alertStatus ? s.btn : s.secondary} onClick={() => reloadAlerts(status, alertType)}>
              {status.replace("_", " ")}
            </button>
          ))}
          <label style={{ ...s.hint, display: "flex", alignItems: "center", gap: 8 }}>
            Type
            <select style={s.input} value={alertType} onChange={(event) => reloadAlerts(alertStatus, event.target.value)}>
              <option value="all">all types</option>
              <option value="assign_report">move report</option>
              <option value="merge_vehicles">merge vehicles</option>
              <option value="request_sample">sample ID request</option>
              <option value="flag_report">wrong account report</option>
              <option value="alpha_question">alpha question</option>
              <option value="alpha_problem">alpha problem</option>
              <option value="alpha_suggestion">alpha suggestion</option>
            </select>
          </label>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Type</th><th style={s.th}>Customer</th><th style={s.th}>Sample</th><th style={s.th}>Summary</th><th style={s.th}>Status</th><th style={s.th}>Action</th></tr></thead>
            <tbody>
              {alerts.map(alert => <AlertRow key={alert.id} alert={alert} onOpen={() => setSelectedAlert(alert)} onResolve={(status, note) => doAction(
                () => resolveAdminAlert(alert.id, { status, note }),
                `Alert ${status}.`
              )} />)}
              {alerts.length === 0 && <tr><td style={s.td} colSpan="6">No alerts for this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>}

      {!isLearningLabPage && dashboardModal === "thresholds" && canSeeModule("thresholds") && <DashboardSectionModal title="Threshold Control Panel" onClose={() => setDashboardModal(null)}><section style={{ ...s.section, marginBottom: 0, border: "none", boxShadow: "none", padding: 0 }}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.hint}>Review, save, and publish threshold templates and conditional rules.</div>
          </div>
          {canSeeModule("threshold_learning_lab") && (
            <button type="button" style={s.btn} onClick={() => navigate("/admin/learning-lab")}>
              Learning Lab
            </button>
          )}
        </div>
        <div style={s.tabBar}>
          {[
            ["base", "Base Thresholds"],
            ["conditions", "Conditional Rules"],
            ["preview", "Test Result"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              style={thresholdPanel === key ? s.tabActive : s.tab}
              onClick={() => {
                setThresholdPanel(key);
                if (key === "base") setActiveType("base");
                if (key === "conditions") setActiveType("conditional");
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {thresholdPanel !== "preview" && (
          <div style={s.toolbar}>
            <div>
              <span style={s.pill}>
                Published Source: {activePublishedMeta?.source || "none"}{activePublishedMeta?.draftName ? ` (${activePublishedMeta.draftName})` : ""}
              </span>
              <div style={{ ...s.hint, marginTop: 6 }}>
                Draft: {activeDraftMeta ? `${activeDraftMeta.draftName || "Untitled draft"} saved ${formatDate(activeDraftMeta.updatedAt)}` : activeSelectionIsPublished ? "published template loaded" : "new unsaved draft"}
              </div>
            </div>
            <div style={s.row}>
              <select style={{ ...s.input, minWidth: 250 }} value={activeSelectionId} onChange={e => loadSavedThresholdConfig(e.target.value)}>
                <option value="">New draft</option>
                {activePublishedMeta && (
                  <option value={activePublishedOptionId}>
                    {activePublishedMeta.draftName ? `Published: ${activePublishedMeta.draftName}` : "Published Global Thresholds"} - {formatDate(activePublishedMeta.publishedAt || activePublishedMeta.updatedAt)}
                  </option>
                )}
                {activeDraftList.map((draft) => (
                  <option key={draft.id} value={draft.id}>{draft.draftName || "Untitled draft"} - {formatDate(draft.updatedAt)}</option>
                ))}
              </select>
              <input style={{ ...s.input, minWidth: 220 }} value={activeDraftName} onChange={e => setActiveDraftName(e.target.value)} placeholder="Draft name" />
              <button type="button" style={s.secondary} onClick={renameActiveDraft} disabled={!activeDraftMeta}>Rename</button>
              <button type="button" style={s.secondary} onClick={() => loadPublishedConfig()}>Reset to published</button>
              <input style={{ ...s.input, minWidth: 200 }} value={note} onChange={e => setNote(e.target.value)} placeholder="Change note" />
              <button type="button" style={s.btn} onClick={saveAndPublishActiveThresholds}>Save &amp; Publish</button>
              <button type="button" style={s.danger} onClick={() => doAction(
                () => discardThresholdDraft({ configType: activeType, note, draftId: activeDraftId }),
                "Draft discarded."
              )} disabled={!activeDraftMeta}>Discard</button>
            </div>
            {thresholdSaveNotice && (
              <div style={{ flexBasis: "100%", marginTop: 10, padding: "10px 12px", border: "1px solid #bbf7d0", borderRadius: 8, background: "#ecfdf5", color: "#047857", fontWeight: 900 }}>
                {thresholdSaveNotice.text}
              </div>
            )}
          </div>
        )}

        {thresholdPanel === "base" && (
          <BaseEditor
            config={baseDraft}
            templateName={templateName}
            templateNames={templateNames}
            setTemplateName={setTemplateName}
            updateMetric={updateMetric}
            updateMetricAcrossTemplates={updateMetricAcrossTemplates}
            updateViscosityMapping={updateViscosityMapping}
          />
        )}
        {thresholdPanel === "conditions" && (
          <ConditionalEditor config={conditionalDraft} updateConditional={updateConditional} />
        )}
        {thresholdPanel === "preview" && (
          <EffectiveThresholdPreview
            baseConfig={baseDraft}
            conditionalConfig={conditionalDraft}
            sample={previewSample}
            updatePreview={updatePreview}
            templateName={previewTemplateName}
            thresholds={previewThresholds}
          />
        )}

        <details style={{ marginTop: 14 }}>
          <summary style={{ ...s.secondary, display: "inline-block" }}>Setup Tools</summary>
          <div style={{ ...s.panel, marginTop: 10 }}>
            <div style={{ ...s.hint, marginBottom: 10 }}>Use this only when the database has no published threshold records.</div>
            <button type="button" style={s.secondary} onClick={() => doAction(seedAdminThresholds, "Published thresholds seeded from repository config.")}>Seed Published</button>
          </div>
        </details>
      </section></DashboardSectionModal>}

      {!isLearningLabPage && !isPowerAdmin && !["general_stats", "admin_users", "alerts", "thresholds", "report_review_queue", "sample_lookup", "excel_artemis_review", "threshold_learning_lab", "report_snapshot"].some(canSeeModule) && (
        <section style={s.section}>
          <div style={s.sectionTitle}>No Admin Dashboard modules enabled</div>
          <div style={s.hint}>A Power Admin can enable dashboard sections for standard Admin users.</div>
        </section>
      )}
    </div>
  );
}

function DataSourcePill({ status }) {
  const snapshot = status?.source === "supabase_snapshot";
  const value = snapshot
    ? `Supabase Snapshot${status.updatedAt ? `, last updated ${new Date(status.updatedAt).toLocaleString()}` : ""}`
    : "Live Artemis Database";
  return (
    <div style={{ ...s.status, margin: 0, borderRadius: 999, padding: "7px 12px", display: "flex", gap: 6, alignItems: "center" }}>
      <span style={{ fontWeight: 700 }}>Current Dashboard data source:</span>
      <span style={{ fontWeight: 900, color: snapshot ? "var(--teal-dark)" : "#d97706" }}>{value}</span>
    </div>
  );
}

function LearningOperationsPanel({ data, loading, onRecord }) {
  const current = data?.current;
  if (!current) return null;
  const metrics = current.metrics || {};
  const jobs = metrics.jobs || {};
  const storage = metrics.storage || {};
  const retrieval = metrics.retrieval || {};
  const ai = metrics.ai || {};
  const config = metrics.ragConfiguration || {};
  const routeUsage = data?.compatibilityRoutes?.routes || [];
  const auditHistory = data?.history || [];
  const releases = (metrics.supervisedReleases || []).filter((item) => item.releaseKind === "production_report" && item.status === "passed")
    .reduce((sum, item) => sum + Number(item.count || 0), 0);
  const failedReleases = (metrics.supervisedReleases || []).filter((item) => item.releaseKind === "production_report" && item.status === "failed")
    .reduce((sum, item) => sum + Number(item.count || 0), 0);
  const color = current.status === "healthy" ? "#16a34a" : current.status === "critical" ? "#dc2626" : "#d97706";
  const activeJobs = Number(jobs.byStatus?.queued || 0) + Number(jobs.byStatus?.retry_scheduled || 0) + Number(jobs.byStatus?.running || 0);
  const cards = [
    ["RAG Configuration", `${config.mode || "unknown"} v${config.version || "?"}`, config.retrieval_version || "No retrieval version"],
    ["Active Jobs", activeJobs, `${Number(jobs.unresolvedDeadLetters || 0)} unresolved dead letter`],
    ["Storage Integrity", Number(storage.broken_references || 0) + Number(storage.quarantined_objects || 0) + Number(storage.quarantined_bindings || 0), `${Number(storage.unbound_sources || 0)} unbound source(s)`],
    ["Retrieval (24h)", `${Number(retrieval.fallbacks24h || 0)} fallback(s)`, `${retrieval.cachedP95Ms === null ? "—" : Math.round(retrieval.cachedP95Ms)} ms cached p95 · ${retrieval.uncachedP95Ms === null ? "—" : Math.round(retrieval.uncachedP95Ms)} ms uncached p95`],
    ["AI Budget", ai.budgetPercent === null ? `$${Number(ai.monthCostUsd || 0).toFixed(4)}` : `${ai.budgetPercent}%`, ai.budgetUsd === null ? "No monthly ceiling configured" : `$${Number(ai.monthCostUsd || 0).toFixed(4)} of $${Number(ai.budgetUsd).toFixed(2)}`],
    ["Phase 6 Releases", `${releases} of 2`, failedReleases > 0 ? `${failedReleases} technical check(s) require review` : releases >= 2 ? "Exit observation complete" : "Automatically checked after Lake releases"],
    ["Compatibility Routes", routeUsage.length, `${data?.compatibilityRoutes?.observationDays || 30}-day privacy-safe observation`],
  ];
  return (
    <section style={{ ...s.section, borderColor: color }}>
      <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={s.sectionTitle}>Learning Lab Operations</div>
          <div style={{ ...s.hint, color, fontWeight: 900, textTransform: "uppercase" }}>{current.status}</div>
          <div style={s.hint}>Last checked {formatDate(current.checkedAt)} · privacy-safe aggregate diagnostics only</div>
        </div>
        <button type="button" style={s.secondary} onClick={onRecord} disabled={loading}>{loading ? "Recording..." : "Record Audit"}</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 14 }}>
        {cards.map(([label, value, note]) => <Metric key={label} label={label} value={value} note={note} />)}
      </div>
      {current.alerts?.length > 0 ? (
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {current.alerts.map((item) => (
            <div key={item.code} role="alert" style={{ ...s.status, ...(item.severity === "critical" ? s.err : { background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" }) }}>
              <strong>{item.code.replaceAll("_", " ")}:</strong> {item.message} ({item.value}{item.limit === null ? "" : `; target ${item.limit}`})
            </div>
          ))}
        </div>
      ) : <div style={{ ...s.status, ...s.ok, marginTop: 14 }}>No active Learning Lab operational alerts.</div>}
      {auditHistory.length > 0 && (
        <div style={{ ...s.panel, marginTop: 14 }}>
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Recent recorded audits</div>
          <div style={{ display: "grid", gap: 6 }}>
            {auditHistory.slice(0, 5).map((audit) => (
              <div key={audit.id} style={{ ...s.row, justifyContent: "space-between", gap: 12 }}>
                <span><strong style={{ textTransform: "capitalize" }}>{audit.status}</strong> · {audit.source || "Recorded"}</span>
                <span style={s.hint}>{(audit.alerts || []).length} alert(s) · {formatDate(audit.createdAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {routeUsage.length > 0 && (
        <details style={{ ...s.panel, marginTop: 12 }}>
          <summary style={{ cursor: "pointer", fontWeight: 900 }}>Compatibility route activity</summary>
          <div style={{ ...s.hint, margin: "8px 0" }}>Aggregated route families only; no user, IP, report ID, query, or request content is stored.</div>
          <div style={{ display: "grid", gap: 6 }}>
            {routeUsage.slice(0, 10).map((route) => (
              <div key={`${route.method}:${route.routeKey}`} style={{ ...s.row, justifyContent: "space-between", gap: 12 }}>
                <code>{route.method} {route.routeKey}</code>
                <span style={s.hint}>{route.requestCount} request(s) · last {formatDate(route.lastSeenAt)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

const SNAPSHOT_STAGES = [
  { status: "queued", label: "Queued" },
  { status: "authenticating", label: "Sign In" },
  { status: "exporting", label: "Open Export" },
  { status: "downloading", label: "Download" },
  { status: "validating", label: "Validate" },
  { status: "importing", label: "Upload" },
  { status: "activating", label: "Activate" },
  { status: "post_sync", label: "Sync" },
];

function snapshotStatusLabel(status) {
  if (status === "completed") return "Complete";
  if (status === "no_change") return "Complete — No Changes";
  if (status === "failed") return "Failed";
  if (status === "authentication_required") return "Authentication Required";
  return String(status || "queued").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ReportSnapshotSection({ state, schedule, scheduleDirty, loading, onScheduleChange, onDiscardSchedule, onSaveSchedule, onUpdateNow, onTogglePin, canManage }) {
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const active = state?.currentJob;
  const latestJob = active || state?.jobs?.[0] || null;
  const knownJobId = useRef(latestJob?.id || null);
  const [visibleJobId, setVisibleJobId] = useState(() => active?.id || null);
  useEffect(() => {
    if (active?.id) {
      knownJobId.current = active.id;
      setVisibleJobId(active.id);
      return;
    }
    if (latestJob?.id && latestJob.id !== knownJobId.current) {
      knownJobId.current = latestJob.id;
      setVisibleJobId(latestJob.id);
    }
  }, [active?.id, latestJob?.id]);
  const currentUpdate = visibleJobId
    ? (active?.id === visibleJobId ? active : state?.jobs?.find((job) => job.id === visibleJobId)) || null
    : null;
  const currentStatus = currentUpdate?.status || "queued";
  const updateComplete = ["completed", "no_change"].includes(currentStatus);
  const updateFailed = ["failed", "authentication_required"].includes(currentStatus);
  const activeStageIndex = Math.max(0, SNAPSHOT_STAGES.findIndex((stage) => stage.status === currentStatus));
  const completedStageIndex = updateComplete ? SNAPSHOT_STAGES.length - 1 : activeStageIndex - 1;
  const stageProgress = updateComplete || updateFailed ? 100 : 50;
  const [currentTime, setCurrentTime] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const scheduleReady = Boolean(schedule);
  const toggleWeekday = (day) => {
    const current = new Set(schedule?.weekdays || []);
    if (current.has(day)) current.delete(day); else current.add(day);
    onScheduleChange({ ...schedule, weekdays: [...current].sort() });
  };
  return (
    <section style={{ ...s.section, borderColor: "color-mix(in srgb, var(--teal) 45%, var(--border))" }}>
      <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ ...s.row, gap: 8, marginBottom: 4 }}>
            <div style={{ ...s.sectionTitle, marginBottom: 0 }}>Report Data Snapshot</div>
            <div style={{ ...s.status, margin: 0, borderRadius: 999, padding: "5px 10px", fontSize: 12 }}>
              <strong>Current Date/Time:</strong> {currentTime.toLocaleString()}
            </div>
          </div>
          <div style={s.hint}>Export the complete flattened Artemis workbook, validate it, and atomically update the Supabase report snapshot.</div>
        </div>
        {canManage && (
          <div style={s.row}>
            <button type="button" style={state?.sourceStatus?.pinned ? s.danger : s.secondary} disabled={loading || Boolean(active) || state?.reportDataSource !== "supabase_snapshot"} onClick={onTogglePin}>
              {state?.sourceStatus?.pinned ? "Release Pin" : "Enable Pin"}
            </button>
            <button type="button" style={{ ...s.btn, opacity: loading || active || !state?.automationConfigured || !state?.credentialsConfigured ? 0.55 : 1 }} disabled={loading || Boolean(active) || !state?.automationConfigured || !state?.credentialsConfigured} onClick={onUpdateNow}>
              {active ? "Update in progress" : "Update Now"}
            </button>
          </div>
        )}
      </div>

      <div style={{ ...s.grid, margin: "16px 0" }}>
        <div style={s.panel}><div style={s.label}>Current App Source</div><div style={{ ...s.value, fontSize: 18 }}>{state?.reportDataSource || "Unknown"}</div></div>
        <div style={s.panel}><div style={s.label}>Active Snapshot</div><div style={{ ...s.value, fontSize: 18 }}>{state?.activeBatch?.report_count?.toLocaleString?.() || state?.activeBatch?.report_count || "None"}</div><div style={s.hint}>reports</div></div>
        <div style={s.panel}><div style={s.label}>Last Activated</div><div style={{ ...s.value, fontSize: 16 }}>{state?.activeBatch?.activated_at ? new Date(state.activeBatch.activated_at).toLocaleString() : "Not yet"}</div></div>
        <div style={s.panel}><div style={s.label}>Next Scheduled Run</div><div style={{ ...s.value, fontSize: 16 }}>{schedule?.nextRunAt ? new Intl.DateTimeFormat(undefined,{dateStyle:"medium",timeStyle:"short",timeZone:schedule.timezone||undefined}).format(new Date(schedule.nextRunAt)) : "Disabled"}</div>{schedule?.nextRunAt&&<div style={s.hint}>{schedule.timezone} · Your local time: {new Date(schedule.nextRunAt).toLocaleString()}</div>}</div>
      </div>

      {state?.reportDataSource !== "supabase_snapshot" && (
        <div style={{ ...s.status, color: "#d97706", marginBottom: 12 }}>The dashboard still reads Artemis directly. Snapshot updates can be tested, but production cutover requires REPORT_DATA_SOURCE=supabase_snapshot.</div>
      )}
      {(!state?.credentialsConfigured || !state?.automationConfigured) && (
        <div style={{ ...s.status, ...s.err, marginBottom: 12 }}>Artemis export URLs or credentials are not configured on the worker.</div>
      )}
      {currentUpdate && (
        <div style={{ ...s.panel, marginBottom: 14, borderColor: updateFailed ? "#dc2626" : updateComplete ? "#16a34a" : "var(--teal)" }}>
          <div style={{ ...s.row, justifyContent: "space-between" }}>
            <div style={s.label}>Current Update: {snapshotStatusLabel(currentStatus)}</div>
            <div style={{ ...s.row, gap: 8 }}>
              <div style={{ ...s.hint, fontWeight: 900, color: updateFailed ? "#dc2626" : updateComplete ? "#16a34a" : "var(--teal-dark)" }}>
                {updateComplete ? "All steps complete" : updateFailed ? "Update stopped" : `Step ${activeStageIndex + 1} of ${SNAPSHOT_STAGES.length}`}
              </div>
              {updateComplete && (
                <button
                  type="button"
                  aria-label="Close completed snapshot update"
                  title="Close"
                  onClick={() => setVisibleJobId(null)}
                  style={{ ...s.secondary, padding: 0, width: 28, height: 28, borderRadius: 999, fontSize: 18, lineHeight: 1 }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div
            style={{ ...s.loadingTrack, marginTop: 10, height: 12 }}
            role="progressbar"
            aria-label={`Report snapshot ${snapshotStatusLabel(currentStatus)} progress`}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={stageProgress}
            aria-valuetext={updateComplete ? "All snapshot update steps complete" : updateFailed ? "Snapshot update stopped" : `${snapshotStatusLabel(currentStatus)} in progress`}
          >
            <div
              key={currentStatus}
              className={!updateComplete && !updateFailed ? "sdx-snapshot-progress-active" : undefined}
              style={{
                height: "100%",
                width: updateComplete || updateFailed ? "100%" : undefined,
                borderRadius: 999,
                background: updateFailed ? "#dc2626" : updateComplete ? "#16a34a" : "var(--teal)",
              }}
            />
          </div>
          <div style={{ ...s.row, gap: 7, marginTop: 10 }} aria-label="Snapshot update stages">
            {SNAPSHOT_STAGES.map((stage, index) => {
              const complete = index <= completedStageIndex;
              const isCurrent = !updateComplete && !updateFailed && index === activeStageIndex;
              return (
                <div
                  key={stage.status}
                  style={{
                    border: `1px solid ${complete ? "#16a34a" : isCurrent ? "var(--teal)" : "var(--border)"}`,
                    borderRadius: 999,
                    padding: "5px 9px",
                    background: complete ? "#dcfce7" : "var(--border-subtle)",
                    color: complete ? "#166534" : "var(--text-muted)",
                    fontSize: 11,
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                  }}
                >
                  {complete ? "✓ " : ""}{stage.label}
                </div>
              );
            })}
          </div>
          <div style={{ ...s.hint, marginTop: 8 }}>
            {currentUpdate.errorMessage || currentUpdate.progress?.message || (updateComplete ? "Snapshot update completed." : "Working...")}
          </div>
          {currentUpdate.finishedAt && <div style={{ ...s.hint, marginTop: 4 }}>Finished {new Date(currentUpdate.finishedAt).toLocaleString()}</div>}
        </div>
      )}

      {scheduleReady && canManage && (
        <div style={s.toolbar}>
          <label style={s.row}>
            <input type="checkbox" checked={Boolean(schedule.enabled)} onChange={(event) => onScheduleChange({ ...schedule, enabled: event.target.checked })} />
            <span style={{ ...s.hint, fontWeight: 900 }}>Enable recurring updates</span>
          </label>
          <label>
            <div style={s.label}>Cadence</div>
            <select style={s.input} value={schedule.cadence} onChange={(event) => onScheduleChange({ ...schedule, cadence: event.target.value })}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </label>
          <label>
            <div style={s.label}>Local time</div>
            <input style={s.input} type="time" value={schedule.localTime || "01:00"} onChange={(event) => onScheduleChange({ ...schedule, localTime: event.target.value })} />
          </label>
          <label>
            <div style={s.label}>Timezone</div>
            <select style={s.input} value={schedule.timezone} onChange={(event) => onScheduleChange({ ...schedule, timezone: event.target.value })}>
              <option value="America/Los_Angeles">Pacific</option>
              <option value="America/Denver">Mountain</option>
              <option value="America/Chicago">Central</option>
              <option value="America/New_York">Eastern</option>
              <option value="UTC">UTC</option>
            </select>
          </label>
          <button type="button" style={s.secondary} disabled={loading||!scheduleDirty} onClick={onDiscardSchedule}>Discard Schedule Changes</button>
          <button type="button" style={s.btn} disabled={loading||!scheduleDirty} onClick={onSaveSchedule}>Save Schedule</button>
          {schedule.cadence === "weekly" && (
            <div style={{ ...s.row, flexBasis: "100%" }}>
              {weekdays.map((label, day) => (
                <button key={label} type="button" style={(schedule.weekdays || []).includes(day) ? s.btn : s.secondary} onClick={() => toggleWeekday(day)}>{label}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={s.subTitle}>Recent Updates</div>
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Started</th><th style={s.th}>Trigger</th><th style={s.th}>Status</th><th style={s.th}>Reports</th><th style={s.th}>Result</th></tr></thead>
          <tbody>
            {(state?.jobs || []).map((job) => (
              <tr key={job.id}>
                <td style={s.td}>{new Date(job.startedAt || job.createdAt).toLocaleString()}</td>
                <td style={s.td}>{job.triggerType}</td>
                <td style={s.td}>{job.status.replaceAll("_", " ")}</td>
                <td style={s.td}>{job.validationSummary?.reportCount?.toLocaleString?.() || "—"}</td>
                <td style={s.td}>{job.errorMessage || job.progress?.message || "—"}</td>
              </tr>
            ))}
            {!state?.jobs?.length && <tr><td style={s.td} colSpan="5">No snapshot update jobs yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const metricAccents = {
  amber: { border: "#f59e0b", background: "color-mix(in srgb, #f59e0b 9%, var(--bg-card))", action: "#b45309" },
  violet: { border: "#8b5cf6", background: "color-mix(in srgb, #8b5cf6 9%, var(--bg-card))", action: "#7c3aed" },
  blue: { border: "#3b82f6", background: "color-mix(in srgb, #3b82f6 9%, var(--bg-card))", action: "#2563eb" },
};

function Metric({ label, value, note, onClick, accent, actionLabel = "View list" }) {
  const interactive = Boolean(onClick);
  const accentStyle = metricAccents[accent];
  return (
    <div
      style={{
        ...s.card,
        cursor: interactive ? "pointer" : "default",
        borderColor: accentStyle?.border || (interactive ? "var(--teal)" : "var(--border)"),
        background: accentStyle?.background || s.card.background,
      }}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onClick() : undefined}
      onKeyDown={(event) => {
        if (interactive && (event.key === "Enter" || event.key === " ")) onClick();
      }}
    >
      <div style={s.label}>{label}</div>
      <div style={s.value}>{value ?? 0}</div>
      {note && <div style={{ ...s.hint, marginTop: 6 }}>{note}</div>}
      {interactive && <div style={{ ...s.hint, marginTop: 8, color: accentStyle?.action || "var(--teal-dark)", fontWeight: 900 }}>{actionLabel}</div>}
    </div>
  );
}

function DashboardSectionModal({ title, onClose, children }) {
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose} role="presentation">
      <section
        style={{ ...s.modal, width: "min(1380px, 97vw)", maxHeight: "92vh", padding: 0 }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={{ ...s.row, position: "sticky", top: 0, zIndex: 20, justifyContent: "space-between", padding: "16px 18px", background: "var(--bg-card)", borderBottom: "1px solid var(--border)" }}>
          <div style={{ ...s.sectionTitle, marginBottom: 0 }}>{title}</div>
          <button type="button" style={s.secondary} onClick={onClose} aria-label={`Close ${title}`}>Close</button>
        </div>
        <div style={{ padding: 16 }}>
          {children}
        </div>
      </section>
    </div>
  );
}

function SampleLookupMetric() {
  return (
    <div style={{ ...s.card, borderColor: "var(--teal)", minWidth: 0 }}>
      <div style={s.label}>Sample ID Lookup</div>
      <div style={{ ...s.hint, margin: "8px 0 12px" }}>Open an admin review dashboard by lab sample ID.</div>
      <SampleLookupPanel embedded compact resultInModal />
    </div>
  );
}

function dashboardUrl(email, sampleId = "") {
  const params = new URLSearchParams({ adminEmail: email || "" });
  if (sampleId) params.set("sampleId", sampleId);
  return `/dashboard?${params.toString()}`;
}

function ReportReviewQueueSection({
  embedded,
  reports,
  total,
  limit,
  page,
  search,
  loading,
  sort,
  settings,
  startDateInput,
  canEditStartDate,
  canSync,
  onStartDateChange,
  onSaveStartDate,
  onClearStartDate,
  onSync,
  onLimitChange,
  onPageChange,
  onSearchChange,
  onSortChange,
  onOpenReport,
  onApprove,
}) {
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  return (
    <section style={{ ...s.section, ...(embedded ? { marginBottom: 0, border: "none", boxShadow: "none", padding: 0 } : {}) }}>
      <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          {!embedded && <div style={s.sectionTitle}>Reports to Review</div>}
          <div style={s.hint}>
            Completed lab reports remain pending for customers until approved. Use a start date to focus this queue on newer reports.
          </div>
        </div>
        {canSync && (
          <div style={s.row}>
            <button type="button" style={s.btn} onClick={onSync} disabled={loading}>Sync Now</button>
          </div>
        )}
      </div>

      <div style={{ ...s.toolbar, alignItems: "flex-end" }}>
        <div>
          <div style={s.label}>Review Start Date</div>
          <div style={s.hint}>
            Current: {settings?.reviewStartDate ? formatDateOnly(settings.reviewStartDate) : "Not set"}{settings?.updatedBy ? `; updated by ${settings.updatedBy}` : ""}
          </div>
        </div>
        <div style={s.row}>
          <input
            style={{ ...s.input, minWidth: 170 }}
            type="date"
            value={startDateInput || ""}
            disabled={!canEditStartDate || loading}
            onChange={(event) => onStartDateChange(event.target.value)}
          />
          {canEditStartDate ? (
            <>
              <button type="button" style={s.btn} onClick={onSaveStartDate} disabled={loading}>Save Start Date</button>
              <button type="button" style={s.secondary} onClick={onClearStartDate} disabled={loading || (!settings?.reviewStartDate && !startDateInput)}>Clear Date</button>
            </>
          ) : (
            <span style={s.hint}>Module access required</span>
          )}
        </div>
      </div>

      <div style={{ ...s.row, marginBottom: 12 }}>
        <input
          style={{ ...s.input, minWidth: 260, flex: "1 1 320px" }}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search by name, email, or sample ID"
        />
        {search && <button type="button" style={s.secondary} onClick={() => onSearchChange("")}>Clear Search</button>}
        <label style={{ ...s.hint, fontWeight: 800 }}>
          Rows{" "}
          <select style={s.input} value={limit} onChange={(event) => onLimitChange(event.target.value)}>
            {[10, 25, 50, 100, 250].map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <button type="button" style={s.secondary} disabled={page <= 0 || loading} onClick={() => onPageChange(page - 1)}>Previous</button>
        <span style={s.hint}>Page {page + 1} of {totalPages}</span>
        <button type="button" style={s.secondary} disabled={page + 1 >= totalPages || loading} onClick={() => onPageChange(page + 1)}>Next</button>
      </div>

      {loading && <div style={{ ...s.hint, marginBottom: 10 }}>Loading review queue...</div>}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <SortableTh label="Queued" field="queued" sort={sort} onSort={onSortChange} />
              <SortableTh label="Sample ID" field="sampleId" sort={sort} onSort={onSortChange} />
              <SortableTh label="Customer" field="customer" sort={sort} onSort={onSortChange} />
              <SortableTh label="Email" field="email" sort={sort} onSort={onSortChange} />
              <SortableTh label="Tested" field="tested" sort={sort} onSort={onSortChange} />
              <th style={s.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report.labSampleId}>
                <td style={s.td}>{formatDate(report.queuedAt) || "N/A"}</td>
                <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontWeight: 800 }}>{report.labSampleId}</td>
                <td style={s.td}>{report.customerName || "N/A"}</td>
                <td style={s.td}>{report.customerEmail || "N/A"}</td>
                <td style={s.td}>{formatDate(report.testedDate) || "N/A"}</td>
                <td style={s.td}>
                  <div style={{ ...s.row, flexWrap: "nowrap" }}>
                    <button type="button" style={s.btn} onClick={() => onOpenReport(report)}>Review Report</button>
                  </div>
                </td>
              </tr>
            ))}
            {reports.length === 0 && !loading && (
              <tr>
                <td style={s.td} colSpan="6">
                  No reports are waiting for review.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdminModulesPanel({ modules, loading, onToggle }) {
  return (
    <details style={s.section}>
      <summary style={{ ...s.sectionTitle, marginBottom: 0, cursor: "pointer" }}>Admin Dashboard Modules</summary>
      <div style={{ ...s.hint, margin: "12px 0" }}>
        Power Admin controls which sections are visible in the standard Admin Dashboard. Power Admin always sees every section.
      </div>
      <div style={s.grid}>
        {modules.map((module) => (
          <div key={module.key} style={s.card}>
            <div style={s.label}>{module.label}</div>
            <div style={{ ...s.value, fontSize: 22 }}>{module.enabledForAdmin ? "Shown" : "Hidden"}</div>
            <div style={{ ...s.hint, margin: "6px 0 12px" }}>
              {module.enabledForAdmin ? "Standard Admins can see this module." : "Standard Admins cannot see this module."}
            </div>
            <button
              type="button"
              style={module.enabledForAdmin ? s.danger : s.btn}
              disabled={loading}
              onClick={() => onToggle(module)}
            >
              {module.enabledForAdmin ? "Hide From Admins" : "Show To Admins"}
            </button>
          </div>
        ))}
      </div>
    </details>
  );
}

function moduleAccessMap(user) {
  const map = {};
  (user.moduleAccess || []).forEach((item) => { map[item.key] = Boolean(item.enabled); });
  return map;
}

function AdminUsersPanel({
  embedded,
  users,
  modules,
  loading,
  inviteEmail,
  inviteName,
  inviteAccess,
  onInviteEmailChange,
  onInviteNameChange,
  onInviteAccessChange,
  onInvite,
  onToggle,
  onToggleModule,
  canManage,
  canManagePower,
}) {
  const grantableModules = modules.filter((module) => canManagePower || module.enabledForAdmin);
  return (
    <section style={{ ...s.section, ...(embedded ? { marginBottom: 0, border: "none", boxShadow: "none", padding: 0 } : {}) }}>
      <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          {!embedded && <div style={s.sectionTitle}>Team Members and Collaborators</div>}
          <div style={s.hint}>
            Admins can invite collaborators and choose section access. Regular Admins can only grant sections they can access; Power Admins can grant any section and manage Power Admin status.
          </div>
        </div>
      </div>

      {canManage && (
        <form style={{ ...s.panel, marginBottom: 14 }} onSubmit={onInvite}>
          <div style={s.subTitle}>Invite Collaborator</div>
          <div style={{ ...s.row, alignItems: "flex-end", marginBottom: 12 }}>
            <label style={{ display: "grid", gap: 5, minWidth: 220, flex: "1 1 220px" }}>
              <span style={s.label}>Email</span>
              <input style={s.input} type="email" value={inviteEmail} onChange={(event) => onInviteEmailChange(event.target.value)} placeholder="name@example.com" required />
            </label>
            <label style={{ display: "grid", gap: 5, minWidth: 180, flex: "1 1 180px" }}>
              <span style={s.label}>Name</span>
              <input style={s.input} value={inviteName} onChange={(event) => onInviteNameChange(event.target.value)} placeholder="Optional" />
            </label>
            <button type="submit" style={s.btn} disabled={loading}>Send Invite</button>
          </div>
          <div style={{ ...s.label, marginBottom: 8 }}>Initial Section Access</div>
          <div style={{ ...s.row, gap: 8 }}>
            {grantableModules.map((module) => (
              <label key={module.key} style={{ ...s.pill, cursor: "pointer", background: inviteAccess[module.key] ? "color-mix(in srgb, var(--teal) 14%, transparent)" : "var(--dashboard-selector-bg)" }}>
                <input
                  type="checkbox"
                  checked={Boolean(inviteAccess[module.key])}
                  onChange={(event) => onInviteAccessChange(module.key, event.target.checked)}
                  style={{ marginRight: 6 }}
                />
                {module.label}
              </label>
            ))}
            {grantableModules.length === 0 && <span style={s.hint}>No grantable sections available.</span>}
          </div>
        </form>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Email</th>
              <th style={s.th}>Name</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Section Access</th>
              {canManagePower && <th style={s.th}>Power Admin</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((adminUser) => {
              const access = moduleAccessMap(adminUser);
              return (
                <tr key={adminUser.email}>
                  <td style={s.td}>{adminUser.email}</td>
                  <td style={s.td}>{adminUser.customerName || "N/A"}</td>
                  <td style={s.td}>{adminUser.status}</td>
                  <td style={{ ...s.td, minWidth: 360 }}>
                    {adminUser.canViewAdminDashboard ? (
                      <span style={s.hint}>Power Admins automatically have every section.</span>
                    ) : (
                      <div style={{ ...s.row, gap: 6 }}>
                        {modules.map((module) => {
                          const canGrantModule = canManagePower || module.enabledForAdmin;
                          return (
                          <label key={module.key} style={{ ...s.pill, cursor: canManage ? "pointer" : "default", background: access[module.key] ? "color-mix(in srgb, var(--teal) 14%, transparent)" : "var(--dashboard-selector-bg)" }}>
                            <input
                              type="checkbox"
                              checked={Boolean(access[module.key])}
                              disabled={!canManage || !canGrantModule || loading}
                              onChange={(event) => onToggleModule(adminUser, module.key, event.target.checked)}
                              style={{ marginRight: 6 }}
                            />
                            {module.label}
                          </label>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  {canManagePower && (
                    <td style={s.td}>
                      <button
                        type="button"
                        style={adminUser.canViewAdminDashboard ? s.danger : s.btn}
                        disabled={loading}
                        onClick={() => onToggle(adminUser)}
                      >
                        {adminUser.canViewAdminDashboard ? "Remove Power" : "Make Power Admin"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {users.length === 0 && <tr><td style={s.td} colSpan={canManagePower ? "5" : "4"}>No invited collaborators found.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ListShell({ title, hint, total, limit, page, search, loading, searchPlaceholder, onLimitChange, onPageChange, onSearchChange, onClose, children }) {
  const scrollRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section ref={scrollRef} style={s.modal} onScroll={(event) => setShowBackToTop(event.currentTarget.scrollTop > 80)} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, position: "sticky", top: -16, zIndex: 2, justifyContent: "space-between", margin: "-16px -16px 14px", padding: "16px", background: "var(--bg-card)", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div style={s.sectionTitle}>{title}</div>
            <div style={s.hint}>{hint}</div>
          </div>
          <div style={s.row}>
            <label style={{ ...s.hint, fontWeight: 800 }}>
              Rows{" "}
              <select style={s.input} value={limit} onChange={(event) => onLimitChange(event.target.value)}>
                {[10, 25, 50, 100, 250].map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <button type="button" style={s.secondary} disabled={page <= 0 || loading} onClick={() => onPageChange(page - 1)}>Previous</button>
            <span style={s.hint}>Page {page + 1} of {totalPages}</span>
            <button type="button" style={s.secondary} disabled={page + 1 >= totalPages || loading} onClick={() => onPageChange(page + 1)}>Next</button>
            <button type="button" style={s.secondary} onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{ ...s.row, marginBottom: 12 }}>
          <input
            style={{ ...s.input, minWidth: 280, flex: "1 1 320px" }}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
          />
          {search && <button type="button" style={s.secondary} onClick={() => onSearchChange("")}>Clear Search</button>}
        </div>
        {loading && <div style={{ ...s.hint, marginBottom: 10 }}>Loading...</div>}
        {children}
        <button
          type="button"
          style={{ ...s.backToTop, ...(showBackToTop ? s.backToTopVisible : {}) }}
          onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
          aria-hidden={!showBackToTop}
          tabIndex={showBackToTop ? 0 : -1}
        >
          <span style={{ fontSize: 30, lineHeight: 1 }}>↑</span>
          <span style={{ fontSize: 12 }}>Back to Top</span>
        </button>
      </section>
    </div>
  );
}

function AlphaTestersPanel({
  testers,
  loading,
  inviteEmail,
  manualInvite,
  customerSearch,
  customerResults,
  customerSearchHasRun,
  customerSearchLoading,
  customerInvitingEmail,
  onInviteEmailChange,
  onInvite,
  onCustomerSearchChange,
  onCustomerSearch,
  onInviteCustomer,
  onHoldAll,
  onStatusChange,
  onResend,
  onOpenCustomer,
}) {
  const holdEligibleCount = testers.filter((tester) => ["invited", "active"].includes(tester.status)).length;
  return (
    <div>
      <div style={{ ...s.panel, marginBottom: 14, borderColor: "#fca5a5", background: "#fff7f7" }}>
        <div style={s.subTitle}>Emergency access control</div>
        <div style={{ ...s.hint, marginBottom: 10 }}>
          Temporarily halt dashboard access for every active or invited Alpha Tester during maintenance or technical updates. Already held or revoked accounts remain unchanged.
        </div>
        <button type="button" style={s.danger} disabled={loading || holdEligibleCount === 0} onClick={onHoldAll}>
          {holdEligibleCount === 0 ? "All Alpha Testers Already Held" : `Put All Alpha Testers on Hold (${holdEligibleCount})`}
        </button>
      </div>
      <div style={{ ...s.panel, marginBottom: 14 }}>
        <div style={s.subTitle}>Find an existing customer</div>
        <div style={{ ...s.hint, marginBottom: 10 }}>Search by customer name, report email, or customer ID, then invite the matching customer.</div>
        <form style={s.row} onSubmit={(event) => { event.preventDefault(); onCustomerSearch(); }}>
          <input
            style={{ ...s.input, flex: "1 1 360px" }}
            value={customerSearch}
            onChange={(event) => onCustomerSearchChange(event.target.value)}
            placeholder="Search customers"
            aria-label="Search customers to invite as Alpha Testers"
          />
          <button type="submit" style={s.secondary} disabled={customerSearchLoading || !customerSearch.trim()}>{customerSearchLoading ? "Searching…" : "Search"}</button>
        </form>
        {customerSearchHasRun && !customerSearchLoading && customerResults.length === 0 && <div style={{ ...s.hint, marginTop: 10 }}>No matching customers found.</div>}
        {customerResults.length > 0 && (
          <div style={{ overflowX: "auto", marginTop: 12 }}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Customer</th><th style={s.th}>Email</th><th style={s.th}>Reports</th><th style={s.th}>Account</th><th style={s.th}>Action</th></tr></thead>
              <tbody>
                {customerResults.map((customer) => (
                  <tr key={`${customer.acCustomerId}-${customer.customerEmail}`}>
                    <td style={s.td}>{customer.customerName || "N/A"}<div style={s.hint}>{customer.acCustomerId || "No customer ID"}</div></td>
                    <td style={s.td}>{customer.customerEmail}</td>
                    <td style={s.td}>{customer.totalReports}</td>
                    <td style={s.td}>{customer.account ? `${customer.account.role} · ${customer.account.status}` : "No dashboard account"}</td>
                    <td style={s.td}>
                      {customer.account?.isAlphaTester ? (
                        <span style={s.pill}>Alpha · {customer.account.status}</span>
                      ) : customer.account && customer.account.role !== "customer" ? (
                        <span style={s.hint}>{customer.account.role} accounts cannot be invited</span>
                      ) : (
                        <button type="button" style={s.btn} disabled={Boolean(customerInvitingEmail)} onClick={() => onInviteCustomer(customer)}>
                          {customerInvitingEmail === customer.customerEmail.toLowerCase() ? "Inviting…" : "Invite"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ ...s.panel, marginBottom: 14 }}>
        <div style={s.subTitle}>Invite an alpha tester</div>
        <div style={{ ...s.hint, marginBottom: 10 }}>
          Only the report email address is required. The invitation link lasts 72 hours. After setting a password, the tester can sign in and see released reports whose customer email exactly matches this address.
        </div>
        <div style={s.row}>
          <label style={{ ...s.hint, flex: "1 1 320px" }}>Email<br /><input style={{ ...s.input, width: "100%", boxSizing: "border-box" }} type="email" value={inviteEmail} onChange={(event) => onInviteEmailChange(event.target.value)} placeholder="tester@example.com" /></label>
          <button type="button" style={s.btn} disabled={loading || !inviteEmail.trim()} onClick={onInvite}>Send Invite</button>
        </div>
      </div>
      {manualInvite && (
        <div style={{ ...s.panel, marginBottom: 14, borderColor: "#d97706", background: "#fffbeb" }}>
          <strong style={{ color: "#92400e" }}>Send this setup link to {manualInvite.email}</strong>
          <div style={{ ...s.hint, margin: "6px 0 8px", color: "#92400e" }}>Email delivery is not configured. This one-time link expires in 72 hours and should only be shared with the named tester.</div>
          <div style={s.row}>
            <input style={{ ...s.input, flex: "1 1 420px" }} readOnly value={manualInvite.link} aria-label="Manual alpha tester setup link" />
            <button type="button" style={s.secondary} onClick={() => navigator.clipboard?.writeText(manualInvite.link)}>Copy Link</button>
          </div>
        </div>
      )}
      {loading && <div style={{ ...s.hint, marginBottom: 10 }}>Updating alpha access…</div>}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Tester</th><th style={s.th}>Status</th><th style={s.th}>Invited</th><th style={s.th}>Last Login</th><th style={s.th}>Actions</th></tr></thead>
          <tbody>
            {testers.map((tester) => (
              <tr key={tester.email}>
                <td style={s.td}><strong>{tester.customerName || "Unnamed tester"}</strong><div style={s.hint}>{tester.email}</div></td>
                <td style={s.td}><span style={s.pill}>{tester.status}</span>{tester.status === "invited" && <div style={s.hint}>Access granted; password setup pending</div>}</td>
                <td style={s.td}>{formatDate(tester.invitedAt) || "N/A"}<div style={s.hint}>{tester.invitedBy || "N/A"}</div></td>
                <td style={s.td}>{formatDate(tester.lastLogin) || "Never"}</td>
                <td style={s.td}>
                  <div style={s.row}>
                    {(tester.status === "hold" || tester.status === "revoked") && <button type="button" style={s.btn} disabled={loading} onClick={() => onStatusChange(tester.email, "active")}>Grant Access</button>}
                    {tester.status !== "hold" && tester.status !== "revoked" && <button type="button" style={s.secondary} disabled={loading} onClick={() => onStatusChange(tester.email, "hold")}>Place on Hold</button>}
                    {tester.status !== "revoked" && <button type="button" style={s.danger} disabled={loading} onClick={() => onStatusChange(tester.email, "revoked")}>Revoke</button>}
                    {tester.status !== "revoked" && <button type="button" style={s.secondary} disabled={loading} onClick={() => onResend(tester.email)}>Resend Invite</button>}
                    {onOpenCustomer && <button type="button" style={s.secondary} disabled={loading} onClick={() => onOpenCustomer(tester.email)}>Reports</button>}
                  </div>
                </td>
              </tr>
            ))}
            {testers.length === 0 && !loading && <tr><td style={s.td} colSpan="5">No alpha testers have been invited yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomersModal({ customers, total, limit, page, search, loading, sort, onLimitChange, onPageChange, onSearchChange, onSortChange, onSelect, onInviteAlpha, invitingEmail, inviteNotice, onClose }) {
  return (
    <ListShell
      title="Customers"
      hint={`Showing ${customers.length} of ${total} customer${total === 1 ? "" : "s"}${search ? " matching your search" : ""}.`}
      total={total}
      limit={limit}
      page={page}
      search={search}
      loading={loading}
      searchPlaceholder="Search by name, email, or customer ID"
      onLimitChange={onLimitChange}
      onPageChange={onPageChange}
      onSearchChange={onSearchChange}
      onClose={onClose}
    >
      {inviteNotice && (
        <div style={{ ...s.panel, marginBottom: 14, borderColor: inviteNotice.type === "ok" ? "#86efac" : "#fbbf24", background: inviteNotice.type === "ok" ? "#f0fdf4" : "#fffbeb" }}>
          <div style={{ color: inviteNotice.type === "ok" ? "#166534" : "#92400e", fontWeight: 800 }}>{inviteNotice.text}</div>
          {inviteNotice.link && (
            <div style={{ ...s.row, marginTop: 9 }}>
              <input style={{ ...s.input, flex: "1 1 420px" }} readOnly value={inviteNotice.link} aria-label={`Manual Alpha invite link for ${inviteNotice.email}`} />
              <button type="button" style={s.secondary} onClick={() => navigator.clipboard?.writeText(inviteNotice.link)}>Copy setup link</button>
            </div>
          )}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <SortableTh label="Name" field="name" sort={sort} onSort={onSortChange} />
              <SortableTh label="Email" field="email" sort={sort} onSort={onSortChange} />
              <SortableTh label="Submitted" field="submitted" sort={sort} onSort={onSortChange} />
              <SortableTh label="Tested" field="tested" sort={sort} onSort={onSortChange} />
              <SortableTh label="Reports" field="reports" sort={sort} onSort={onSortChange} />
              <SortableTh label="Pending" field="pending" sort={sort} onSort={onSortChange} />
              <SortableTh label="Role" field="role" sort={sort} onSort={onSortChange} />
              <SortableTh label="Status" field="status" sort={sort} onSort={onSortChange} />
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={`${customer.acCustomerId}-${customer.customerEmail}`} style={{ cursor: "pointer" }} onClick={() => onSelect(customer.customerEmail)}>
                <td style={s.td}>{customer.customerName || "N/A"}</td>
                <td style={s.td}>{customer.customerEmail}</td>
                <td style={s.td}>{formatDate(customer.submittedDate) || "N/A"}</td>
                <td style={s.td}>{formatDate(customer.testedDate) || "N/A"}</td>
                <td style={s.td}>{customer.totalReports}</td>
                <td style={s.td}>{customer.pendingReports}</td>
                <td style={s.td}>{customer.account?.role || "No account"}</td>
                <td style={s.td}>{customer.account?.status || "N/A"}</td>
                <td style={s.td} onClick={(event) => event.stopPropagation()}>
                  <div style={s.row}>
                    <button type="button" style={s.secondary} onClick={() => onSelect(customer.customerEmail)}>View</button>
                    {onInviteAlpha && (
                      customer.account?.isAlphaTester ? (
                        <span style={s.pill}>Alpha · {customer.account.status}</span>
                      ) : customer.account && customer.account.role !== "customer" ? (
                        <span style={s.hint}>{customer.account.role} account</span>
                      ) : (
                        <button
                          type="button"
                          style={s.btn}
                          disabled={Boolean(invitingEmail)}
                          onClick={() => onInviteAlpha(customer)}
                        >
                          {invitingEmail === customer.customerEmail.toLowerCase() ? "Inviting…" : "Invite as Alpha Tester"}
                        </button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {customers.length === 0 && !loading && <tr><td style={s.td} colSpan="9">{search ? "No customers match this search." : "No customers found."}</td></tr>}
          </tbody>
        </table>
      </div>
    </ListShell>
  );
}

function ReportsModal({
  reports,
  total,
  limit,
  page,
  search,
  componentType,
  componentOptions,
  loading,
  sort,
  onLimitChange,
  onPageChange,
  onSearchChange,
  onComponentTypeChange,
  onSortChange,
  onSelect,
  onClose,
}) {
  return (
    <ListShell
      title="Oil Analysis Reports"
      hint={`Showing ${reports.length} of ${total} report${total === 1 ? "" : "s"}${search || componentType ? " matching your filters" : ""}.`}
      total={total}
      limit={limit}
      page={page}
      search={search}
      loading={loading}
      searchPlaceholder="Search by name, email, or sample ID"
      onLimitChange={onLimitChange}
      onPageChange={onPageChange}
      onSearchChange={onSearchChange}
      onClose={onClose}
    >
      <div style={{ ...s.row, marginBottom: 12 }}>
        <label style={{ ...s.hint, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 8 }}>
          Component type
          <select
            style={{ ...s.input, minWidth: 210 }}
            value={componentType}
            onChange={(event) => onComponentTypeChange(event.target.value)}
          >
            <option value="">All component types</option>
            {(componentOptions || []).map((option) => (
              <option key={option.type} value={option.type}>
                {option.type} ({formatCount(option.count)})
              </option>
            ))}
          </select>
        </label>
        {componentType && (
          <button type="button" style={s.secondary} onClick={() => onComponentTypeChange("")}>
            Clear Component Filter
          </button>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <SortableTh label="Sample ID" field="sampleId" sort={sort} onSort={onSortChange} />
              <SortableTh label="Customer" field="customer" sort={sort} onSort={onSortChange} />
              <SortableTh label="Email" field="email" sort={sort} onSort={onSortChange} />
              <SortableTh label="Tested" field="tested" sort={sort} onSort={onSortChange} />
              <SortableTh label="Submitted" field="submitted" sort={sort} onSort={onSortChange} />
              <SortableTh label="Component" field="component" sort={sort} onSort={onSortChange} />
              <SortableTh label="Status" field="status" sort={sort} onSort={onSortChange} />
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report.labSampleId} role="button" tabIndex={0} style={{ cursor: "pointer" }} onClick={() => onSelect(report.labSampleId)} onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onSelect(report.labSampleId);
              }}>
                <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontWeight: 800 }}>{report.labSampleId}</td>
                <td style={s.td}>{report.customerName || "N/A"}</td>
                <td style={s.td}>{report.customerEmail || "N/A"}</td>
                <td style={s.td}>{formatDate(report.testedDate) || "N/A"}</td>
                <td style={s.td}>{formatDate(report.submittedDate) || "N/A"}</td>
                <td style={s.td}>{report.component_type || "N/A"}</td>
                <td style={s.td}>{report.isReady ? "Ready" : "Pending"}</td>
              </tr>
            ))}
            {reports.length === 0 && !loading && <tr><td style={s.td} colSpan="7">{search || componentType ? "No reports match these filters." : "No reports found."}</td></tr>}
          </tbody>
        </table>
      </div>
    </ListShell>
  );
}

function formatIssueType(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const excelArtemisDecisionOptions = [
  { value: "needs_review", label: "Needs review" },
  { value: "needs_client_input", label: "Needs client input" },
  { value: "resolved_accepted", label: "Resolved / accepted" },
  { value: "use_excel_value", label: "Use released PDF value" },
  { value: "use_artemis_value", label: "Use snapshot value" },
  { value: "exclude_from_threshold_learning", label: "Exclude from threshold learning" },
];

function formatExcelArtemisDecision(value) {
  return excelArtemisDecisionOptions.find((option) => option.value === value)?.label || "Needs review";
}

function getRecommendationReviewTarget(metric) {
  const normalized = String(metric || "").toLowerCase();
  if (normalized.includes("viscosity")) {
    return { type: "section", id: "threshold-learning-viscosity-candidates", label: "Open viscosity evidence" };
  }
  if (normalized.includes("oxidation")) {
    return { type: "section", id: "threshold-learning-oxidation-candidates", label: "Open oxidation evidence" };
  }
  if (normalized.includes("fuel")) {
    return { type: "section", id: "threshold-learning-fuel-candidates", label: "Open fuel evidence" };
  }
  if (normalized.includes("sodium") || normalized.includes("soot")) {
    return { type: "section", id: "threshold-learning-rule-proposals", label: "Open rule proposal area" };
  }
  return { type: "review", search: metric || "", label: "Open review queue" };
}

function ExcelArtemisReviewModal({
  items,
  total,
  decisionSummary,
  limit,
  page,
  search,
  issueType,
  decisionStatus,
  loading,
  onLimitChange,
  onPageChange,
  onSearchChange,
  onIssueTypeChange,
  onDecisionStatusChange,
  onSelect,
  onClose,
}) {
  const decisionCounts = decisionSummary || {};
  const decisionTotal = Object.values(decisionCounts).reduce((sum, count) => sum + Number(count || 0), 0);
  return (
    <ListShell
      title="PDF / Snapshot Comparison Review"
      hint={`Showing ${items.length} of ${total} open comparison review item${total === 1 ? "" : "s"}${search || issueType !== "all" || decisionStatus !== "all" ? " matching your filters" : ""}.`}
      total={total}
      limit={limit}
      page={page}
      search={search}
      loading={loading}
      searchPlaceholder="Search by sample ID, PDF, or metric"
      onLimitChange={onLimitChange}
      onPageChange={onPageChange}
      onSearchChange={onSearchChange}
      onClose={onClose}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          style={{
            ...s.secondary,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            minHeight: 54,
            textAlign: "left",
            borderColor: decisionStatus === "all" ? "#00964d" : s.secondary.borderColor,
            background: decisionStatus === "all" ? "#ecfdf5" : s.secondary.background,
          }}
          onClick={() => onDecisionStatusChange("all")}
        >
          <span style={{ lineHeight: 1.15 }}>All</span>
          <strong style={{ flex: "0 0 auto", padding: "3px 7px", borderRadius: 999, background: "rgba(15, 23, 42, 0.06)" }}>{decisionTotal}</strong>
        </button>
        {excelArtemisDecisionOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            style={{
              ...s.secondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              minHeight: 54,
              textAlign: "left",
              borderColor: decisionStatus === option.value ? "#00964d" : s.secondary.borderColor,
              background: decisionStatus === option.value ? "#ecfdf5" : s.secondary.background,
            }}
            onClick={() => onDecisionStatusChange(option.value)}
          >
            <span style={{ lineHeight: 1.15 }}>{option.label}</span>
            <strong style={{ flex: "0 0 auto", padding: "3px 7px", borderRadius: 999, background: "rgba(15, 23, 42, 0.06)" }}>{decisionCounts[option.value] || 0}</strong>
          </button>
        ))}
      </div>
      <div style={{ ...s.row, marginBottom: 12 }}>
        <label style={{ ...s.hint, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 8 }}>
          Issue type
          <select
            style={{ ...s.input, minWidth: 230 }}
            value={issueType}
            onChange={(event) => onIssueTypeChange(event.target.value)}
          >
            <option value="all">All issue types</option>
            <option value="missing_snapshot_report">Missing snapshot report</option>
            <option value="missing_snapshot_metric">Missing snapshot metric</option>
            <option value="invalid_snapshot_metric">Invalid snapshot metric</option>
            <option value="snapshot_metric_disagreement">PDF / Snapshot value disagreement</option>
            <option value="pdf_threshold_status_disagreement">PDF / threshold status disagreement</option>
            <option value="pdf_overall_status_disagreement">PDF / overall rating disagreement</option>
          </select>
        </label>
        {issueType !== "all" && (
          <button type="button" style={s.secondary} onClick={() => onIssueTypeChange("all")}>
            Clear Issue Filter
          </button>
        )}
        {decisionStatus !== "all" && (
          <button type="button" style={s.secondary} onClick={() => onDecisionStatusChange("all")}>
            Clear Decision Filter
          </button>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Sample</th>
              <th style={s.th}>PDF Report</th>
              <th style={s.th}>Issues</th>
              <th style={s.th}>Latest</th>
              <th style={s.th}>Expert Review</th>
              <th style={s.th}>Suggested Path</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} role="button" tabIndex={0} style={{ cursor: "pointer" }} onClick={() => onSelect(item.id)} onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onSelect(item.id);
              }}>
                <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontWeight: 800 }}>{item.labSampleId || "N/A"}</td>
                <td style={s.td}>{item.workbookPath}</td>
                <td style={s.td}>
                  <div style={{ fontWeight: 900 }}>{item.issueCount}</div>
                  <div style={s.hint}>{(item.issueTypes || []).map(formatIssueType).join(", ")}</div>
                </td>
                <td style={s.td}>{formatDate(item.latestSeenAt) || "N/A"}</td>
                <td style={s.td}>
                  <div style={{ fontWeight: 900 }}>{formatExcelArtemisDecision(item.annotation?.decisionStatus)}</div>
                  {item.annotation?.updatedAt ? (
                    <div style={{ ...s.hint, marginTop: 3 }}>
                      Updated {formatDate(item.annotation.updatedAt)} by {item.annotation.updatedBy || item.annotation.createdBy || "unknown"}
                    </div>
                  ) : (
                    <div style={{ ...s.hint, marginTop: 3 }}>Not reviewed yet</div>
                  )}
                </td>
                <td style={{ ...s.td, minWidth: 280 }}>{item.suggestedPath}</td>
              </tr>
            ))}
            {items.length === 0 && !loading && <tr><td style={s.td} colSpan="6">{search || issueType !== "all" || decisionStatus !== "all" ? "No comparison review items match these filters." : "No open PDF / Snapshot comparison review items."}</td></tr>}
          </tbody>
        </table>
      </div>
    </ListShell>
  );
}

function ExcelArtemisReviewDetailModal({ detail, loading, onSaveAnnotation, onClose }) {
  const issueTypes = detail.issueTypes || [];
  const [decisionStatus, setDecisionStatus] = useState(detail.annotation?.decisionStatus || "needs_review");
  const [note, setNote] = useState(detail.annotation?.note || "");
  useEffect(() => {
    setDecisionStatus(detail.annotation?.decisionStatus || "needs_review");
    setNote(detail.annotation?.note || "");
  }, [detail.id, detail.annotation?.decisionStatus, detail.annotation?.note]);
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <div style={{ ...s.modal, ...s.detailModal }} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.sectionTitle}>PDF / Snapshot Comparison Review</div>
            <div style={s.hint}>{detail.workbookPath}</div>
          </div>
          <button type="button" style={s.secondary} onClick={onClose}>Close</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 12 }}>
          <div style={s.panel}>
            <div style={s.label}>Sample ID</div>
            <div style={{ ...s.value, fontSize: 20 }}>{detail.labSampleId || "N/A"}</div>
          </div>
          <div style={s.panel}>
            <div style={s.label}>Open Issues</div>
            <div style={{ ...s.value, fontSize: 20 }}>{detail.issueCount || 0}</div>
          </div>
          <div style={s.panel}>
            <div style={s.label}>Reconciliation Status</div>
            <div style={{ ...s.value, fontSize: 18 }}>{detail.reconciliation?.status || "N/A"}</div>
          </div>
        </div>
        <div style={{ ...s.panel, marginBottom: 12 }}>
          <div style={s.label}>Issue Types</div>
          <div style={{ marginTop: 8 }}>{issueTypes.length ? issueTypes.map(formatIssueType).join(", ") : "N/A"}</div>
        </div>
        {detail.reconciliation?.summary?.sourceDocumentType === "pdf" && (
          <div style={{ ...s.panel, marginBottom: 12 }}>
            <div style={s.label}>Released PDF Assessment</div>
            <div style={{ ...s.row, marginTop: 8 }}>
              <span style={s.pill}>PDF: {detail.reconciliation.summary.releasedOverallCondition || detail.reconciliation.summary.releasedOverallStatus || "N/A"}</span>
              <span style={s.pill}>Current rules: {detail.reconciliation.summary.evaluatedOverallStatus || "N/A"}</span>
            </div>
            <div style={{ ...s.row, marginTop: 8 }}>
              {Object.values(detail.reconciliation.summary.currentCategoryScores || {}).map((category) => (
                <span key={category.label} style={s.pill}>{category.label}: {category.score ?? "N/A"}/100</span>
              ))}
            </div>
            {detail.reconciliation.summary.releasedComment && (
              <div style={{ marginTop: 10, lineHeight: 1.5 }}>
                <strong>Comments / Recommendations:</strong> {detail.reconciliation.summary.releasedComment}
              </div>
            )}
          </div>
        )}
        <div style={{ ...s.panel, marginBottom: 12, borderColor: "color-mix(in srgb, var(--teal) 35%, var(--border))" }}>
          <div style={s.label}>Suggested Reconciliation Path</div>
          <div style={{ marginTop: 8, lineHeight: 1.5 }}>{detail.suggestedPath}</div>
        </div>
        <form
          style={{ ...s.panel, marginBottom: 12, background: "var(--bg-page)" }}
          onSubmit={(event) => {
            event.preventDefault();
            onSaveAnnotation(detail.id, { decisionStatus, note });
          }}
        >
          <div style={s.label}>Expert Review Decision</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 260px) minmax(0, 1fr)", gap: 10, marginTop: 8 }}>
            <label>
              <span style={s.hint}>Status</span>
              <select
                style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 4 }}
                value={decisionStatus}
                onChange={(event) => setDecisionStatus(event.target.value)}
              >
                {excelArtemisDecisionOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span style={s.hint}>Notes</span>
              <textarea
                style={{ ...s.input, width: "100%", boxSizing: "border-box", minHeight: 82, marginTop: 4, resize: "vertical" }}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Add expert/client direction for this discrepancy group."
              />
            </label>
          </div>
          <div style={{ ...s.row, justifyContent: "space-between", marginTop: 10 }}>
            <div style={s.hint}>
              {detail.annotation?.updatedAt ? `Last updated ${formatDate(detail.annotation.updatedAt)} by ${detail.annotation.updatedBy || detail.annotation.createdBy || "unknown"}` : "No expert decision saved yet."}
            </div>
            <button type="submit" style={s.btn} disabled={loading}>{loading ? "Saving..." : "Save Decision"}</button>
          </div>
        </form>
        {detail.reconciliation?.summary && (
          <div style={{ ...s.panel, marginBottom: 12 }}>
            <div style={s.label}>Run Summary</div>
            <div style={{ ...s.hint, marginTop: 8 }}>
              Override applied: {detail.reconciliation.summary.overrideApplied ? "Yes" : "No"} ·
              Metrics extracted: {detail.reconciliation.summary.metricsExtracted ?? "N/A"} ·
              Missing data issues: {detail.reconciliation.summary.missingDataIssueCount ?? "N/A"}
            </div>
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Type</th>
                <th style={s.th}>Metric</th>
                <th style={s.th}>Released PDF Value</th>
                <th style={s.th}>Snapshot Value</th>
                <th style={s.th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {(detail.issues || []).map((issue) => (
                <tr key={issue.id}>
                  <td style={s.td}>{formatIssueType(issue.issueType)}</td>
                  <td style={s.td}>{issue.metricLabel || issue.metricKey || "Report"}</td>
                  <td style={s.td}>{issue.workbookValue ?? "N/A"}</td>
                  <td style={s.td}>{issue.artemisValue ?? "N/A"}</td>
                  <td style={{ ...s.td, minWidth: 260 }}>{issue.note || "N/A"}</td>
                </tr>
              ))}
              {(!detail.issues || detail.issues.length === 0) && <tr><td style={s.td} colSpan="5">No issue rows found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ExpertKnowledgeLearningLabSection({ learning, loading, onRefresh }) {
  const workspace = learning?.expertWorkspace || { sources: [], rules: [], profiles: [], questions: [], totals: {} };
  const totals = workspace.totals || {};
  const latestUpload = [
    ...(learning?.uploadJobs || []).map((job) => ({
      uploadedAt: job.createdAt || job.created_at,
      fileName: job.fileName || job.file_name,
    })),
    ...(workspace.sources || [])
      .filter((source) => String(source.source_key || source.sourceKey || "").startsWith("upload:"))
      .map((source) => ({
        uploadedAt: source.imported_at || source.importedAt,
        fileName: source.file_name || source.fileName,
      })),
  ].filter((upload) => upload.uploadedAt && !Number.isNaN(new Date(upload.uploadedAt).getTime()))
    .sort((left, right) => new Date(right.uploadedAt).getTime() - new Date(left.uploadedAt).getTime())[0];
  const activeUpload = (learning?.uploadJobs || []).find((job) => ["queued", "running", "retry_scheduled"].includes(job.status));
  const activeProcessed = Number(activeUpload?.progress?.processed || 0);
  const activeTotal = Number(activeUpload?.progress?.total || 0);
  const activePercent = ["queued", "retry_scheduled"].includes(activeUpload?.status)
    ? 3
    : activeTotal > 0
      ? Math.min(96, Math.max(5, Math.round((activeProcessed / activeTotal) * 95)))
      : 8;
  const activeProgressLabel = activeUpload?.status === "queued"
    ? "Queued for processing"
    : activeUpload?.status === "retry_scheduled"
      ? "A temporary problem occurred; retry is scheduled"
    : activeTotal > 0 && activeProcessed >= activeTotal
      ? "Finalizing learned profiles"
      : activeTotal > 0
        ? `Processing report ${Math.min(activeProcessed + 1, activeTotal)} of ${activeTotal}`
        : "Preparing and reading the uploaded batch";
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [knowledgeFile, setKnowledgeFile] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [profileDraft, setProfileDraft] = useState(null);
  const [correction, setCorrection] = useState("");
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [reviewQueue, setReviewQueue] = useState(null);
  const [showExpertRules, setShowExpertRules] = useState(false);
  const [resolvedReviewIds, setResolvedReviewIds] = useState(new Set());
  const [directionInterpretation, setDirectionInterpretation] = useState(null);
  const [questionInterpretation, setQuestionInterpretation] = useState(null);
  const [questionConversation,setQuestionConversation]=useState(null);
  const [conversationInput,setConversationInput]=useState("");
  const [conversationCorrection,setConversationCorrection]=useState("");
  const [conversationLoading,setConversationLoading]=useState(false);
  const [aiThinking, setAiThinking] = useState(null);
  const [reviewFeedback,setReviewFeedback]=useState(null);
  const [selectedProfileMetrics,setSelectedProfileMetrics]=useState(new Set());
  const [dirtyProfileMetrics,setDirtyProfileMetrics]=useState(new Set());

  function openReviewQueue(type, items) {
    if (!items.length) return;
    setEditingId(""); setProfileDraft(null); setCorrection(""); setDirectionInterpretation(null); setQuestionInterpretation(null); setReviewFeedback(null); setSelectedProfileMetrics(new Set()); setDirtyProfileMetrics(new Set());
    setResolvedReviewIds(new Set());
    setReviewQueue({ type, items:[...items], index:0 });
  }
  const activeReviewItem=reviewQueue?.items?.[reviewQueue.index];
  useEffect(()=>{
    if(reviewQueue?.type!=="questions"||!activeReviewItem?.id){setQuestionConversation(null);return undefined;}
    let cancelled=false;setConversationLoading(true);setQuestionConversation(null);setConversationInput("");setConversationCorrection("");
    getAdminLearningQuestionConversation(activeReviewItem.id).then((result)=>{if(!cancelled)setQuestionConversation(result.data);}).catch((err)=>{if(!cancelled)setNotice(err.response?.data?.error||"Unable to open this expert conversation.");}).finally(()=>{if(!cancelled)setConversationLoading(false);});
    return()=>{cancelled=true;};
  },[reviewQueue?.type,activeReviewItem?.id]);
  function markReviewResolved(id) {
    setResolvedReviewIds((current)=>new Set([...current,id]));
  }
  function advanceReviewQueue() {
    setEditingId(""); setProfileDraft(null); setCorrection(""); setDirectionInterpretation(null); setQuestionInterpretation(null); setReviewFeedback(null); setSelectedProfileMetrics(new Set()); setDirtyProfileMetrics(new Set());
    setReviewQueue((current)=>current&&current.index<current.items.length-1?{...current,index:current.index+1}:null);
  }
  function skipReviewItem() {
    setEditingId(""); setProfileDraft(null); setCorrection(""); setDirectionInterpretation(null); setQuestionInterpretation(null); setReviewFeedback(null); setSelectedProfileMetrics(new Set()); setDirtyProfileMetrics(new Set());
    setReviewQueue((current)=>current&&current.index<current.items.length-1?{...current,index:current.index+1}:null);
  }

  async function refreshProfiles() {
    setBusy(true); setNotice("");
    try { await rebuildAdminLearningProfiles(); await onRefresh(); setNotice("Learned profiles refreshed from Lake's knowledge and all available PDF evidence."); }
    catch (err) { setNotice(err.response?.data?.error || "Profiles could not be refreshed."); }
    finally { setBusy(false); }
  }
  async function saveExpertRule(rule, draft) {
    setBusy(true); setNotice("");
    try {
      await updateAdminLearningExpertRule(rule.id, draft);
      await onRefresh();
      setNotice("Lake's expert-rule adjustment was saved as a new version and all affected profiles were recalculated.");
      return true;
    } catch (err) {
      setNotice(err.response?.data?.error || err.message || "The expert rule could not be updated.");
      return false;
    } finally { setBusy(false); }
  }
  async function uploadKnowledge(event) {
    event.preventDefault();
    if (!knowledgeFile) return setNotice("Choose a PDF, XLSX, or ZIP file first.");
    const fileError=validateLearningLabFile(knowledgeFile);if(fileError)return setNotice(fileError);
    setBusy(true); setNotice("");
    try {
      const fileBase64 = await fileToBase64(knowledgeFile);
      if (/\.zip$/i.test(knowledgeFile.name)) await uploadAdminThresholdLearningArchive({ fileName:knowledgeFile.name,fileBase64,runLimit:500,aiEnabled:false,mode:"dry_run" });
      else {
        const result=await uploadAdminLearningObject(knowledgeFile);
        const job=result.data?.job||result.data?.processing||{};
        setNotice(job.status==="completed"?"This unchanged document was already processed; no duplicate extraction work was created.":"Knowledge source verified and queued for private, page-provenanced extraction.");
      }
      setKnowledgeFile(null); await onRefresh();
      if(/\.zip$/i.test(knowledgeFile.name))setNotice("PDF batch accepted. Profiles can be refreshed after processing completes.");
    } catch (err) { setNotice(err.response?.data?.error || err.message || "Knowledge upload failed."); }
    finally { setBusy(false); }
  }
  function beginEdit(profile) {
    setEditingId(profile.id); setCorrection(""); setDirectionInterpretation(null); setReviewFeedback(null);
    setSelectedProfileMetrics(new Set()); setDirtyProfileMetrics(new Set());
    setProfileDraft({ templateName:profile.context?.templateName || "", recommendations:(profile.recommendations || []).map((row)=>({ ...row, proposed:{...(row.proposed||{})} })) });
  }
  function updateBoundary(metricKey, field, value) {
    setDirectionInterpretation(null);
    setDirtyProfileMetrics((current)=>new Set([...current,metricKey]));
    setSelectedProfileMetrics((current)=>{const next=new Set(current);next.delete(metricKey);return next;});
    setProfileDraft((current)=>({ ...current,recommendations:current.recommendations.map((row)=>row.metricKey===metricKey?{...row,proposed:{...row.proposed,[field]:Number(value)}}:row) }));
  }
  function replaceQueuedProfile(profile) {
    if(!profile)return;
    setReviewQueue((current)=>current?{...current,items:current.items.map((item,index)=>index===current.index?profile:item)}:current);
  }
  function toggleProfileMetric(metricKey) {
    setSelectedProfileMetrics((current)=>{const next=new Set(current);if(next.has(metricKey))next.delete(metricKey);else next.add(metricKey);return next;});
  }
  async function saveProfile(profile) {
    const templateChanged=cleanUi(profileDraft?.templateName)!==cleanUi(profile.context?.templateName);
    const recommendations=(profileDraft?.recommendations||[]).filter((row)=>dirtyProfileMetrics.has(row.metricKey));
    if(!templateChanged&&!recommendations.length)return setNotice("Change a threshold value or assigned template before recalculating.");
    setBusy(true); setNotice("");
    try { const result=await updateAdminLearningProfile(profile.id,{templateName:profileDraft?.templateName,recommendations,explanation:correction});const updated=(result.data?.profiles||[]).find((item)=>item.id===profile.id);replaceQueuedProfile(updated);setProfileDraft(updated?{templateName:updated.context?.templateName||"",recommendations:(updated.recommendations||[]).map((row)=>({...row,proposed:{...(row.proposed||{})}}))}:null);setSelectedProfileMetrics(new Set());setDirtyProfileMetrics(new Set());await onRefresh();setNotice("Lake's edits were saved as binding knowledge and recalculated. Review the updated borders, then select any eligible rows to publish now."); }
    catch(err){setNotice(err.response?.data?.error||"Profile changes could not be saved.");}finally{setBusy(false);}
  }
  async function approveSelectedProfileMetrics(profile) {
    const metricKeys=[...selectedProfileMetrics];
    if(!metricKeys.length)return setNotice("Select at least one orange review row to approve.");
    if(dirtyProfileMetrics.size)return setNotice("Save edited values and recalculate before approving selected rows.");
    if(cleanUi(profileDraft?.templateName)!==cleanUi(profile.context?.templateName))return setNotice("Save the assigned-template change and recalculate before approving selected rows.");
    setBusy(true);setNotice("");setReviewFeedback({type:"working",title:"Validating and publishing selected rows…"});
    try{const result=await approveSelectedAdminLearningProfileMetrics(profile.id,metricKeys);const updated=result.data?.profile||(result.data?.workspace?.profiles||[]).find((item)=>item.id===profile.id);replaceQueuedProfile(updated);if(updated?.status==="approved")markReviewResolved(profile.id);setProfileDraft(updated?{templateName:updated.context?.templateName||"",recommendations:(updated.recommendations||[]).map((row)=>({...row,proposed:{...(row.proposed||{})}}))}:null);setSelectedProfileMetrics(new Set());setDirtyProfileMetrics(new Set());await onRefresh();const remaining=Number(result.data?.remainingChanges||0);const detail=`${metricKeys.length} selected threshold change${metricKeys.length===1?" was":"s were"} validated and published.${remaining?` ${remaining} proposed change${remaining===1?" remains":"s remain"} for later review.`:" The profile is now complete."}`;setReviewFeedback({type:"success",title:"Selected rows approved and implemented",detail,completedAt:new Date().toISOString()});setNotice(detail);}catch(err){const payload=err.response?.data||{};const detail=payload.error||"The selected rows could not be approved.";if(payload.code==="HISTORICAL_COLOR_CONFLICTS"&&payload.details?.conflicts?.length)setReviewFeedback({type:"conflict",title:"Selected rows still contain blocking decisions",detail:"Review the red rows, adjust their thresholds, save and recalculate, then try the selection again.",conflictDetails:payload.details});else setReviewFeedback({type:"error",title:"Selected approval not completed",detail});setNotice(detail);}finally{setBusy(false);}
  }
  async function approveProfile(profile) {
    setBusy(true);setNotice("");setReviewFeedback({type:"working",title:"Validating and publishing…"});try{const result=await approveAdminLearningProfile(profile.id);markReviewResolved(profile.id);await onRefresh();const published=result.data?.published;const detail=published?`The threshold configuration was implemented immediately${published.id?` as published version ${published.id}`:""}.` : "Lake’s approval was recorded immediately. No threshold version was created because this profile produced no scoring change.";setReviewFeedback({type:"success",title:"Correct / Approve completed",detail,completedAt:new Date().toISOString()});setNotice(detail);}catch(err){const payload=err.response?.data||{};const detail=payload.error||"Profile could not be approved or published.";if(payload.code==="HISTORICAL_COLOR_CONFLICTS"&&payload.details?.conflicts?.length){setEditingId(profile.id);setCorrection("Resolve the historical color conflicts shown during approval.");setDirectionInterpretation(null);setProfileDraft({templateName:profile.context?.templateName||"",recommendations:(profile.recommendations||[]).map((row)=>({...row,proposed:{...(row.proposed||{})}}))});setReviewFeedback({type:"conflict",title:"Review the failed historical decisions",detail:"Adjust the affected thresholds below, then save and retry publication without leaving this review.",conflictDetails:payload.details});}else{setReviewFeedback({type:"error",title:"Approval not completed",detail});}setNotice(detail);}finally{setBusy(false);}
  }
  async function resolveConflictsAndPublish(profile, conflicts) {
    const metricKeys=new Set((conflicts||[]).map((item)=>item.metricKey));
    const recommendations=(profileDraft?.recommendations||[]).filter((item)=>metricKeys.has(item.metricKey));
    setBusy(true);setNotice("");setReviewFeedback((current)=>({...current,type:"working",title:"Saving corrections and retrying publication…"}));
    try {
      await updateAdminLearningProfile(profile.id,{templateName:profileDraft?.templateName||profile.context?.templateName,recommendations,explanation:correction||"Resolved historical color conflicts during approval."});
      const result=await approveAdminLearningProfile(profile.id);markReviewResolved(profile.id);await onRefresh();const published=result.data?.published;const detail=published?`Corrections validated and published${published.id?` as version ${published.id}`:""}.`:"Corrections validated and Lake’s approval was recorded; no new threshold version was needed.";setEditingId("");setProfileDraft(null);setCorrection("");setReviewFeedback({type:"success",title:"Corrected, approved, and implemented",detail,completedAt:new Date().toISOString()});setNotice(detail);
    } catch(err) {
      const payload=err.response?.data||{};const detail=payload.error||"The corrections could not be validated and published.";
      if(payload.code==="HISTORICAL_COLOR_CONFLICTS"&&payload.details?.conflicts?.length)setReviewFeedback({type:"conflict",title:"Some historical decisions still need review",detail:"The correction was saved and recalculated. Review the remaining items below, adjust again, and retry publication.",conflictDetails:payload.details});
      else setReviewFeedback({type:"error",title:"Correction was not published",detail});
      setNotice(detail);
    } finally { setBusy(false); }
  }
  async function needsCorrection(profile) {
    if (!cleanUi(correction)) return setNotice("Describe what needs correction first.");
    setBusy(true);try{await correctAdminLearningProfile(profile.id,correction);markReviewResolved(profile.id);setEditingId("");setCorrection("");await onRefresh();setNotice("Correction request saved as an Outstanding Question. Select Next to continue.");}catch(err){setNotice(err.response?.data?.error||"Correction request could not be saved.");}finally{setBusy(false);}
  }
  async function interpretDirection(profile) {
    if (!cleanUi(correction)) return setNotice("Enter Lake's explanation or direction first.");
    setBusy(true);setAiThinking({type:"profile",id:profile.id});setNotice("");try{const result=await interpretAdminLearningDirection(profile.id,correction);setDirectionInterpretation(result.data);setNotice("AI interpretation ready. Review it carefully before confirming and recalculating.");}catch(err){setNotice(err.response?.data?.error||"Lake's direction could not be interpreted.");}finally{setBusy(false);setAiThinking(null);}
  }
  async function confirmDirection(profile) {
    if(!directionInterpretation?.id)return;const lowConfidence=directionInterpretation.interpretation?.confidence==="low";if(lowConfidence&&!confirmLowConfidenceInterpretation())return;setBusy(true);setNotice("");try{await confirmAdminLearningDirection(profile.id,directionInterpretation.id,lowConfidence);markReviewResolved(profile.id);setEditingId("");setProfileDraft(null);setCorrection("");setDirectionInterpretation(null);await onRefresh();setNotice("Lake confirmed the structured interpretation. Related profiles and historical validation were recalculated. Select Next to continue.");}catch(err){setNotice(err.response?.data?.error||"The interpreted direction could not be applied.");}finally{setBusy(false);}
  }
  async function answerQuestion(question) {
    const answer=questionAnswers[question.id]||{};if(!answer.choice)return setNotice("Choose an answer before saving Lake's guidance.");setBusy(true);try{await answerAdminLearningQuestion(question.id,{choice:answer.choice,values:{warn:answer.warn===""||answer.warn===undefined?null:Number(answer.warn),fail:answer.fail===""||answer.fail===undefined?null:Number(answer.fail)},explanation:answer.explanation||""});markReviewResolved(question.id);await onRefresh();setNotice("Lake's answer was saved and the profiles were recalculated. Select Next to continue.");}catch(err){setNotice(err.response?.data?.error||"The answer could not be saved.");}finally{setBusy(false);}
  }
  async function interpretQuestion(question) {
    const answer=questionAnswers[question.id]||{};if(!cleanUi(answer.explanation))return setNotice("Enter Lake's expert explanation first.");setBusy(true);setAiThinking({type:"question",id:question.id});setNotice("");try{const result=await interpretAdminLearningQuestion(question.id,{choice:answer.choice||"",values:{warn:answer.warn===""||answer.warn===undefined?null:Number(answer.warn),fail:answer.fail===""||answer.fail===undefined?null:Number(answer.fail)},explanation:answer.explanation});setQuestionInterpretation(result.data);setNotice("AI interpretation ready. Review it carefully before confirming and recalculating.");}catch(err){setNotice(err.response?.data?.error||"The expert explanation could not be interpreted.");}finally{setBusy(false);setAiThinking(null);}
  }
  async function confirmQuestion(question) {
    if(!questionInterpretation?.id)return;const lowConfidence=questionInterpretation.interpretation?.confidence==="low";if(lowConfidence&&!confirmLowConfidenceInterpretation())return;setBusy(true);setNotice("");try{await confirmAdminLearningQuestion(question.id,questionInterpretation.id,lowConfidence);markReviewResolved(question.id);setQuestionInterpretation(null);await onRefresh();setNotice("Lake confirmed the structured explanation. Related profiles and historical validation were recalculated. Select Next to continue.");}catch(err){setNotice(err.response?.data?.error||"The interpreted explanation could not be applied.");}finally{setBusy(false);}
  }
  async function sendConversationMessage(question){if(!cleanUi(conversationInput))return;setConversationLoading(true);setNotice("");try{const result=await sendAdminLearningQuestionMessage(question.id,conversationInput);setQuestionConversation(result.data);setConversationInput("");}catch(err){setNotice(err.response?.data?.error||"The answer could not be added to the conversation.");}finally{setConversationLoading(false);}}
  async function confirmConversationProposition(question,confirmed){if(!confirmed&&!cleanUi(conversationCorrection))return setNotice("Tell the AI what should be corrected first.");setConversationLoading(true);setNotice("");try{const result=await confirmAdminLearningQuestionProposition(question.id,confirmed,conversationCorrection);setQuestionConversation(result.data);setConversationCorrection("");}catch(err){setNotice(err.response?.data?.error||"The interpretation could not be updated.");}finally{setConversationLoading(false);}}
  async function runConversationAction(question,action){setConversationLoading(true);setNotice("");try{const result=await runAdminLearningQuestionAction(question.id,action);setQuestionConversation(result.data);if(result.data?.status==="completed"){markReviewResolved(question.id);await onRefresh();setNotice("The question was excluded without creating a rule. Its conversation remains in the audit trail. Select Next to continue.");}}catch(err){setNotice(err.response?.data?.error||"The conversation action could not be completed.");}finally{setConversationLoading(false);}}
  async function finalizeConversation(question){setConversationLoading(true);setNotice("");try{const result=await finalizeAdminLearningQuestionConversation(question.id);setQuestionConversation(result.data.conversation);markReviewResolved(question.id);await onRefresh();setNotice("Lake’s confirmed conversation was saved as structured expert knowledge and related profiles were recalculated. Select Next to continue.");}catch(err){setNotice(err.response?.data?.error||"The confirmed answer could not be finalized.");}finally{setConversationLoading(false);}}
  const pending=(workspace.profiles||[]).filter((profile)=>profile.status!=="approved");
  const approved=(workspace.profiles||[]).filter((profile)=>profile.status==="approved");
  return <section style={s.section}>
    <div style={{...s.row,justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}><div><div style={s.sectionTitle}>Learning Lab</div><div style={s.hint}>Lake's expert rules define the scoring logic. Completed PDFs validate and refine that knowledge. Report measurements are never changed.</div></div><button type="button" style={s.secondary} onClick={refreshProfiles} disabled={busy||loading}>{busy?"Working...":"Refresh Learnings"}</button></div>
    {notice&&<div role="status" style={{...s.panel,marginBottom:12,borderColor:"#86b892"}}>{notice}</div>}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:14}}>
      <LearningStat
        label="Expert Rules"
        value={totals.rules||0}
        help="Approved, structured instructions from Lake and authoritative source documents. They guide profile recommendations, but this count does not mean every rule has changed a live report threshold."
        actionLabel="View and adjust rules"
        onClick={()=>setShowExpertRules(true)}
      />
      <LearningStat
        label="Indexed Claims"
        value={totals.claims||0}
        help="Distinct technical statements extracted from uploaded whitepapers, workbooks, and reference documents. Claims remain cited knowledge; they cannot change scoring until any actionable rule is explicitly confirmed and published."
      />
      <LearningStat
        label="Profiles to Review"
        value={totals.pendingProfiles||0}
        help="Context-specific threshold profiles with new or recalculated recommendations waiting for Lake. These profiles are not live until their eligible changes pass historical validation and are approved and published."
        actionLabel="Open profile review"
        onClick={()=>openReviewQueue("profiles",pending)}
      />
      <LearningStat
        label="Outstanding Questions"
        value={totals.questions||0}
        help="Consolidated conflicts, ambiguities, or missing expert decisions that the system cannot safely resolve on its own. Answering a question recalculates affected profiles; it does not by itself publish a live threshold."
        actionLabel="Open questions"
        onClick={()=>openReviewQueue("questions",workspace.questions||[])}
      />
      <LearningStat
        label="Approved Profiles"
        value={totals.approvedProfiles||0}
        help="Learned profiles Lake has accepted after validation. Profiles with scoring changes are published into the conditional threshold configuration; no-change approvals are recorded without creating a redundant threshold version."
      />
      <LearningStat
        label="Historical Decisions Reproduced"
        value={`${totals.reproduced||0}/${totals.validated||0}`}
        help={<><strong>This counts metric-color decisions, not reports.</strong><br/><br/>Each uploaded expert PDF supplies an answer key: its measured value and the green, orange, or red color Lake finalized for that metric. The Learning Lab independently evaluates that same value using the proposed threshold, then checks whether its calculated color matches Lake’s PDF color.<br/><br/><strong>Example:</strong> If a PDF shows Potassium 12 as Orange and the proposed rule also evaluates 12 as Orange, that historical decision is reproduced. If the proposed rule produces Green or Red, it is not reproduced.<br/><br/>The Snapshot confirms sample identity, measurements, engine template, fuel, viscosity, brand, mileage, and other context. It does not supply the authoritative color; Lake’s released PDF does.<br/><br/><strong>{totals.reproduced||0} of {totals.validated||0}</strong> means that many individual metric colors were recreated across the uploaded PDF reports. It does not mean that many reports were reproduced, and it does not approve or publish any threshold.</>}
      />
      <LearningStat
        label="Drafts Awaiting Publish"
        value={totals.drafts||0}
        help="Saved Learning Lab-generated threshold configurations that have not become the active published version. A draft has no effect on report scoring until it is validated and explicitly published."
      />
    </div>
    <form style={s.panel} onSubmit={uploadKnowledge}><div style={s.subTitle}>1. Add Knowledge</div><div style={s.hint}>Upload completed-report ZIPs, expert-rule workbooks, or expert reference PDFs.</div><div style={{...s.hint,marginTop:6}}><strong>Last batch uploaded:</strong> {latestUpload?<>{new Date(latestUpload.uploadedAt).toLocaleString()}{latestUpload.fileName?` · ${latestUpload.fileName}`:""}</>:"No uploads recorded yet."}</div>{activeUpload&&<div style={{marginTop:12,padding:"10px 12px",border:"1px solid #60a5fa",borderRadius:8,background:"#eff6ff",color:"#1e3a5f"}}><div style={{display:"flex",justifyContent:"space-between",gap:12,marginBottom:7,fontSize:13,fontWeight:800}}><span>{activeProgressLabel}</span><span>{activePercent}%</span></div><div role="progressbar" aria-label={`Processing ${activeUpload.fileName}`} aria-valuemin="0" aria-valuemax="100" aria-valuenow={activePercent} style={{height:10,borderRadius:999,overflow:"hidden",background:"#dbeafe"}}><div style={{height:"100%",width:`${activePercent}%`,borderRadius:999,background:"#2563eb",transition:"width .35s ease"}}/></div><div style={{fontSize:12,marginTop:6}}>{activeUpload.fileName}{activeProcessed>0?` · ${activeProcessed} processed`:""}</div></div>}<div style={{...s.row,marginTop:10,alignItems:"center"}}><input type="file" accept=".zip,.pdf,.xlsx,application/zip,application/pdf" style={{...s.input,flex:1}} onChange={(event)=>setKnowledgeFile(event.target.files?.[0]||null)}/><button type="submit" style={s.btn} disabled={busy||!knowledgeFile}>Add Knowledge</button></div>{knowledgeFile&&<div style={{...s.hint,marginTop:6}}>{knowledgeFile.name} · {formatBytes(knowledgeFile.size)}</div>}</form>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:12,marginTop:12}}>
      <ReviewQueueBadge title="2. What the Lab Learned" count={pending.length} description="Scoring profiles waiting for Lake’s review." action="Review Learned Profiles" tone="blue" onClick={()=>openReviewQueue("profiles",pending)}/>
      <ReviewQueueBadge title="3. Outstanding Questions" count={(workspace.questions||[]).length} description="Specific conflicts or missing guidance Lake needs to resolve." action="Review Questions" tone="amber" onClick={()=>openReviewQueue("questions",workspace.questions||[])}/>
    </div>
    <div style={{...s.panel,marginTop:12}}><div style={s.subTitle}>4. Approved Knowledge</div><div style={s.hint}>Approved profile changes are validated and published immediately. Previous versions remain available in History.</div>{approved.map((profile)=><div key={profile.id} style={{...s.row,justifyContent:"space-between",borderTop:"1px solid var(--border)",paddingTop:10,marginTop:10}}><div><strong>{profile.title}</strong><div style={s.hint}>{profile.evidence_summary?.reportCount||0} reports · {profile.evidence_summary?.reproduced||0}/{profile.evidence_summary?.validated||0} decisions reproduced</div></div><span style={{...s.pill,color:"#15803d",background:"#dcfce7"}}>Published</span></div>)}{approved.length===0&&<div style={{...s.hint,marginTop:10}}>No profiles approved yet.</div>}</div>
    {reviewQueue&&<LearningReviewModal queue={reviewQueue} resolved={resolvedReviewIds.has(reviewQueue.items[reviewQueue.index]?.id)} busy={busy} feedback={reviewFeedback} aiThinking={aiThinking} answer={questionAnswers[reviewQueue.items[reviewQueue.index]?.id]||{}} editingId={editingId} draft={profileDraft} correction={correction} interpretation={directionInterpretation} questionInterpretation={questionInterpretation} conversation={questionConversation} conversationInput={conversationInput} conversationCorrection={conversationCorrection} conversationLoading={conversationLoading} selectedProfileMetrics={selectedProfileMetrics} dirtyProfileMetrics={dirtyProfileMetrics} onToggleProfileMetric={toggleProfileMetric} onApproveSelectedProfileMetrics={approveSelectedProfileMetrics} onConversationInput={setConversationInput} onConversationCorrection={setConversationCorrection} onSendConversation={sendConversationMessage} onConfirmConversation={confirmConversationProposition} onConversationAction={runConversationAction} onFinalizeConversation={finalizeConversation} onClose={()=>setReviewQueue(null)} onSkip={skipReviewItem} onNext={advanceReviewQueue} onBeginEdit={beginEdit} onCancelEdit={()=>{setEditingId("");setProfileDraft(null);setCorrection("");setDirectionInterpretation(null);setReviewFeedback(null);setSelectedProfileMetrics(new Set());setDirtyProfileMetrics(new Set());}} onTemplateChange={(value)=>{setDirectionInterpretation(null);setSelectedProfileMetrics(new Set());setProfileDraft((current)=>({...current,templateName:value}));}} onBoundaryChange={updateBoundary} onCorrectionChange={(value)=>{setCorrection(value);setDirectionInterpretation(null);}} onSaveProfile={saveProfile} onResolveConflicts={resolveConflictsAndPublish} onApproveProfile={approveProfile} onNeedsCorrection={needsCorrection} onInterpretDirection={interpretDirection} onConfirmDirection={confirmDirection} onAnswerChange={(questionId,next)=>{setQuestionInterpretation(null);setQuestionAnswers((current)=>({...current,[questionId]:next}));}} onAnswerQuestion={answerQuestion} onInterpretQuestion={interpretQuestion} onConfirmQuestion={confirmQuestion}/>
    }
    {showExpertRules&&<ExpertRulesModal rules={workspace.rules||[]} busy={busy} onSave={saveExpertRule} onClose={()=>setShowExpertRules(false)}/>}
  </section>;
}

function ReviewQueueBadge({title,count,description,action,tone,onClick}){
  const palette=tone==="amber"?{background:"#fffbeb",border:"#f59e0b",count:"#b45309"}:{background:"#eff6ff",border:"#60a5fa",count:"#1d4ed8"};
  return <button type="button" onClick={onClick} disabled={!count} style={{...s.panel,textAlign:"left",cursor:count?"pointer":"default",opacity:count?1:.65,border:`1px solid ${palette.border}`,background:palette.background,color:"var(--text)",width:"100%"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}><div style={{fontWeight:900,fontSize:17}}>{title}</div><span style={{fontSize:26,fontWeight:950,color:palette.count}}>{count}</span></div><div style={{marginTop:6,lineHeight:1.45}}>{description}</div><div style={{marginTop:12,fontWeight:850,color:palette.count}}>{count?`${action} →`:"Nothing waiting"}</div></button>;
}

function ExpertRulesModal({rules,busy,onSave,onClose}) {
  const [editingId,setEditingId]=useState("");
  const [draft,setDraft]=useState(null);
  const [error,setError]=useState("");
  function begin(rule){setEditingId(rule.id);setError("");setDraft({label:rule.label||"",priority:rule.priority??0,threshold:{...(rule.threshold||{})},explanation:""});}
  function updateThreshold(field,value){setDraft((current)=>({...current,threshold:{...current.threshold,[field]:value}}));}
  async function save(rule){const threshold={...draft.threshold};for(const field of ["pass","warn","fail","low","high"]){if(threshold[field]!==undefined&&threshold[field]!=="")threshold[field]=Number(threshold[field]);}const saved=await onSave(rule,{label:draft.label,priority:Number(draft.priority),conditions:rule.conditions||{},threshold,explanation:draft.explanation});if(saved){setEditingId("");setDraft(null);setError("");}}
  return <div style={s.modalBackdrop} onMouseDown={onClose}><section style={{...s.modal,width:"min(1180px,96vw)",height:"88vh",overflow:"hidden",display:"flex",flexDirection:"column",padding:0}} role="dialog" aria-modal="true" aria-label="Expert Rules" onMouseDown={(event)=>event.stopPropagation()}>
    <div style={{...s.row,justifyContent:"space-between",padding:"16px 18px",borderBottom:"1px solid var(--border)",flex:"0 0 auto"}}><div><div style={{...s.sectionTitle,marginBottom:3}}>Expert Rules</div><div style={s.hint}>These approved rules supply threshold values and matching conditions. Saving an adjustment creates a new version and recalculates affected profiles.</div></div><button type="button" style={s.secondary} onClick={onClose}>Close</button></div>
    <div style={{overflowY:"auto",padding:16,flex:"1 1 auto"}}>{error&&<div style={{...s.panel,borderColor:"#dc2626",color:"#b91c1c",marginBottom:10}}>{error}</div>}{rules.map((rule)=>{const editing=editingId===rule.id;const fields=["pass","warn","fail","low","high"].filter((field)=>editing?(draft?.threshold?.[field]!==undefined):rule.threshold?.[field]!==undefined);return <div key={rule.id} style={{...s.panel,marginBottom:10,borderColor:editing?"var(--teal)":"var(--border)"}}>
      <div style={{...s.row,justifyContent:"space-between",alignItems:"flex-start"}}><div><div style={{fontWeight:900,fontSize:16}}>{rule.metric_key} · {editing?<input style={{...s.input,minWidth:280}} value={draft.label} onChange={(event)=>setDraft((current)=>({...current,label:event.target.value}))}/>:rule.label}</div><div style={{...s.hint,marginTop:4}}>{rule.file_name||"Lake adjustment"}{rule.source_location?` · ${rule.source_location}`:""} · Version {rule.version||1}</div></div>{!editing&&<button type="button" style={s.secondary} onClick={()=>begin(rule)}>Adjust Parameters</button>}</div>
      {editing?<div style={{marginTop:12}}><div style={{...s.row,alignItems:"flex-end"}}>{fields.map((field)=><label key={field} style={s.label}>{field}<input type="number" step="any" style={{...s.input,display:"block",width:95,marginTop:4}} value={draft.threshold?.[field]??""} onChange={(event)=>updateThreshold(field,event.target.value)}/></label>)}<label style={s.label}>Priority<input type="number" style={{...s.input,display:"block",width:95,marginTop:4}} value={draft.priority} onChange={(event)=>setDraft((current)=>({...current,priority:event.target.value}))}/></label></div><label style={{...s.label,display:"block",marginTop:10}}>Reason for adjustment<textarea style={{...s.input,width:"100%",boxSizing:"border-box",minHeight:62,display:"block",marginTop:5}} placeholder="Explain what Lake is correcting" value={draft.explanation} onChange={(event)=>setDraft((current)=>({...current,explanation:event.target.value}))}/></label><div style={{...s.row,marginTop:10}}><button type="button" style={s.btn} disabled={busy} onClick={()=>save(rule)}>{busy?"Saving and recalculating...":"Save Rule and Recalculate"}</button><button type="button" style={s.secondary} disabled={busy} onClick={()=>{setEditingId("");setDraft(null);setError("");}}>Cancel</button></div></div>:<div style={{display:"grid",gridTemplateColumns:"minmax(220px,1fr) minmax(260px,2fr)",gap:12,marginTop:10}}><div><div style={s.label}>Threshold parameters</div><div style={{...s.row,marginTop:6}}>{fields.map((field)=><span key={field} style={s.pill}>{field}: {rule.threshold?.[field]}</span>)}{rule.threshold?.type&&<span style={s.pill}>type: {rule.threshold.type}</span>}</div></div><div><div style={s.label}>Applies when</div><div style={{...s.hint,marginTop:6,whiteSpace:"pre-wrap"}}>{JSON.stringify(rule.conditions||{})}</div></div></div>}
    </div>})}{!rules.length&&<div style={s.hint}>No approved expert rules are available.</div>}</div>
  </section></div>;
}

function LearningReviewModal({queue,resolved,busy,feedback,aiThinking,answer,editingId,draft,correction,interpretation,questionInterpretation,conversation,conversationInput,conversationCorrection,conversationLoading,selectedProfileMetrics,dirtyProfileMetrics,onToggleProfileMetric,onApproveSelectedProfileMetrics,onConversationInput,onConversationCorrection,onSendConversation,onConfirmConversation,onConversationAction,onFinalizeConversation,onClose,onSkip,onNext,onBeginEdit,onCancelEdit,onTemplateChange,onBoundaryChange,onCorrectionChange,onSaveProfile,onResolveConflicts,onApproveProfile,onNeedsCorrection,onInterpretDirection,onConfirmDirection,onAnswerChange,onAnswerQuestion,onInterpretQuestion,onConfirmQuestion}){
  const item=queue.items[queue.index];if(!item)return null;const isProfile=queue.type==="profiles";const isLast=queue.index===queue.items.length-1;const current=queue.index+1;const total=queue.items.length;const remaining=Math.max(0,total-current);const progress=Math.round((current/total)*100);
  const publishing=feedback?.type==="working";
  const conflictDetails=feedback?.type==="conflict"?feedback.conflictDetails:null;
  if(publishing){feedback=null;onClose=()=>{};}
  return <div style={s.modalBackdrop} onMouseDown={onClose}><section style={{...s.modal,width:"min(1180px,96vw)",height:"88vh",overflow:"hidden",display:"flex",flexDirection:"column"}} onMouseDown={(event)=>event.stopPropagation()}><div style={{paddingBottom:12,borderBottom:"1px solid var(--border)",flex:"0 0 auto"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}><div><div style={{...s.subTitle,marginBottom:5}}>{isProfile?"Learned Profile Review":"Outstanding Question Review"}</div><div style={{display:"flex",alignItems:"center",gap:9,flexWrap:"wrap"}}><strong style={{fontSize:22,color:"var(--text-primary)"}}>{current} of {total}</strong><span style={{...s.pill,color:resolved?"#166534":"#92400e",background:resolved?"#dcfce7":"#fef3c7"}}>{resolved?"Resolved":"Awaiting Lake"}</span><span style={{fontSize:13,fontWeight:750,color:"var(--text-muted)"}}>{remaining===0?"Final item":`${remaining} remaining`}</span></div></div><button type="button" style={s.secondary} onClick={onClose}>Close</button></div><div role="progressbar" aria-label={`${isProfile?"Learned profile":"Outstanding question"} review progress`} aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress} style={{height:9,marginTop:11,borderRadius:999,overflow:"hidden",background:"var(--border-subtle)",border:"1px solid var(--border)"}}><div style={{height:"100%",width:`${progress}%`,borderRadius:999,background:"linear-gradient(90deg,var(--teal),#16a34a)",transition:"width 220ms ease"}}/></div></div>
    {aiThinking?.id===item.id&&<AiInterpretationProgress type={aiThinking.type}/>
    }
    {publishing&&<PublicationProgress/>}
    {feedback&&<div role="status" aria-live="assertive" style={{flex:"0 0 auto",margin:"10px 0",padding:"12px 14px",borderRadius:8,border:`2px solid ${feedback.type==="success"?"#16a34a":feedback.type==="conflict"?"#d97706":"#dc2626"}`,background:feedback.type==="success"?"#dcfce7":feedback.type==="conflict"?"#fef3c7":"#fee2e2",color:feedback.type==="success"?"#14532d":feedback.type==="conflict"?"#78350f":"#7f1d1d"}}><div style={{fontSize:16,fontWeight:950}}>{feedback.type==="success"?"✓ ":feedback.type==="conflict"?"Review required: ":"Approval blocked: "}{feedback.title}</div><div style={{marginTop:4,lineHeight:1.45,fontWeight:700}}>{feedback.detail}</div>{feedback.completedAt&&<div style={{fontSize:12,marginTop:5}}>Implemented {new Date(feedback.completedAt).toLocaleString()}</div>}</div>}
    <div style={{flex:"1 1 auto",minHeight:0,overflowY:"auto",overscrollBehavior:"contain",padding:"0 4px 16px 0"}}>{isProfile?<>{conflictDetails?<HistoricalConflictReview details={conflictDetails} draft={draft} onBoundaryChange={onBoundaryChange}/>:<LearningProfileCard profile={item} forceOpen hideActions editing={editingId===item.id} draft={editingId===item.id?draft:null} correction={editingId===item.id?correction:""} busy={busy} selectedMetrics={selectedProfileMetrics} dirtyMetrics={dirtyProfileMetrics} onToggleSelection={onToggleProfileMetric} onBeginEdit={()=>onBeginEdit(item)} onCancel={onCancelEdit} onTemplateChange={onTemplateChange} onBoundaryChange={onBoundaryChange} onCorrectionChange={onCorrectionChange} onSave={()=>onSaveProfile(item)} onApprove={()=>onApproveProfile(item)} onNeedsCorrection={()=>onNeedsCorrection(item)}/>} {interpretation?.profileId===item.id&&<DirectionInterpretationPreview proposal={interpretation}/>}</>:<QuestionConversationReview question={item} conversation={conversation} input={conversationInput} correction={conversationCorrection} loading={conversationLoading} onInput={onConversationInput} onCorrection={onConversationCorrection} onSend={()=>onSendConversation(item)} onConfirm={(confirmed)=>onConfirmConversation(item,confirmed)} onAction={(action)=>onConversationAction(item,action)} onFinalize={()=>onFinalizeConversation(item)}/>}</div>
    <div style={{paddingTop:12,borderTop:"1px solid var(--border)",flex:"0 0 auto",background:"var(--bg-card)"}}>{isProfile&&<div style={{...s.row,paddingBottom:12}}>{conflictDetails?<><button type="button" style={s.btn} onClick={()=>onResolveConflicts(item,conflictDetails.conflicts)} disabled={busy}>{busy?"Validating corrections…":"Save Corrections & Retry Publish"}</button><button type="button" style={s.secondary} onClick={()=>{onCancelEdit();onBeginEdit(item);}} disabled={busy}>Open Full Profile Editor</button><button type="button" style={s.secondary} onClick={onCancelEdit} disabled={busy}>Cancel Review</button></>:editingId===item.id?<><button type="button" style={s.btn} onClick={()=>onApproveSelectedProfileMetrics(item)} disabled={busy||!selectedProfileMetrics?.size||dirtyProfileMetrics?.size>0}>{busy?"Validating selected rows…":`Approve Selected & Publish (${selectedProfileMetrics?.size||0})`}</button><button type="button" style={s.secondary} onClick={()=>onSaveProfile(item)} disabled={busy}>Save Edited Values and Recalculate</button><button type="button" style={s.secondary} onClick={()=>onInterpretDirection(item)} disabled={busy||!cleanUi(correction)}>Interpret Lake’s Direction</button>{interpretation?.profileId===item.id&&<button type="button" style={s.btn} onClick={()=>onConfirmDirection(item)} disabled={busy||(interpretation.interpretation?.clarification_questions||[]).length>0}>Confirm Interpretation and Recalculate</button>}<button type="button" style={s.secondary} onClick={()=>onNeedsCorrection(item)} disabled={busy}>Needs Correction</button><button type="button" style={s.secondary} onClick={onCancelEdit}>Cancel</button></>:<><button type="button" style={s.btn} onClick={()=>onApproveProfile(item)} disabled={busy||resolved}>{busy?"Validating & Publishing…":resolved?"Approved & Implemented":"Correct / Approve & Publish"}</button><button type="button" style={s.secondary} onClick={()=>onBeginEdit(item)} disabled={busy||resolved}>Edit Thresholds or Assigned Template</button></>}</div>}<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,borderTop:isProfile?"1px solid var(--border)":"none",paddingTop:12}}><button type="button" style={s.secondary} onClick={onSkip} disabled={busy||conversationLoading}>{isLast?"Skip and Close":"Skip"}</button><div style={{...s.hint,fontWeight:resolved?850:undefined,color:resolved?"#15803d":undefined}}>{resolved?"Accepted and implemented. You can continue.":conflictDetails?"Only the affected metrics are shown. Production remains unchanged until validation passes.":isProfile?"Approve eligible rows now, or Skip this profile and return later.":"Confirmed facts are saved after every Yes — lock this in action."}</div><button type="button" style={s.btn} onClick={onNext} disabled={busy||!resolved}>{isLast?"Finish Review":"Next"}</button></div></div>
  </section></div>;
}

function HistoricalConflictReview({details,draft,onBoundaryChange}){
  const conflicts=details?.conflicts||[];const metricKeys=[...new Set(conflicts.map((item)=>item.metricKey))];const recommendations=draft?.recommendations||[];
  return <div style={{paddingTop:14}}><div style={{...s.panel,border:"2px solid #dc2626",background:"#fef2f2"}}><div style={{...s.subTitle,color:"#991b1b"}}>Fix {conflicts.length} failed decision{conflicts.length===1?"":"s"} across {metricKeys.length} metric{metricKeys.length===1?"":"s"}</div><div style={{marginTop:5,color:"#7f1d1d",lineHeight:1.45}}>Expected is the color Lake finalized on the historical report. Calculated is what the proposed threshold produces. Adjust the boundaries for each affected metric, then retry publication.</div></div>{metricKeys.map((metricKey)=>{const recommendation=recommendations.find((item)=>item.metricKey===metricKey);const rows=conflicts.filter((item)=>item.metricKey===metricKey);return <div key={metricKey} style={{...s.panel,marginTop:12,border:"2px solid #dc2626",background:"#fffafa"}}><div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}><div><div style={{fontSize:18,fontWeight:950}}>{rows[0]?.metricLabel||metricKey}</div><div style={{...s.hint,marginTop:3,color:"#b91c1c"}}>{rows.length} publication-blocking historical decision{rows.length===1?"":"s"}</div></div><div style={{display:"flex",gap:7,flexWrap:"wrap"}}>{["pass","warn","fail"].map((field)=><label key={field} style={{fontSize:11,fontWeight:850,textTransform:"uppercase"}}>{field}<input type="number" step="any" style={{...s.input,width:86,display:"block",marginTop:3}} value={recommendation?.proposed?.[field]??0} onChange={(event)=>onBoundaryChange(metricKey,field,event.target.value)}/></label>)}</div></div><div style={{overflowX:"auto",marginTop:12,padding:2}}><table style={s.table}><thead><tr><th style={s.th}>Report</th><th style={s.th}>Value</th><th style={s.th}>Expected</th><th style={s.th}>Calculated</th><th style={s.th}>Context / question</th></tr></thead><tbody>{rows.map((row)=><tr key={row.validationId} style={{background:"#fef2f2",outline:"2px solid #dc2626",outlineOffset:"-2px"}}><td style={s.td}><strong>{row.labSampleId||"Historical report"}</strong>{row.sampleDate&&<div style={s.hint}>{new Date(row.sampleDate).toLocaleDateString()}</div>}</td><td style={s.td}>{row.value??"—"}</td><td style={s.td}><StatusColorLabel status={row.expectedStatus}/></td><td style={s.td}><StatusColorLabel status={row.evaluatedStatus}/></td><td style={{...s.td,minWidth:220}}>{[row.context?.templateName,row.context?.oilBrand,row.context?.viscosityGrade].filter(Boolean).join(" · ")||"Profile context"}{row.questionTitle&&<div style={{...s.hint,marginTop:3,color:"#b45309"}}>Outstanding Question: {row.questionTitle}</div>}</td></tr>)}</tbody></table></div></div>})}</div>;
}

function StatusColorLabel({status}){const map={normal:{label:"Green / Normal",color:"#166534",background:"#dcfce7"},warn:{label:"Orange / Watch",color:"#92400e",background:"#fef3c7"},critical:{label:"Red / Critical",color:"#991b1b",background:"#fee2e2"},na:{label:"N/A",color:"#475569",background:"#f1f5f9"}};const value=map[status]||{label:status||"Unknown",color:"#475569",background:"#f1f5f9"};return <span style={{...s.pill,color:value.color,background:value.background}}>{value.label}</span>;}

function PublicationProgress(){
  const stages=["Validating historical decisions…","Preparing threshold configuration…","Publishing changes…","Confirming implementation…"];
  const [stage,setStage]=useState(0);
  useEffect(()=>{const timer=window.setInterval(()=>setStage((current)=>Math.min(current+1,stages.length-1)),1400);return()=>window.clearInterval(timer);},[]);
  return <div role="status" aria-live="polite" style={{flex:"0 0 auto",margin:"10px 0",padding:"12px 14px",border:"2px solid #3b82f6",borderRadius:8,background:"#eff6ff",color:"#1e3a8a"}}><div style={{display:"flex",alignItems:"center",gap:10}}><span aria-hidden="true" style={{width:20,height:20,border:"3px solid #bfdbfe",borderTopColor:"#2563eb",borderRadius:"50%",animation:"spin .8s linear infinite",flex:"0 0 auto"}}/><div><strong style={{fontSize:16}}>Validating &amp; Publishing</strong><div style={{fontSize:13,marginTop:3,fontWeight:750}}>{stages[stage]}</div></div></div><div style={{...s.loadingTrack,height:8,marginTop:10,background:"#dbeafe",borderColor:"#bfdbfe"}}><div style={{...s.loadingFill,background:"linear-gradient(90deg,#93c5fd,#2563eb,#93c5fd)"}} className="sdx-dashboard-loading-bar"/></div><div style={{fontSize:12,marginTop:7,color:"#475569"}}>Please keep this review open. Nothing advances until publication is confirmed.</div></div>;
}

function AiInterpretationProgress({type}){
  const message=type==="question"?"Analyzing Lake’s expert explanation…":"Interpreting Lake’s direction…";return <div role="status" aria-live="polite" style={{flex:"0 0 auto",margin:"10px 0 0",padding:"11px 13px",border:"1px solid #60a5fa",borderRadius:8,background:"#eff6ff",color:"#1e3a8a"}}><div style={{display:"flex",alignItems:"center",gap:10}}><span aria-hidden="true" style={{width:20,height:20,border:"3px solid #bfdbfe",borderTopColor:"#2563eb",borderRadius:"50%",animation:"spin .8s linear infinite",flex:"0 0 auto"}}/><div><strong>{message}</strong><div style={{fontSize:12,marginTop:2,color:"#475569"}}>Reviewing the selected answer, context, thresholds, and historical evidence. Nothing is being applied yet.</div></div></div><div style={{...s.loadingTrack,height:7,marginTop:9,background:"#dbeafe",borderColor:"#bfdbfe"}}><div style={{...s.loadingFill,background:"linear-gradient(90deg,#93c5fd,#2563eb,#93c5fd)"}} className="sdx-dashboard-loading-bar"/></div></div>;
}

function DirectionInterpretationPreview({proposal}){
  const value=proposal.interpretation||{};return <div style={{marginTop:14,padding:14,border:"1px solid #60a5fa",borderRadius:8,background:"#eff6ff",color:"#172554"}}><div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center"}}><strong style={{fontSize:16}}>AI interpretation for Lake to confirm</strong><span style={{...s.pill,color:value.confidence==="low"?"#991b1b":"#1d4ed8",background:value.confidence==="low"?"#fee2e2":"#dbeafe"}}>{value.confidence||"unknown"} confidence</span></div>{value.confidence_reason&&<div style={{marginTop:8,fontWeight:800,color:value.confidence==="low"?"#991b1b":"#172554"}}>{value.confidence_reason}</div>}<div style={{marginTop:8,fontWeight:750}}>{value.summary}</div><div style={{marginTop:5}}><strong>Applies to:</strong> {value.scope_explanation}</div>{value.proposed_template&&<div style={{marginTop:5}}><strong>Assigned template:</strong> {value.proposed_template}</div>}{(value.metric_changes||[]).length>0&&<div style={{overflowX:"auto",marginTop:10}}><table style={s.table}><thead><tr><th style={s.th}>Metric</th><th style={s.th}>Pass</th><th style={s.th}>Warn</th><th style={s.th}>Fail</th><th style={s.th}>AI reasoning</th></tr></thead><tbody>{value.metric_changes.map((change)=><tr key={change.metric_key}><td style={s.td}><strong>{change.metric_key}</strong></td><td style={s.td}>{change.pass??"Unchanged"}</td><td style={s.td}>{change.warn??"Unchanged"}</td><td style={s.td}>{change.fail??"Unchanged"}</td><td style={s.td}>{change.reason}</td></tr>)}</tbody></table></div>}{(value.assumptions||[]).length>0&&<div style={{marginTop:9}}><strong>Assumptions:</strong><ul style={{margin:"5px 0 0 20px"}}>{value.assumptions.map((item)=><li key={item}>{item}</li>)}</ul></div>}{(value.clarification_questions||[]).length>0&&<div style={{marginTop:10,padding:10,borderLeft:"4px solid #d97706",background:"#fffbeb",color:"#78350f"}}><strong>Clarification required before confirmation:</strong><ul style={{margin:"5px 0 0 20px"}}>{value.clarification_questions.map((item)=><li key={item}>{item}</li>)}</ul><div style={{marginTop:6}}>Revise Lake’s direction above, then interpret it again.</div></div>}<div style={{marginTop:10,fontSize:12,color:"#475569"}}>This is a proposal only. Nothing changes until Lake clicks Confirm Interpretation and Recalculate.</div></div>;
}

function QuestionInterpretationPreview({proposal}){
  const value=proposal.interpretation||{};return <div style={{marginTop:14,padding:14,border:"1px solid #60a5fa",borderRadius:8,background:"#eff6ff",color:"#172554"}}><div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center"}}><strong style={{fontSize:16}}>AI interpretation for Lake to confirm</strong><span style={{...s.pill,color:"#1d4ed8",background:"#dbeafe"}}>{value.confidence||"unknown"} confidence</span></div><div style={{marginTop:8,fontWeight:750}}>{value.summary}</div><div style={{marginTop:5}}><strong>Applies to:</strong> {value.scope_explanation}</div>{value.metric_key&&<div style={{marginTop:5}}><strong>Metric:</strong> {value.metric_key} · Pass {value.pass??"unchanged"} · Warn {value.warn??"unchanged"} · Fail {value.fail??"unchanged"}</div>}{value.proposed_template&&<div style={{marginTop:5}}><strong>Assigned template:</strong> {value.proposed_template}</div>}{Object.values(value.conditions||{}).some(Boolean)&&<div style={{marginTop:5}}><strong>Conditions:</strong> {Object.entries(value.conditions).filter(([,item])=>item).map(([key,item])=>`${key}: ${item}`).join(" · ")}</div>}{(value.assumptions||[]).length>0&&<div style={{marginTop:9}}><strong>Assumptions:</strong><ul style={{margin:"5px 0 0 20px"}}>{value.assumptions.map((item)=><li key={item}>{item}</li>)}</ul></div>}{(value.clarification_questions||[]).length>0&&<div style={{marginTop:10,padding:10,borderLeft:"4px solid #d97706",background:"#fffbeb",color:"#78350f"}}><strong>Clarification required before confirmation:</strong><ul style={{margin:"5px 0 0 20px"}}>{value.clarification_questions.map((item)=><li key={item}>{item}</li>)}</ul><div style={{marginTop:6}}>Revise the Expert Explanation, then interpret it again.</div></div>}<div style={{marginTop:10,fontSize:12,color:"#475569"}}>This is a proposal only. Nothing changes until Lake clicks Confirm Interpretation and Recalculate.</div></div>;
}

function QuestionConversationReview({question,conversation,input,correction,loading,onInput,onCorrection,onSend,onConfirm,onAction,onFinalize}){
  if(!conversation)return <div style={{...s.loadingBox,minHeight:300}}><div className="sdx-dashboard-spinner"/><div>Opening the expert conversation…</div></div>;
  const proposition=conversation.current_proposition;const confirmed=conversation.confirmed_facts||[];const rules=conversation.draft_rules||[];const status=conversation.status||"active";const legacy=(conversation.turns||[]).find((turn)=>turn.turn_type==="legacy_summary");const visible=(conversation.turns||[]).filter((turn)=>!turn.turn_type.startsWith("legacy"));const lastAssistant=[...(conversation.turns||[])].reverse().find((turn)=>turn.role==="assistant"&&turn.turn_type!=="legacy_summary");const ready=lastAssistant?.payload?.ready_to_finalize===true&&!proposition;
  return <div style={{paddingTop:14}}><div style={{...s.panel,borderColor:"#60a5fa",background:"#eff6ff"}}><div style={{fontSize:18,fontWeight:950,color:"#1e3a8a"}}>Conversation with the Learning Lab</div><div style={{marginTop:5,lineHeight:1.45,color:"#1e3a8a"}}>Answer one question at a time. Confirmed points are locked and will not be asked again. You never need to rewrite the complete answer.</div>{legacy&&<details style={{marginTop:9}}><summary style={{cursor:"pointer",fontWeight:800}}>Earlier form attempts preserved ({conversation.legacy_attempt_count||0})</summary><div style={{...s.hint,marginTop:5}}>These attempts remain available for audit but are not treated as confirmed knowledge.</div></details>}</div>
    {confirmed.length>0&&<div style={{...s.panel,marginTop:10,borderColor:"#86b892",background:"#f0fdf4"}}><div style={{fontWeight:950,color:"#166534"}}>Confirmed and locked</div>{confirmed.map((fact)=><div key={fact.key} style={{marginTop:7,color:"#14532d"}}>✓ {fact.statement}</div>)}</div>}
    <div style={{display:"flex",flexDirection:"column",gap:9,marginTop:12}}>{visible.map((turn)=><div key={turn.id} style={{alignSelf:turn.role==="user"?"flex-end":"flex-start",maxWidth:"86%",padding:"10px 13px",borderRadius:12,background:turn.role==="user"?"var(--teal)":"var(--bg-muted)",color:turn.role==="user"?"white":"var(--text)",border:"1px solid var(--border)",whiteSpace:"pre-wrap",lineHeight:1.45}}><div style={{fontSize:11,fontWeight:900,textTransform:"uppercase",opacity:.75,marginBottom:3}}>{turn.role==="user"?"Lake":"Learning Lab"}</div>{turn.content}</div>)}</div>
    {proposition&&<div style={{...s.panel,marginTop:12,border:"2px solid #3b82f6",background:"#eff6ff"}}><div style={{fontWeight:950,fontSize:17,color:"#1e3a8a"}}>Is this what you mean?</div><div style={{marginTop:7,fontSize:15,lineHeight:1.5,color:"#172554"}}>{proposition.statement}</div>{(proposition.rules||[]).length>0&&<div style={{overflowX:"auto",marginTop:10}}><table style={s.table}><thead><tr><th style={s.th}>Rule</th><th style={s.th}>Metric</th><th style={s.th}>Thresholds</th><th style={s.th}>Scope</th></tr></thead><tbody>{proposition.rules.map((rule)=><tr key={rule.rule_key}><td style={s.td}>{rule.label}</td><td style={s.td}>{rule.metric_key}</td><td style={s.td}>{[rule.pass!==null?`Pass ${rule.pass}`:null,rule.warn!==null?`Warn ${rule.warn}`:null,rule.fail!==null?`Fail ${rule.fail}`:null].filter(Boolean).join(" · ")||"No numeric change"}</td><td style={s.td}>{(rule.conditions||[]).map((item)=>`${item.field} ${item.operator} ${item.value}`).join("; ")||"All matching reports"}</td></tr>)}</tbody></table></div>}<div style={{...s.row,marginTop:12}}><button type="button" style={s.btn} disabled={loading} onClick={()=>onConfirm(true)}>Yes — lock this in</button></div><div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #bfdbfe"}}><label style={{...s.label,color:"#1e3a8a"}}>No — tell the AI only what to correct<textarea style={{...s.input,width:"100%",minHeight:70,boxSizing:"border-box",display:"block",marginTop:5}} value={correction} onChange={(event)=>onCorrection(event.target.value)} placeholder="Example: The 50 limit is red at 50, not only above 50."/></label><button type="button" style={{...s.secondary,marginTop:7}} disabled={loading||!cleanUi(correction)} onClick={()=>onConfirm(false)}>Correct This Interpretation</button></div></div>}
    {!proposition&&status==="active"&&!ready&&<div style={{...s.panel,marginTop:12}}><label style={{...s.label,display:"block"}}>Lake’s answer<textarea style={{...s.input,width:"100%",minHeight:90,boxSizing:"border-box",display:"block",marginTop:5}} value={input} onChange={(event)=>onInput(event.target.value)} onKeyDown={(event)=>{if((event.metaKey||event.ctrlKey)&&event.key==="Enter")onSend();}} placeholder="Answer only the question immediately above. Confirmed answers do not need to be repeated."/></label><div style={{...s.row,marginTop:9}}><button type="button" style={s.btn} disabled={loading||!cleanUi(input)} onClick={onSend}>{loading?"Thinking…":"Send Answer"}</button><button type="button" style={s.secondary} disabled={loading} onClick={()=>onAction("use_workbook")}>Use Workbook as Written</button></div></div>}
    {ready&&<div style={{...s.panel,marginTop:12,border:"2px solid #16a34a",background:"#f0fdf4"}}><div style={{fontWeight:950,fontSize:17,color:"#166534"}}>This answer is ready to finalize</div><div style={{marginTop:5,color:"#14532d"}}>Review the locked facts and {rules.length} drafted rule{rules.length===1?"":"s"}. Finalizing saves the structured knowledge and recalculates related profiles.</div><button type="button" style={{...s.btn,marginTop:10}} disabled={loading} onClick={onFinalize}>Confirm Answer & Recalculate</button></div>}
    {rules.length>0&&<details style={{...s.panel,marginTop:12}}><summary style={{cursor:"pointer",fontWeight:900}}>Draft structured rules ({rules.length})</summary><div style={{marginTop:8}}>{rules.map((rule)=><div key={rule.rule_key} style={{padding:"7px 0",borderTop:"1px solid var(--border)"}}><strong>{rule.label}</strong><div style={s.hint}>{rule.metric_key} · {[rule.warn!==null?`Warn ${rule.warn}`:null,rule.fail!==null?`Fail ${rule.fail}`:null].filter(Boolean).join(" · ")||"Context guidance"}</div></div>)}</div></details>}
    <div style={{...s.row,marginTop:12}}>{status==="paused"||status==="needs_developer"?<button type="button" style={s.secondary} disabled={loading} onClick={()=>onAction("resume")}>Resume Conversation</button>:<button type="button" style={s.secondary} disabled={loading||status==="completed"} onClick={()=>onAction("pause")}>Save & Continue Later</button>}<button type="button" style={s.secondary} disabled={loading||status==="completed"} onClick={()=>onAction("developer_review")}>Needs Developer Interpretation</button><button type="button" style={s.secondary} disabled={loading||status==="completed"} onClick={()=>{if(window.confirm("Exclude this question without creating a threshold rule? Confirmed conversation history will remain in the audit trail."))onAction("exclude");}}>Exclude This Question</button></div>{status==="needs_developer"&&<div style={{...s.panel,marginTop:10,borderColor:"#d97706",background:"#fffbeb",color:"#78350f"}}>This conversation is paused for developer interpretation. Confirmed expert facts remain saved.</div>}
  </div>;
}

function OutstandingQuestionForm({question,answer,busy,hideActions=false,onChange,onSave}){
  return <div style={{paddingTop:16}}><strong style={{fontSize:18}}>{question.title}</strong><div style={{marginTop:9,padding:"12px 14px",borderLeft:"4px solid #d97706",borderRadius:"0 8px 8px 0",background:"var(--bg-muted)",color:"var(--text)",fontSize:15,lineHeight:1.5,fontWeight:650}}><span style={{display:"block",fontSize:11,textTransform:"uppercase",letterSpacing:"0.08em",color:"#b45309",marginBottom:3,fontWeight:900}}>Lake’s review needed</span>{question.detail}</div><div style={{display:"grid",gridTemplateColumns:"minmax(220px,1fr) repeat(2,minmax(90px,.35fr))",gap:8,marginTop:12}}><select style={s.input} value={answer.choice||""} onChange={(e)=>onChange({...answer,choice:e.target.value})}><option value="">Choose Lake's answer…</option>{(question.choices||[]).map((choice)=><option key={choice} value={choice}>{choice}</option>)}</select><input style={s.input} type="number" placeholder="Warn (optional)" value={answer.warn??""} onChange={(e)=>onChange({...answer,warn:e.target.value})}/><input style={s.input} type="number" placeholder="Fail (optional)" value={answer.fail??""} onChange={(e)=>onChange({...answer,fail:e.target.value})}/></div><div style={{...s.row,marginTop:8}}><input style={{...s.input,flex:1}} placeholder="Expert explanation (optional)" value={answer.explanation||""} onChange={(e)=>onChange({...answer,explanation:e.target.value})}/>{!hideActions&&<button type="button" style={s.secondary} onClick={onSave} disabled={busy}>Save Answer</button>}</div></div>;
}

function cleanUi(value){return String(value||"").trim();}

function confirmLowConfidenceInterpretation(){
  return window.confirm("Low-confidence AI interpretation\n\nThe AI is uncertain that it correctly understood Lake’s direction. Confirming this interpretation will create binding expert knowledge and may negatively affect thresholds across related profiles.\n\nPlease review the proposed scope, conditions, template, and threshold values carefully.\n\nDo you still want to confirm and recalculate?");
}

function LearningProfileCard({profile,forceOpen=false,hideActions=false,editing,draft,correction,busy,selectedMetrics=new Set(),dirtyMetrics=new Set(),onToggleSelection,onBeginEdit,onCancel,onTemplateChange,onBoundaryChange,onCorrectionChange,onSave,onApprove,onNeedsCorrection}){
  const rows=draft?.recommendations||profile.recommendations||[];const changes=rows.filter((row)=>row.changed).length;const summary=profile.evidence_summary||{};
  const eligibleRows=rows.filter((row)=>learningProfileRowState(row).selectable&&!dirtyMetrics.has(row.metricKey));
  return <details open={forceOpen||editing} style={{borderTop:"1px solid var(--border)",paddingTop:12,marginTop:12}}><summary style={{cursor:"pointer"}}><span style={{display:"inline-flex",width:"calc(100% - 20px)",justifyContent:"space-between",gap:10}}><span><strong>{profile.title}</strong><span style={{...s.pill,marginLeft:8}}>{profile.confidence}</span></span><span style={s.hint}>{summary.reportCount||0} reports · {changes} proposed change{changes===1?"":"s"}</span></span></summary><div style={{...s.hint,marginTop:8}}>Based on Lake's rules and {summary.reportCount||0} completed report{summary.reportCount===1?"":"s"}. Historical decisions reproduced: {summary.reproduced||0}/{summary.validated||0}.</div>
    {editing&&<label style={{...s.label,display:"block",marginTop:10}}>Assigned template<input style={{...s.input,display:"block",marginTop:5,width:220}} value={draft?.templateName||""} onChange={(e)=>onTemplateChange(e.target.value)}/></label>}
    <div style={{...s.row,marginTop:10,gap:8}}><span style={{...s.pill,border:"2px solid #f59e0b",background:"#fffbeb",color:"#92400e"}}>Orange: needs Lake’s review</span><span style={{...s.pill,border:"2px solid #dc2626",background:"#fef2f2",color:"#991b1b"}}>Red: blocks full publication</span>{editing&&eligibleRows.length>0&&<><button type="button" style={{...s.secondary,padding:"5px 9px",fontSize:12}} onClick={()=>eligibleRows.forEach((row)=>{if(!selectedMetrics.has(row.metricKey))onToggleSelection(row.metricKey);})}>Select all eligible</button>{selectedMetrics.size>0&&<button type="button" style={{...s.secondary,padding:"5px 9px",fontSize:12}} onClick={()=>[...selectedMetrics].forEach(onToggleSelection)}>Clear selection</button>}</>}</div>
    {editing&&<div style={{...s.hint,marginTop:7}}>Choose the orange rows Lake wants to publish now. Unselected rows remain pending. Red rows must be corrected, saved, and recalculated before they can be selected.</div>}
    <div style={{overflowX:"auto",marginTop:10,padding:"2px"}}><table style={s.table}><thead><tr>{editing&&<th style={s.th}>Approve now</th>}<th style={s.th}>Metric</th><th style={s.th}>Current</th><th style={s.th}>Proposed</th><th style={s.th}>Knowledge Source</th><th style={s.th}>Evidence</th></tr></thead><tbody>{rows.map((row)=>{const dirty=dirtyMetrics.has(row.metricKey);const state=learningProfileRowState({...row,changed:row.changed||dirty});const rowStyle=state.blocking?{background:"#fef2f2",outline:"2px solid #dc2626",outlineOffset:"-2px"}:state.needsReview?{background:"#fffbeb",outline:"2px solid #f59e0b",outlineOffset:"-2px"}:undefined;return <tr key={row.metricKey} style={rowStyle}>{editing&&<td style={{...s.td,minWidth:105}}>{state.blocking?<span style={{fontSize:11,fontWeight:900,color:"#b91c1c"}}>Fix first</span>:state.selectable&&!dirty?<label style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,fontWeight:850,cursor:"pointer"}}><input type="checkbox" checked={selectedMetrics.has(row.metricKey)} onChange={()=>onToggleSelection(row.metricKey)}/>Approve</label>:dirty?<span style={{fontSize:11,fontWeight:850,color:"#92400e"}}>Save first</span>:<span style={s.hint}>No change</span>}</td>}<td style={s.td}><strong>{row.metricKey}</strong>{state.blocking?<div style={{fontSize:11,fontWeight:850,color:"#b91c1c",marginTop:3}}>Blocks publication</div>:state.needsReview?<div style={{fontSize:11,fontWeight:850,color:"#b45309",marginTop:3}}>Needs review</div>:null}</td><td style={s.td}>{compactThreshold(row.current)}</td><td style={s.td}>{editing?<div style={{display:"flex",gap:5}}>{["pass","warn","fail"].map((field)=><label key={field} style={{fontSize:11}}>{field}<input type="number" style={{...s.input,width:72,display:"block"}} value={row.proposed?.[field]??0} onChange={(e)=>onBoundaryChange(row.metricKey,field,e.target.value)}/></label>)}</div>:compactThreshold(row.proposed)}</td><td style={s.td}>{row.sourceLabel}<div style={s.hint}>{row.source}</div></td><td style={s.td}>{row.reproduced}/{row.evidenceCount} reproduced<div style={s.hint}>{row.confidence}</div></td></tr>;})}</tbody></table></div>
    {editing&&<textarea style={{...s.input,width:"100%",boxSizing:"border-box",minHeight:70,marginTop:10}} placeholder="Lake's explanation or direction" value={correction} onChange={(e)=>onCorrectionChange(e.target.value)}/>} {!hideActions&&(editing?<div style={{...s.row,marginTop:8}}><button type="button" style={s.btn} onClick={onSave} disabled={busy}>Save and Recalculate</button><button type="button" style={s.secondary} onClick={onNeedsCorrection} disabled={busy}>Needs Correction</button><button type="button" style={s.secondary} onClick={onCancel}>Cancel</button></div>:<div style={{...s.row,marginTop:10}}><button type="button" style={s.btn} onClick={onApprove} disabled={busy}>Correct / Approve</button><button type="button" style={s.secondary} onClick={onBeginEdit}>Edit Thresholds or Assigned Template</button></div>)}
  </details>;
}

function compactThreshold(threshold={}){return `Pass ${threshold.pass??"—"} · Warn ${threshold.warn??"—"} · Fail ${threshold.fail??"—"}`;}

function AuthoritativeThresholdLearningLabSection({
  learning, loading, guidanceTitle, guidanceCategory, guidanceText, uploadFile, uploadLimit, uploadAiEnabled,
  onGuidanceTitleChange, onGuidanceCategoryChange, onGuidanceTextChange, onUploadFileChange, onUploadLimitChange,
  onUploadAiEnabledChange, onSaveGuidance, onUploadArchive, onRebuildInference, onCreateDrafts, onRefresh, onOpenReview,
}) {
  const preview = learning?.learnedRulePreview || { run: null, rules: [], exceptions: [] };
  const rules = preview.rules || [];
  const displayRules = Object.values(rules.reduce((groups, rule) => {
    const thresholdKey = JSON.stringify(rule.learned_threshold || {});
    const key = rule.allocation === "base" ? `${rule.template_name}:${rule.metric_key}:${thresholdKey}` : rule.id;
    if (!groups[key]) groups[key] = { ...rule, evidence_ids: [...(rule.evidence_ids || [])] };
    else groups[key].evidence_ids = [...new Set([...(groups[key].evidence_ids || []), ...(rule.evidence_ids || [])])];
    return groups;
  }, {}));
  const exceptions = preview.exceptions || [];
  const validations = preview.validations || [];
  const validationSummary = preview.validationSummary || {};
  const directions = preview.directions || [];
  const jobs = learning?.uploadJobs || [];
  const guidance = learning?.guidance || [];
  const evidenceCount = Number(preview.run?.evidence_count || 0);
  const batchIds = preview.run?.snapshot_batch_ids || [];
  const canCreateDrafts = rules.length > 0 && preview.run?.status === "ready_for_draft" && !loading;
  const [rebuildResult, setRebuildResult] = useState(null);
  const [rebuildError, setRebuildError] = useState("");
  const [selectedRuleId, setSelectedRuleId] = useState("");
  const [leftEvidenceId, setLeftEvidenceId] = useState("");
  const [rightEvidenceId, setRightEvidenceId] = useState("");
  const [exceptionForms, setExceptionForms] = useState({});
  const [directionForm, setDirectionForm] = useState({ purpose:"explanation", action:"none", explanation:"", isBlocking:false, parameters:"{}" });
  const [workflowNotice, setWorkflowNotice] = useState("");
  const activeRuleId = selectedRuleId || displayRules[0]?.id || "";
  const activeRule = displayRules.find((rule) => rule.id === activeRuleId);
  const ruleValidations = validations.filter((row) => row.rule_id === activeRuleId);
  const sortedValidations = [...ruleValidations].sort((a,b) => Number(a.authoritative_value)-Number(b.authoritative_value));
  const suggestedPair = (() => { for(let i=0;i<sortedValidations.length-1;i+=1) if(sortedValidations[i].authoritative_status!==sortedValidations[i+1].authoritative_status) return [sortedValidations[i].evidence_id,sortedValidations[i+1].evidence_id]; return sortedValidations.slice(0,2).map((row)=>row.evidence_id); })();
  const effectiveLeft = leftEvidenceId && ruleValidations.some((row)=>row.evidence_id===leftEvidenceId) ? leftEvidenceId : suggestedPair[0] || "";
  const effectiveRight = rightEvidenceId && ruleValidations.some((row)=>row.evidence_id===rightEvidenceId) ? rightEvidenceId : suggestedPair[1] || "";
  const comparisonRows = [effectiveLeft,effectiveRight].map((id)=>ruleValidations.find((row)=>row.evidence_id===id)).filter(Boolean);
  async function handleRebuildInference() {
    setRebuildResult(null);
    setRebuildError("");
    try {
      const result = await onRebuildInference();
      setRebuildResult({
        at: result?.run?.created_at || new Date().toISOString(),
        rules: result?.rules?.length || 0,
        exceptions: result?.exceptions?.length || 0,
      });
    } catch (err) {
      setRebuildError(err.response?.data?.error || "Inference rebuild failed. Review the page alert for details.");
    }
  }
  async function handleResolveException(item) {
    const form = exceptionForms[item.id] || { action:"use_pdf", note:"" };
    setWorkflowNotice("");
    try { await resolveAdminPdfLearningException(item.id, form); await onRefresh(); setWorkflowNotice("Exception resolution saved. Rebuild inference to apply it."); }
    catch(err){ setWorkflowNotice(err.response?.data?.error || "Exception resolution failed."); }
  }
  async function handleSaveDirection(event) {
    event.preventDefault();
    if(!activeRule) return;
    try {
      const parameters = JSON.parse(directionForm.parameters || "{}");
      await saveAdminPdfLearningDirection({ scopeType:"rule", ruleId:activeRule.id, scopeKey:`${activeRule.template_name}:${activeRule.metric_key}`, ...directionForm, parameters });
      setDirectionForm({ purpose:"explanation", action:"none", explanation:"", isBlocking:false, parameters:"{}" });
      await onRefresh(); setWorkflowNotice("Expert direction saved. Rebuild inference to apply binding changes.");
    } catch(err){ setWorkflowNotice(err.response?.data?.error || err.message || "Expert direction could not be saved."); }
  }
  async function handleMarkReady() {
    try { await markAdminPdfLearningReady(preview.run.id, "Validated in Learning Lab workspace."); await onRefresh(); setWorkflowNotice("This inference run is Ready for Draft."); }
    catch(err){ setWorkflowNotice(err.response?.data?.error || "Run could not be marked ready."); }
  }
  return (
    <section style={s.section}>
      <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={s.sectionTitle}>
            <HelpTitle help="Released expert PDFs define the intended metric colors. A pinned Supabase snapshot supplies report identity and context only. Clean evidence compiles reusable draft rules; only ambiguous evidence enters expert review.">Threshold Learning Lab</HelpTitle>
          </div>
          <div style={s.hint}>Teach reusable scoring rules from authoritative expert PDFs. Learned changes remain drafts until explicitly published.</div>
        </div>
        <div style={s.row}>
          <button type="button" style={s.secondary} onClick={() => document.getElementById("authoritative-exceptions")?.scrollIntoView({ behavior: "smooth", block: "start" })}>Review Exceptions</button>
          <button type="button" style={s.secondary} onClick={onRefresh} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
        <LearningStat label="Evidence Metrics" value={evidenceCount} />
        <LearningStat label="Learned Changes" value={displayRules.length} />
        <LearningStat label="Exceptions" value={exceptions.length} />
        <LearningStat label="Inference Status" value={preview.run?.status || "Ready for PDFs"} />
      </div>
      {workflowNotice && <div role="status" style={{ ...s.panel, marginBottom: 12, color:"var(--text-primary)" }}>{workflowNotice}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(300px, 0.65fr)", gap: 14, alignItems: "start" }}>
        <div style={{ minWidth: 0 }}>
          <form style={s.panel} onSubmit={onUploadArchive}>
            <div style={s.subTitle}>1. Authoritative PDF Upload</div>
            <div style={s.hint}>Upload a ZIP containing released expert PDFs. The job pins one successful Supabase snapshot batch before matching samples and never queries or falls back to Artemis.</div>
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Released PDF ZIP</label>
            <input type="file" accept=".zip,application/zip" style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }} onChange={(event) => onUploadFileChange(event.target.files?.[0] || null)} />
            {uploadFile && <div style={{ ...s.hint, marginTop: 6 }}>{uploadFile.name} · {formatBytes(uploadFile.size)}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(150px, .35fr) minmax(220px, 1fr)", gap: 12, marginTop: 12 }}>
              <div>
                <label style={s.label}>Report Limit</label>
                <input type="number" min="1" max="500" style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }} value={uploadLimit} onChange={(event) => onUploadLimitChange(event.target.value)} />
              </div>
              <label style={{ ...s.row, alignItems: "flex-start", marginTop: 20 }}>
                <input type="checkbox" checked={uploadAiEnabled} onChange={(event) => onUploadAiEnabledChange(event.target.checked)} />
                <span><strong>Use Sol for parser ambiguity</strong><div style={s.hint}>Deterministic extraction and inference remain the source of truth.</div></span>
              </label>
            </div>
            <button type="submit" style={{ ...s.btn, marginTop: 12 }} disabled={loading}>{loading ? "Processing..." : "Upload and Learn"}</button>
          </form>

          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-start" }}>
              <div><div style={s.subTitle}>2. Consolidated Learned-Rule Preview</div><div style={s.hint}>Every clean observation must reproduce its expert PDF color before a draft can be created.</div></div>
              <button type="button" style={s.secondary} onClick={handleRebuildInference} disabled={loading}>{loading ? "Rebuilding..." : "Rebuild Inference"}</button>
            </div>
            {batchIds.length > 0 && <div style={{ ...s.hint, marginTop: 8 }}>Pinned snapshot batch: {batchIds.join(", ")}</div>}
            {preview.run?.created_at && <div style={{ ...s.hint, marginTop: 5 }}>Last compiled: {formatDate(preview.run.created_at)} · Inference version {preview.run.inference_version || "current"}</div>}
            {Array.isArray(preview.differences) && preview.differences.length > 0 && <div style={{ ...s.hint, marginTop: 5 }}>
              Compared with the previous rebuild: {preview.differences.filter((item) => item.change === "added").length} added · {preview.differences.filter((item) => item.change === "removed").length} removed · {preview.differences.filter((item) => item.change === "boundary_adjusted").length} boundary-adjusted · {preview.differences.filter((item) => item.change === "unchanged").length} unchanged
            </div>}
            {rebuildResult && (
              <div role="status" style={{ ...s.ok, marginTop: 10 }}>
                Inference rebuilt successfully at {formatDate(rebuildResult.at)}. The consolidated result contains {formatCount(rebuildResult.rules)} learned change{rebuildResult.rules === 1 ? "" : "s"} and {formatCount(rebuildResult.exceptions)} exception{rebuildResult.exceptions === 1 ? "" : "s"}. If the values look unchanged, the same evidence produced the same deterministic rules.
              </div>
            )}
            {rebuildError && <div role="alert" style={{ ...s.err, marginTop: 10 }}>{rebuildError}</div>}
            {displayRules.map((rule) => (
              <details key={rule.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 12 }}>
                <summary style={{ cursor: "pointer", listStylePosition: "outside" }}>
                  <span style={{ ...s.row, justifyContent: "space-between", display: "inline-flex", width: "calc(100% - 18px)", marginLeft: 6 }}>
                    <strong>{rule.template_name} · {rule.metric_key}</strong>
                    <span style={s.row}><span style={{ ...s.pill, color: "#15803d", background: "#dcfce7" }}>Included in latest rebuild</span><span style={s.pill}>{rule.allocation}</span></span>
                  </span>
                </summary>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 8, marginTop: 8 }}>
                  <ThresholdSummaryCard label="Current" threshold={rule.before_threshold} />
                  <ThresholdSummaryCard label="Learned" threshold={rule.learned_threshold} />
                </div>
                <div style={{ ...s.hint, marginTop: 6 }}>{(rule.evidence_ids || []).length} supporting evidence item{(rule.evidence_ids || []).length === 1 ? "" : "s"} · simulation reproduced all effective colors</div>
              </details>
            ))}
            {displayRules.length === 0 && <div style={{ ...s.hint, marginTop: 10 }}>No learned changes yet. Upload authoritative PDFs to begin.</div>}
          </div>

          <div id="authoritative-exceptions" style={{ ...s.panel, marginTop: 12, scrollMarginTop: 16 }}>
            <div style={{ ...s.row, justifyContent: "space-between", alignItems: "flex-start" }}>
              <div><div style={s.subTitle}>3. Actionable Exceptions</div><div style={s.hint}>Only unmatched samples, numeric disagreements, ambiguous extraction or context, contradictory evidence, and non-inferable rules appear here.</div></div>
              <span style={s.pill}>{exceptions.length} requiring action</span>
            </div>
            {exceptions.slice(0, 12).map((item, index) => (
              <div key={item.id || `${item.metricKey || "exception"}-${index}`} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
                <strong>{item.lab_sample_id || item.context?.templateName || "Evidence"}{item.metric_key || item.metricKey ? ` · ${item.metric_key || item.metricKey}` : ""}</strong>
                <div style={s.hint}>{item.reason || "Expert resolution required"}</div>
                {item.id && <div style={{ display:"grid",gridTemplateColumns:"minmax(150px,.4fr) minmax(220px,1fr) auto",gap:8,marginTop:8 }}>
                  <select style={s.input} value={exceptionForms[item.id]?.action || "use_pdf"} onChange={(event)=>setExceptionForms((current)=>({...current,[item.id]:{...(current[item.id]||{}),action:event.target.value}}))}>
                    <option value="use_pdf">Use PDF value</option><option value="use_snapshot">Use snapshot value</option><option value="exclude_metric">Exclude metric</option><option value="exclude_report">Exclude report</option><option value="require_more_evidence">Require more evidence</option>
                  </select>
                  <input style={s.input} placeholder="Expert resolution note" value={exceptionForms[item.id]?.note || ""} onChange={(event)=>setExceptionForms((current)=>({...current,[item.id]:{...(current[item.id]||{}),note:event.target.value}}))} />
                  <button type="button" style={s.secondary} onClick={()=>handleResolveException(item)}>Save</button>
                </div>}
              </div>
            ))}
            {exceptions.length === 0 && <div style={{ ...s.hint, marginTop: 10 }}>No exceptions require expert review.</div>}
          </div>

          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={s.subTitle}>4. Validation Workspace</div>
            <div style={s.hint}>Inspect every authoritative observation before approving the consolidated run. The nearest opposing-color reports are suggested automatically.</div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(135px,1fr))",gap:8,marginTop:10 }}>
              <MiniStat label="Validated" value={validationSummary.reproduced || 0} /><MiniStat label="Failed" value={validationSummary.failed || 0} /><MiniStat label="Reports" value={validationSummary.documents || 0} /><MiniStat label="Blocking Notes" value={validationSummary.blockingDirections || 0} />
            </div>
            {displayRules.length > 0 && <>
              <label style={{...s.label,display:"block",marginTop:12}}>Learned rule</label>
              <select style={{...s.input,width:"100%",marginTop:6}} value={activeRuleId} onChange={(event)=>{setSelectedRuleId(event.target.value);setLeftEvidenceId("");setRightEvidenceId("");}}>{displayRules.map((rule)=><option key={rule.id} value={rule.id}>{rule.template_name} · {rule.metric_key}</option>)}</select>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:10,marginTop:10}}>
                <EvidenceComparisonCard label="Report A" rows={ruleValidations} value={effectiveLeft} onChange={setLeftEvidenceId} row={comparisonRows[0]} />
                <EvidenceComparisonCard label="Report B" rows={ruleValidations} value={effectiveRight} onChange={setRightEvidenceId} row={comparisonRows[1]} />
              </div>
              <div style={{overflowX:"auto",marginTop:12}}><table style={s.table}><thead><tr><th style={s.th}>Report</th><th style={s.th}>Value</th><th style={s.th}>PDF</th><th style={s.th}>Current</th><th style={s.th}>Learned</th><th style={s.th}>Validation</th></tr></thead><tbody>{ruleValidations.map((row)=><tr key={row.id}><td style={s.td}>{row.lab_sample_id}</td><td style={s.td}>{row.authoritative_value}</td><td style={s.td}>{row.authoritative_status}</td><td style={s.td}>{row.before_status}</td><td style={s.td}>{row.learned_status}</td><td style={s.td}>{row.reproduced ? "Pass" : "Fail"}</td></tr>)}</tbody></table></div>
              <form onSubmit={handleSaveDirection} style={{borderTop:"1px solid var(--border)",marginTop:12,paddingTop:12}}>
                <div style={s.label}>Add direction for {activeRule?.template_name} · {activeRule?.metric_key}</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8,marginTop:8}}><select style={s.input} value={directionForm.purpose} onChange={(e)=>setDirectionForm((v)=>({...v,purpose:e.target.value,action:e.target.value==="explanation"?"none":v.action}))}><option value="explanation">Explanation only</option><option value="binding">Binding directive</option><option value="follow_up">Follow-up required</option><option value="future_consideration">Future consideration</option></select><select style={s.input} value={directionForm.action} onChange={(e)=>setDirectionForm((v)=>({...v,action:e.target.value}))}><option value="none">No inference action</option><option value="context_only">Keep context-only</option><option value="prevent_base_promotion">Prevent base promotion</option><option value="request_base">Request base allocation</option><option value="require_more_evidence">Require more evidence</option><option value="expert_boundary">Set expert boundary</option><option value="qualitative_non_inferable">Qualitative/non-inferable</option></select></div>
                {directionForm.action==="expert_boundary" && <input style={{...s.input,width:"100%",boxSizing:"border-box",marginTop:8}} value={directionForm.parameters} onChange={(e)=>setDirectionForm((v)=>({...v,parameters:e.target.value}))} placeholder='{"warn": 20, "fail": 40}' />}
                <textarea style={{...s.input,width:"100%",boxSizing:"border-box",minHeight:75,marginTop:8}} value={directionForm.explanation} onChange={(e)=>setDirectionForm((v)=>({...v,explanation:e.target.value}))} placeholder="Expert reasoning" />
                <label style={{...s.row,marginTop:8}}><input type="checkbox" checked={directionForm.isBlocking} onChange={(e)=>setDirectionForm((v)=>({...v,isBlocking:e.target.checked}))} /> Block draft readiness</label>
                <button type="submit" style={{...s.secondary,marginTop:8}}>Save Expert Direction</button>
              </form>
            </>}
          </div>

          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={s.subTitle}>5. Readiness and Draft Handoff</div>
            <div style={s.hint}>Create base and/or learned conditional drafts from the consolidated preview. This never publishes live customer thresholds.</div>
            <div style={{ ...s.row, marginTop: 12 }}>
              {preview.run?.status === "validated" && <button type="button" style={s.secondary} onClick={handleMarkReady}>Mark Ready for Draft</button>}
              <button type="button" style={s.btn} onClick={onCreateDrafts} disabled={!canCreateDrafts}>Create Threshold Drafts</button>
              <span style={s.hint}>{preview.run?.status === "ready_for_draft" ? "Approved and ready to create drafts." : exceptions.length ? "Resolve exceptions, rebuild, and validate before approval." : "Mark this validated run Ready for Draft."}</span>
            </div>
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <form style={s.panel} onSubmit={onSaveGuidance}>
            <div style={s.subTitle}>Expert Direction</div>
            <div style={s.hint}>Save durable calibration guidance that should inform future inference and expert review.</div>
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Title</label>
            <input style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }} value={guidanceTitle} onChange={(event) => onGuidanceTitleChange(event.target.value)} />
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Category</label>
            <select style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }} value={guidanceCategory} onChange={(event) => onGuidanceCategoryChange(event.target.value)}>
              <option value="general">General</option><option value="viscosity">Viscosity</option><option value="contamination">Contamination</option><option value="wear">Wear</option><option value="oil">Oil condition</option><option value="scoring">Overall scoring</option>
            </select>
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Notes</label>
            <textarea style={{ ...s.input, width: "100%", boxSizing: "border-box", minHeight: 130, marginTop: 6, resize: "vertical" }} value={guidanceText} onChange={(event) => onGuidanceTextChange(event.target.value)} />
            <button type="submit" style={{ ...s.btn, marginTop: 12 }} disabled={loading}>Save Guidance</button>
          </form>

          <div style={{ ...s.panel, marginTop: 12 }}><div style={s.subTitle}>Active Guidance</div>
            {guidance.map((item) => <div key={item.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}><strong>{item.title}</strong><div style={s.hint}>{item.category} · {formatDate(item.updatedAt || item.createdAt)}</div><div style={{ marginTop: 6 }}>{item.guidanceText}</div></div>)}
            {guidance.length === 0 && <div style={s.hint}>No expert guidance saved yet.</div>}
          </div>

          <div style={{ ...s.panel, marginTop: 12 }}><div style={s.subTitle}>Recent PDF Learning Runs</div>
            {jobs.map((job) => <div key={job.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}><div style={{ ...s.row, justifyContent: "space-between" }}><strong>{job.fileName}</strong><span style={s.pill}>{job.status}</span></div><div style={s.hint}>{formatBytes(job.fileSizeBytes)} · {formatDate(job.createdAt)} · Processed {formatCount(job.progress?.processed || job.resultSummary?.counters?.processed || 0)}</div>{job.errorMessage && <div style={{ ...s.err, marginTop: 6 }}>{job.errorMessage}</div>}</div>)}
            {jobs.length === 0 && <div style={s.hint}>No authoritative PDF learning runs yet.</div>}
          </div>
        </div>
      </div>
    </section>
  );
}

function ThresholdSummaryCard({ label, threshold = {} }) {
  const fields = [
    ["Pass", threshold.pass],
    ["Warn", threshold.warn],
    ["Fail", threshold.fail],
    ["Type", threshold.type],
    ["Unit", threshold.unit],
  ].filter(([, value]) => value !== undefined && value !== null && value !== "");
  return (
    <div style={{ minWidth: 0, maxWidth: "100%", padding: 10, background: "var(--bg-page)", borderRadius: 6, overflow: "hidden" }}>
      <div style={{ ...s.label, marginBottom: 7 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
        {fields.map(([name, value]) => (
          <span key={name} style={{ maxWidth: "100%", padding: "4px 7px", borderRadius: 5, background: "var(--bg-card)", border: "1px solid var(--border)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
            <strong>{name}:</strong> {String(value)}
          </span>
        ))}
      </div>
    </div>
  );
}

function EvidenceComparisonCard({ label, rows, value, onChange, row }) {
  return (
    <div style={{minWidth:0,padding:10,border:"1px solid var(--border)",borderRadius:8,background:"var(--bg-page)"}}>
      <div style={s.label}>{label}</div>
      <select style={{...s.input,width:"100%",marginTop:6}} value={value} onChange={(event)=>onChange(event.target.value)}>
        {(rows||[]).map((item)=><option key={item.evidence_id} value={item.evidence_id}>{item.lab_sample_id} · {item.authoritative_value} · {item.authoritative_status}</option>)}
      </select>
      {row && <div style={{marginTop:8,lineHeight:1.45}}>
        <strong>{row.lab_sample_id}</strong><div style={s.hint}>{row.pdf_path} · {formatDate(row.sample_date)}</div>
        <div style={{marginTop:6}}>PDF: <strong>{row.authoritative_value} · {row.authoritative_status}</strong></div>
        <div style={s.hint}>Snapshot: {row.snapshot_value ?? "N/A"} · Current: {row.before_status} · Learned: {row.learned_status}</div>
        <div style={s.hint}>Category scores: {JSON.stringify(row.category_scores_before || {})} → {JSON.stringify(row.category_scores_after || {})}</div>
        {row.comments && <div style={{...s.hint,marginTop:6}}>{row.comments}</div>}
        <a href={`/api/admin/threshold-learning/documents/${encodeURIComponent(row.document_id)}/pdf`} target="_blank" rel="noreferrer" style={{display:"inline-block",marginTop:8}}>Open stored PDF</a>
      </div>}
    </div>
  );
}

function ThresholdLearningLabSection({
  learning,
  loading,
  guidanceTitle,
  guidanceCategory,
  guidanceText,
  uploadFile,
  uploadLimit,
  uploadAiEnabled,
  proposalReviewNotes,
  onGuidanceTitleChange,
  onGuidanceCategoryChange,
  onGuidanceTextChange,
  onUploadFileChange,
  onUploadLimitChange,
  onUploadAiEnabledChange,
  onProposalReviewNoteChange,
  onSaveGuidance,
  onUploadArchive,
  onGenerateProposals,
  onReviewProposal,
  draftPreview,
  onPreviewThresholdDraft,
  onCreateThresholdDraft,
  onCloseDraftPreview,
  onRefresh,
  onOpenReview,
}) {
  const summary = learning?.summary || {};
  const recommendations = learning?.recommendations || [];
  const guidance = learning?.guidance || [];
  const topPatterns = learning?.topPatterns || [];
  const topMetrics = learning?.topMetrics || [];
  const topTemplates = learning?.topTemplates || [];
  const candidates = learning?.candidates || {};
  const uploadJobs = learning?.uploadJobs || [];
  const proposals = learning?.proposals || [];
  const thresholdAudit = learning?.thresholdAudit || [];
  const thresholdDraftSimulations = learning?.thresholdDraftSimulations || [];
  const learnedRulePreview = learning?.learnedRulePreview || { rules: [], exceptions: [] };
  const excelArtemisReviewFocus = learning?.excelArtemisReviewFocus || {};
  const excelArtemisReviewProgress = learning?.excelArtemisReviewProgress || {};
  const [selectedProposal, setSelectedProposal] = useState(null);
  function openRecommendationReview(item) {
    const target = getRecommendationReviewTarget(item?.metric);
    if (target.type === "review") {
      onOpenReview({ issueType: "all", decisionStatus: "all", search: target.search });
      return;
    }
    const element = document.getElementById(target.id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      element.focus?.({ preventScroll: true });
    }
  }
  return (
    <section style={s.section}>
      <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <div style={s.sectionTitle}>
            <HelpTitle help="A controlled workbench for comparing released PDF reports against the active snapshot and dashboard threshold logic. It gathers color-status evidence, recommendations, expert notes, discrepancy review status, draft simulations, and proposal history. It does not publish live thresholds unless an admin later reviews and publishes a draft in the Thresholds Control Panel.">Threshold Learning Lab</HelpTitle>
          </div>
          <div style={s.hint}>PDF / Snapshot comparison findings, expert direction, and threshold-rule refinement workbench.</div>
        </div>
        <div style={s.row}>
          <button type="button" style={s.secondary} onClick={onOpenReview}>Review Discrepancies</button>
          <button type="button" style={s.secondary} onClick={onRefresh} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
        <LearningStat label="Processed" value={summary.processed} />
        <LearningStat label="Exact" value={summary.exact} />
        <LearningStat
          label="Override Aligned"
          value={summary.overridden}
          help="Reports where the first automatic threshold evaluation did not match the released PDF, but the reconciliation tool could derive a report-specific deterministic override that made the metric statuses align with the released outcome. These are aligned after an override; they are not global threshold-rule changes."
        />
        <LearningStat label="Missing Snapshot" value={summary.skipped} />
        <LearningStat label="Needs Review" value={summary.needsReview} />
        <LearningStat label="Parity" value={`${summary.parityRate || 0}%`} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(280px, 0.75fr)", gap: 14, alignItems: "start" }}>
        <div style={{ minWidth: 0 }}>
          <ExpertReviewProgressPanel progress={excelArtemisReviewProgress} onOpenReview={onOpenReview} />

          <div style={{ ...s.panel, marginBottom: 12 }}>
            <div style={s.subTitle}>Authoritative PDF Learned Rules</div>
            <div style={s.hint}>Clean PDF color evidence is compiled automatically. Only quarantined or non-inferable evidence needs expert review; learned changes remain drafts until explicitly published.</div>
            <div style={{ ...s.row, marginTop: 10 }}>
              <span style={s.pill}>{learnedRulePreview.rules?.length || 0} learned changes</span>
              <span style={s.pill}>{learnedRulePreview.exceptions?.length || 0} exceptions</span>
              <span style={s.pill}>{learnedRulePreview.run?.status || "No inference yet"}</span>
            </div>
            {(learnedRulePreview.rules || []).slice(0, 12).map((rule) => (
              <div key={rule.id} style={{ borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 10 }}>
                <div style={{ fontWeight: 900 }}>{rule.template_name} · {rule.metric_key}</div>
                <div style={s.hint}>Before {JSON.stringify(rule.before_threshold)} → learned {JSON.stringify(rule.learned_threshold)} · {rule.allocation} override</div>
              </div>
            ))}
            {(learnedRulePreview.exceptions || []).length > 0 && (
              <button type="button" style={{ ...s.secondary, marginTop: 10 }} onClick={onOpenReview}>Review actionable exceptions</button>
            )}
          </div>

          <div style={s.subTitle}>
            <HelpTitle help="A short list of the strongest recurring threshold-learning signals from historical comparisons. These are recommendations to review, not automatic changes.">Key Findings</HelpTitle>
          </div>
          <div style={{ overflowX: "auto", marginBottom: 12 }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Metric</th>
                  <th style={s.th}>Priority</th>
                  <th style={s.th}>Recommendation</th>
                  <th style={s.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.map((item) => {
                  const target = getRecommendationReviewTarget(item.metric);
                  return (
                  <tr
                    key={item.metric}
                    role="button"
                    tabIndex={0}
                    title={target.label}
                    style={{ cursor: "pointer" }}
                    onClick={() => openRecommendationReview(item)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openRecommendationReview(item);
                      }
                    }}
                  >
                    <td style={{ ...s.td, fontWeight: 900 }}>{item.metric}</td>
                    <td style={s.td}>{item.priority}</td>
                    <td style={{ ...s.td, minWidth: 280 }}>{item.recommendation}</td>
                    <td style={s.td}>
                      <div>{item.status}</div>
                      <div style={{ ...s.hint, color: "#00964d", fontWeight: 900, marginTop: 4 }}>{target.label}</div>
                    </td>
                  </tr>
                  );
                })}
                {recommendations.length === 0 && <tr><td style={s.td} colSpan="4">No threshold-learning recommendations recorded yet.</td></tr>}
              </tbody>
            </table>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            <LearningList title="Top Patterns" items={topPatterns} help="Most common before/after status changes discovered during reconciliation, such as a metric moving from normal to N/A or warning." />
            <LearningList title="Top Metrics" items={topMetrics} help="Metrics that most often needed deterministic overrides to match the released PDF color status." />
            <LearningList title="Template Hot Spots" items={topTemplates} help="Template and metric combinations where released PDF outcomes repeatedly differed from current dashboard logic." />
          </div>

          <ExcelArtemisFocusPanel
            focus={excelArtemisReviewFocus}
            onOpenReview={onOpenReview}
          />

          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={s.subTitle}>
              <HelpTitle help="Evidence tables that show historical value ranges and status outcomes by template, grade, brand, or component. These help Lake decide whether a rule should change; they do not change customer reports by themselves.">Candidate Threshold Tables</HelpTitle>
            </div>
            <div style={s.hint}>
              These are review-only candidates derived from historical workbook outcomes. They do not publish thresholds and should be reviewed by the oil-analysis expert before being turned into rule proposals.
            </div>
            <div id="threshold-learning-viscosity-candidates" tabIndex={-1}>
              <CandidateTable
                title="Viscosity Grade Bands"
                type="viscosity"
                items={candidates.viscosityBands || []}
              />
            </div>
            <div id="threshold-learning-oxidation-candidates" tabIndex={-1}>
              <CandidateTable
                title="Oxidation Brand / Template Rules"
                type="oxidation"
                items={candidates.oxidationRules || []}
              />
            </div>
            <div id="threshold-learning-fuel-candidates" tabIndex={-1}>
              <CandidateTable
                title="Fuel Dilution Template / Component Review"
                type="fuel"
                items={candidates.fuelDilutionRules || []}
              />
            </div>
          </div>

          <DraftSimulationPanel simulations={thresholdDraftSimulations} />

          <div id="threshold-learning-rule-proposals" tabIndex={-1} style={{ ...s.panel, marginTop: 12 }}>
            <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 8 }}>
              <div>
                <div style={s.subTitle}>
                  <HelpTitle help="Evidence-backed recommendations generated from comparison findings. Accepting one records expert approval only; publishing still requires reviewing the generated draft in the Thresholds Control Panel.">Rule Change Proposals</HelpTitle>
                </div>
                <div style={s.hint}>Generate reviewable proposals from the current comparison findings. Accepting a proposal records expert approval; it does not publish live thresholds.</div>
              </div>
              <div style={s.row}>
                <button type="button" style={s.secondary} onClick={onGenerateProposals} disabled={loading}>{loading ? "Working..." : "Generate Proposals"}</button>
                <button type="button" style={s.btn} onClick={onPreviewThresholdDraft} disabled={loading}>Preview Accepted Changes</button>
              </div>
            </div>
            {proposals.map((proposal) => (
              <div key={proposal.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>{proposal.title}</div>
                    <div style={s.hint}>{proposal.metric} · {proposal.priority} priority · {proposal.status}</div>
                  </div>
                  <span style={s.pill}>{proposal.status}</span>
                </div>
                <div style={{ marginTop: 8, lineHeight: 1.45 }}>{proposal.recommendation}</div>
                <div style={{ ...s.hint, marginTop: 8 }}>{proposal.rationale}</div>
                <ProposalEvidence evidence={proposal.evidence} compact />
                {proposal.status === "proposed" ? (
                  <div style={{ marginTop: 10 }}>
                    <textarea
                      style={{ ...s.input, width: "100%", boxSizing: "border-box", minHeight: 70, resize: "vertical" }}
                      value={proposalReviewNotes?.[proposal.id] || ""}
                      onChange={(event) => onProposalReviewNoteChange(proposal.id, event.target.value)}
                      placeholder="Optional expert review note before accepting or rejecting this proposal."
                    />
                    <div style={{ ...s.row, marginTop: 8 }}>
                      <button type="button" style={s.secondary} disabled={loading} onClick={() => setSelectedProposal(proposal)}>Why this proposal?</button>
                      <button type="button" style={s.btn} disabled={loading} onClick={() => onReviewProposal(proposal.id, "accepted")}>Accept</button>
                      <button type="button" style={s.danger} disabled={loading} onClick={() => onReviewProposal(proposal.id, "rejected")}>Reject</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ ...s.hint, marginBottom: 8 }}>
                      Reviewed by {proposal.reviewedBy || "unknown"} {proposal.reviewedAt ? `on ${formatDate(proposal.reviewedAt)}` : ""}
                      {proposal.reviewNote ? `: ${proposal.reviewNote}` : ""}
                      {proposal.generatedDraftId ? ` · Draft: ${proposal.generatedDraftId}` : ""}
                    </div>
                    <button type="button" style={s.secondary} disabled={loading} onClick={() => setSelectedProposal(proposal)}>View evidence and history</button>
                  </div>
                )}
              </div>
            ))}
            {proposals.length === 0 && <div style={s.hint}>No rule change proposals generated yet.</div>}
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <form style={s.panel} onSubmit={onSaveGuidance}>
            <div style={s.subTitle}>
              <HelpTitle help="Project memory for Lake's guidance. Notes saved here can be referenced during future comparison runs and AI-assisted analysis so expert direction is not trapped in a chat thread.">Expert Direction</HelpTitle>
            </div>
            <div style={s.hint}>Save client guidance here so future comparison runs and AI-assisted threshold updates can use it as project memory.</div>
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Title</label>
            <input style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }} value={guidanceTitle} onChange={(event) => onGuidanceTitleChange(event.target.value)} placeholder="Example: Sodium should be coolant-context only" />
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Category</label>
            <select style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }} value={guidanceCategory} onChange={(event) => onGuidanceCategoryChange(event.target.value)}>
              <option value="general">General</option>
              <option value="sodium">Sodium / coolant</option>
              <option value="viscosity">Viscosity</option>
              <option value="oxidation">Oxidation</option>
              <option value="silicon">Silicon</option>
              <option value="phosphorus">Phosphorus</option>
              <option value="fuel_dilution">Fuel dilution</option>
              <option value="soot">Soot</option>
              <option value="copper">Copper</option>
            </select>
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Notes</label>
            <textarea
              style={{ ...s.input, width: "100%", boxSizing: "border-box", minHeight: 130, marginTop: 6, resize: "vertical" }}
              value={guidanceText}
              onChange={(event) => onGuidanceTextChange(event.target.value)}
              placeholder="Enter expert direction, caveats, examples, or rule preferences to apply during future analysis."
            />
            <button type="submit" style={{ ...s.btn, marginTop: 12 }} disabled={loading}>Save Guidance</button>
          </form>

          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={s.subTitle}>
              <HelpTitle help="The current active expert notes that should shape future reconciliation interpretation and threshold-rule recommendations.">Active Guidance</HelpTitle>
            </div>
            {guidance.slice(0, 5).map((item) => (
              <div key={item.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
                <div style={{ fontWeight: 900 }}>{item.title}</div>
                <div style={s.hint}>{item.category} · {formatDate(item.updatedAt || item.createdAt)} · {item.updatedBy || item.createdBy}</div>
                <div style={{ marginTop: 6, lineHeight: 1.45 }}>{item.guidanceText}</div>
              </div>
            ))}
            {guidance.length === 0 && <div style={s.hint}>No expert guidance saved yet.</div>}
          </div>

          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={s.subTitle}>
              <HelpTitle help="A read-only audit trail of threshold drafts, publishes, and discarded drafts. Use this to understand who changed threshold configurations and when.">Threshold Change History</HelpTitle>
            </div>
            <div style={s.hint}>Recent draft, publish, and discard activity for base thresholds and conditional rules.</div>
            {thresholdAudit.map((item) => (
              <div key={item.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <strong>{thresholdActionLabel(item.action)}</strong>
                  <span style={s.pill}>{item.configType}</span>
                </div>
                <div style={s.hint}>
                  {formatDate(item.createdAt)} · {item.adminEmail || "unknown"}
                  {item.draftName ? ` · ${item.draftName}` : ""}
                  {item.versionStatus ? ` · ${item.versionStatus}` : ""}
                </div>
                {item.note && <div style={{ marginTop: 6, lineHeight: 1.45 }}>{item.note}</div>}
              </div>
            ))}
            {thresholdAudit.length === 0 && <div style={{ ...s.hint, marginTop: 8 }}>No threshold change history recorded yet.</div>}
          </div>

          <form style={{ ...s.panel, marginTop: 12, background: "var(--bg-page)" }} onSubmit={onUploadArchive}>
            <div style={s.subTitle}>
              <HelpTitle help="Upload a zip of completed PDF reports to compare their released values, color statuses, overall condition, and recommendations against the active snapshot and current published threshold logic. Browser runs are capped and dry-run only.">Upload PDF / Snapshot Comparison Batch</HelpTitle>
            </div>
            <div style={s.hint}>Upload a zip archive of completed PDF reports. The lab reads the first report page, matches each Sample ID to the active snapshot, and compares the PDF's green/caution/red decisions with current thresholds and conditional rules.</div>
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>PDF Zip Archive</label>
            <input
              type="file"
              accept=".zip,application/zip"
              style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }}
              onChange={(event) => onUploadFileChange(event.target.files?.[0] || null)}
            />
            {uploadFile && <div style={{ ...s.hint, marginTop: 6 }}>{uploadFile.name} · {formatBytes(uploadFile.size)}</div>}
            <label style={{ ...s.label, display: "block", marginTop: 12 }}>Run Limit</label>
            <input
              type="number"
              min="1"
              max="500"
              style={{ ...s.input, width: "100%", boxSizing: "border-box", marginTop: 6 }}
              value={uploadLimit}
              onChange={(event) => onUploadLimitChange(event.target.value)}
            />
            <label style={{ ...s.row, marginTop: 12, alignItems: "flex-start" }}>
              <input
                type="checkbox"
                checked={uploadAiEnabled}
                onChange={(event) => onUploadAiEnabledChange(event.target.checked)}
              />
              <span>
                <strong>Use Sol for ambiguous cases</strong>
                <div style={s.hint}>Only parser ambiguity or unusual mismatch patterns are escalated; deterministic status parity remains the gate.</div>
              </span>
            </label>
            <button type="submit" style={{ ...s.btn, marginTop: 12 }} disabled={loading}>{loading ? "Working..." : "Start Dry Comparison"}</button>
          </form>

          <div style={{ ...s.panel, marginTop: 12 }}>
            <div style={s.subTitle}>
              <HelpTitle help="Recent browser-started comparison jobs, including status and basic result counts. These help confirm new evidence was processed before reviewing findings.">Recent Upload Runs</HelpTitle>
            </div>
            {uploadJobs.map((job) => (
              <div key={job.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <strong>{job.fileName}</strong>
                  <span style={s.pill}>{job.status}</span>
                </div>
                <div style={s.hint}>
                  {formatBytes(job.fileSizeBytes)} · Limit {job.runLimit} · {job.uploadedBy} · {formatDate(job.createdAt)}
                </div>
                <div style={{ marginTop: 6, lineHeight: 1.45 }}>
                  Processed {formatCount(job.progress?.processed || job.resultSummary?.counters?.processed || 0)}
                  {job.resultSummary?.counters ? ` · Override aligned ${formatCount(job.resultSummary.counters.overridden || 0)} · Skipped ${formatCount(job.resultSummary.counters.skipped || 0)}` : ""}
                </div>
                {job.errorMessage && <div style={{ ...s.err, marginTop: 6 }}>{job.errorMessage}</div>}
              </div>
            ))}
            {uploadJobs.length === 0 && <div style={s.hint}>No uploaded comparison jobs yet.</div>}
          </div>
        </div>
      </div>
      {selectedProposal && (
        <ProposalDetailModal
          proposal={selectedProposal}
          onClose={() => setSelectedProposal(null)}
        />
      )}
      {draftPreview && (
        <ThresholdDraftPreviewModal
          preview={draftPreview}
          loading={loading}
          onCreateDraft={onCreateThresholdDraft}
          onClose={onCloseDraftPreview}
        />
      )}
    </section>
  );
}

function LearningStat({ label, value, help, onClick, actionLabel }) {
  const interactive=Boolean(onClick);
  return (
    <div style={{...s.panel,cursor:interactive?"pointer":"default",borderColor:interactive?"var(--teal)":"var(--border)"}} role={interactive?"button":undefined} tabIndex={interactive?0:undefined} onClick={interactive?onClick:undefined} onKeyDown={interactive?(event)=>{if(event.key==="Enter"||event.key===" ")onClick();}:undefined}>
      <div style={s.label}>{help ? <HelpTitle help={help}>{label}</HelpTitle> : label}</div>
      <div style={{ ...s.value, fontSize: 22 }}>{typeof value === "number" ? formatCount(value) : value || "0"}</div>
      {interactive&&<div style={{...s.hint,marginTop:7,color:"var(--teal-dark)",fontWeight:900}}>{actionLabel||"Open"} →</div>}
    </div>
  );
}

function ExpertReviewProgressPanel({ progress, onOpenReview }) {
  const counts = progress?.byDecision || {};
  const statusOptions = [
    { value: "needs_review", label: "Needs Review" },
    { value: "needs_client_input", label: "Needs Client Input" },
    { value: "resolved_accepted", label: "Resolved" },
    { value: "use_excel_value", label: "Use released PDF" },
    { value: "use_artemis_value", label: "Use snapshot" },
    { value: "exclude_from_threshold_learning", label: "Excluded" },
  ];
  const recentItems = progress?.recentItems || [];
  const needsClientInput = progress?.needsClientInput || [];
  return (
    <div style={{ ...s.panel, marginBottom: 12 }}>
      <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <div style={s.subTitle}>
            <HelpTitle help="Tracks how much of the PDF / Snapshot discrepancy queue has been reviewed by an expert. It reads saved review decisions only; it does not change thresholds or approve reports.">Expert Review Progress</HelpTitle>
          </div>
          <div style={s.hint}>{formatCount(progress?.reviewed || 0)} of {formatCount(progress?.total || 0)} discrepancy groups reviewed.</div>
        </div>
        <button type="button" style={s.secondary} onClick={() => onOpenReview({ decisionStatus: "all" })}>Open Review Queue</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
        <MiniStat label="Total Items" value={progress?.total || 0} />
        <MiniStat label="Reviewed" value={progress?.reviewed || 0} />
        <MiniStat label="Needs Review" value={progress?.needsReview || 0} />
        <MiniStat label="Reviewed %" value={`${progress?.reviewedPercent || 0}%`} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, marginTop: 10 }}>
        {statusOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            style={{
              ...s.secondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              minHeight: 42,
              textAlign: "left",
            }}
            onClick={() => onOpenReview({ decisionStatus: option.value })}
          >
            <span style={{ lineHeight: 1.15 }}>{option.label}</span>
            <strong style={{ flex: "0 0 auto", padding: "3px 7px", borderRadius: 999, background: "rgba(15, 23, 42, 0.06)" }}>{formatCount(counts[option.value] || 0)}</strong>
          </button>
        ))}
      </div>
      {(needsClientInput.length > 0 || recentItems.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 12 }}>
          <ProgressItemList title="Needs Client Input" items={needsClientInput} onOpenReview={() => onOpenReview({ decisionStatus: "needs_client_input" })} />
          <ProgressItemList title="Recently Updated" items={recentItems} onOpenReview={() => onOpenReview({ decisionStatus: "all" })} />
        </div>
      )}
    </div>
  );
}

function ProgressItemList({ title, items, onOpenReview }) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 10, background: "var(--bg-page)" }}>
      <div style={{ ...s.row, justifyContent: "space-between" }}>
        <div style={s.label}>{title}</div>
        <button type="button" style={{ ...s.secondary, padding: "5px 8px" }} onClick={onOpenReview}>Open</button>
      </div>
      {(items || []).slice(0, 4).map((item) => (
        <div key={`${title}-${item.id}`} style={{ borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: 8 }}>
          <strong>{item.labSampleId || "N/A"}</strong>
          <div style={s.hint}>{formatExcelArtemisDecision(item.decisionStatus)} · {formatCount(item.issueCount || 0)} issue row{item.issueCount === 1 ? "" : "s"}</div>
          <div style={s.hint}>{item.updatedAt ? `${formatDate(item.updatedAt)} by ${item.updatedBy || "unknown"}` : "No saved update date"}</div>
        </div>
      ))}
      {(!items || items.length === 0) && <div style={{ ...s.hint, marginTop: 8 }}>No items in this bucket.</div>}
    </div>
  );
}

function ProposalImpactSummary({ impact, compact = false }) {
  if (!impact) return null;
  const draftability = impact.draftability || {};
  const stats = [
    ["Candidate Records", impact.candidateRecordCount],
    ["Concern Outcomes", impact.candidateConcernCount],
    ["Mismatch Signals", impact.candidateMismatchCount || impact.patternSignalCount],
    ["Candidate Groups", impact.candidateGroupCount],
  ];
  if (compact) {
    return (
      <div style={{ ...s.hint, marginTop: 8 }}>
        Impact: {formatCount(impact.candidateRecordCount || 0)} candidate records · {formatCount(impact.candidateMismatchCount || impact.patternSignalCount || 0)} mismatch signals · {draftability.status === "draftable" ? "draftable" : "review-only"}
      </div>
    );
  }
  return (
    <div style={{ ...s.panel, marginTop: 10, background: "var(--bg-page)" }}>
      <div style={s.label}>Impact Preview</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 8 }}>
        {stats.map(([label, value]) => (
          <div key={label} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 10, background: "var(--bg-card)" }}>
            <div style={s.hint}>{label}</div>
            <div style={{ fontWeight: 900, fontSize: 18, marginTop: 4 }}>{formatCount(value || 0)}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, lineHeight: 1.45 }}>
        <strong>{draftability.status === "draftable" ? "Draftable" : "Review-only"}:</strong> {draftability.target || "Expert review"}
      </div>
      {draftability.note && <div style={{ ...s.hint, marginTop: 4 }}>{draftability.note}</div>}
      {(impact.exampleSampleIds || []).length > 0 && (
        <div style={{ ...s.hint, marginTop: 4 }}>Examples: {impact.exampleSampleIds.join(", ")}</div>
      )}
    </div>
  );
}

function ProposalEvidence({ evidence, compact = false }) {
  const metricMatches = evidence?.topMetricMatches || [];
  const patternMatches = evidence?.topPatternMatches || [];
  const templateMatches = evidence?.topTemplateMatches || [];
  const guidance = evidence?.activeGuidance || [];
  const viscosityBands = evidence?.viscosityBands || [];
  const oxidationRules = evidence?.oxidationRules || [];
  const fuelDilutionRules = evidence?.fuelDilutionRules || [];
  const evidenceLimit = compact ? 2 : 8;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={s.hint}>
        Evidence: {formatCount(evidence?.processed || 0)} processed · {evidence?.parityRate || 0}% parity
      </div>
      <ProposalImpactSummary impact={evidence?.impact} compact={compact} />
      {[...metricMatches, ...patternMatches, ...templateMatches].slice(0, compact ? 4 : 12).map((item) => (
        <div key={`${item.pattern}-${item.count}`} style={{ ...s.hint, marginTop: 3 }}>
          {item.pattern}: {formatCount(item.count)}
        </div>
      ))}
      {guidance.slice(0, compact ? 2 : 8).map((item) => (
        <div key={item.id} style={{ ...s.hint, marginTop: 3 }}>
          Guidance: {item.title}{!compact && item.guidanceText ? ` - ${item.guidanceText}` : ""}
        </div>
      ))}
      {viscosityBands.slice(0, evidenceLimit).map((item) => (
        <div key={`${item.templateName}-${item.viscosityGrade}`} style={{ ...s.hint, marginTop: 3 }}>
          Candidate band: {item.templateName} / {item.viscosityGrade} normal {formatRange(item.ranges?.normal)} · {formatCount(item.mismatches)} mismatch{item.mismatches === 1 ? "" : "es"}
        </div>
      ))}
      {oxidationRules.slice(0, evidenceLimit).map((item) => (
        <div key={`${item.templateName}-${item.oilBrand}`} style={{ ...s.hint, marginTop: 3 }}>
          Oxidation candidate: {item.templateName} / {item.oilBrand} warn {formatRange(item.ranges?.warn)} · critical {formatRange(item.ranges?.critical)}
        </div>
      ))}
      {fuelDilutionRules.slice(0, evidenceLimit).map((item) => (
        <div key={`${item.templateName}-${item.componentType}`} style={{ ...s.hint, marginTop: 3 }}>
          Fuel candidate: {item.templateName} / {item.componentType} normal {formatRange(item.ranges?.normal)} · warn {formatRange(item.ranges?.warn)} · critical {formatRange(item.ranges?.critical)}
        </div>
      ))}
    </div>
  );
}

function ProposalDetailModal({ proposal, onClose }) {
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section style={{ ...s.modal, ...s.detailModal }} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.sectionTitle}>{proposal.title}</div>
            <div style={s.hint}>{proposal.metric} · {proposal.priority} priority · {proposal.status}</div>
          </div>
          <button type="button" style={s.secondary} onClick={onClose}>Close</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginBottom: 12 }}>
          <div style={s.panel}>
            <div style={s.label}>Recommendation</div>
            <div style={{ marginTop: 8, lineHeight: 1.5 }}>{proposal.recommendation}</div>
          </div>
          <div style={s.panel}>
            <div style={s.label}>Review History</div>
            <div style={{ marginTop: 8, lineHeight: 1.5 }}>
              <div>Status: <strong>{proposal.status}</strong></div>
              <div>Created: {formatDate(proposal.createdAt)} by {proposal.createdBy || "system"}</div>
              {proposal.reviewedAt && <div>Reviewed: {formatDate(proposal.reviewedAt)} by {proposal.reviewedBy || "unknown"}</div>}
              {proposal.reviewNote && <div>Review note: {proposal.reviewNote}</div>}
              {proposal.generatedDraftId && <div>Generated draft: {proposal.generatedDraftId}</div>}
            </div>
          </div>
        </div>

        <div style={s.panel}>
          <div style={s.label}>Why This Proposal Exists</div>
          <div style={{ marginTop: 8, lineHeight: 1.5 }}>{proposal.rationale}</div>
          <ProposalEvidence evidence={proposal.evidence} />
        </div>
      </section>
    </div>
  );
}

function formatThresholdValueSet(value = {}) {
  const entries = Object.entries(value || {}).filter(([key, child]) => child !== null && child !== undefined && key !== "direction" && key !== "column" && key !== "label");
  if (!entries.length) return "None";
  return entries.map(([key, child]) => `${key}: ${child}`).join(" · ");
}

function changeSubject(change) {
  const parts = [
    change.configType === "conditional" ? "Conditional" : "Base",
    change.templateName,
    change.oilBrand || change.brandKey || change.viscosityGrade,
    change.metric,
  ].filter(Boolean);
  return parts.join(" / ");
}

function ThresholdChangeList({ title, changes }) {
  return (
    <div style={{ ...s.panel, marginTop: 12 }}>
      <div style={s.label}>{title}</div>
      {(changes || []).map((change, index) => (
        <div key={`${title}-${change.proposalId}-${index}`} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
          <div style={{ fontWeight: 900 }}>{changeSubject(change)}</div>
          <div style={{ ...s.hint, marginTop: 4 }}>Examples: {(change.sampleIds || []).join(", ") || "N/A"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginTop: 8 }}>
            <div>
              <div style={s.label}>Before</div>
              <div style={{ marginTop: 4 }}>{formatThresholdValueSet(change.before)}</div>
            </div>
            <div>
              <div style={s.label}>After</div>
              <div style={{ marginTop: 4 }}>{formatThresholdValueSet(change.after)}</div>
            </div>
          </div>
        </div>
      ))}
      {(!changes || changes.length === 0) && <div style={{ ...s.hint, marginTop: 8 }}>No automatic changes in this group.</div>}
    </div>
  );
}

function ThresholdDraftPreviewModal({ preview, loading, onCreateDraft, onClose }) {
  const baseChanges = preview?.baseChanges || [];
  const conditionalChanges = preview?.conditionalChanges || [];
  const reviewOnly = preview?.reviewOnlyProposals || [];
  const acceptedProposals = preview?.proposals || [];
  const impactTotals = acceptedProposals.reduce((acc, proposal) => {
    const impact = proposal.evidence?.impact || {};
    acc.candidateRecords += Number(impact.candidateRecordCount || 0);
    acc.concerns += Number(impact.candidateConcernCount || 0);
    acc.mismatches += Number(impact.candidateMismatchCount || impact.patternSignalCount || 0);
    return acc;
  }, { candidateRecords: 0, concerns: 0, mismatches: 0 });
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section style={{ ...s.modal, ...s.detailModal }} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.sectionTitle}>Accepted Proposal Draft Preview</div>
            <div style={s.hint}>
              Review these before/after changes before creating threshold drafts. This step does not publish anything.
            </div>
          </div>
          <button type="button" style={s.secondary} onClick={onClose}>Close</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <LearningStat label="Accepted Proposals" value={preview?.proposalCount || 0} />
          <LearningStat label="Base Changes" value={baseChanges.length} />
          <LearningStat label="Conditional Changes" value={conditionalChanges.length} />
          <LearningStat label="Drafts To Create" value={preview?.draftCount || 0} />
          <LearningStat label="Candidate Records" value={impactTotals.candidateRecords} />
          <LearningStat label="Mismatch Signals" value={impactTotals.mismatches} />
        </div>

        <div style={{ ...s.panel, marginTop: 12, background: "var(--bg-page)" }}>
          <div style={s.label}>Accepted Proposal Impact</div>
          <div style={{ marginTop: 6, lineHeight: 1.45 }}>
            This accepted set is supported by {formatCount(impactTotals.candidateRecords)} historical candidate records,
            including {formatCount(impactTotals.concerns)} warning/critical outcomes and {formatCount(impactTotals.mismatches)} mismatch signals.
          </div>
          <div style={{ ...s.hint, marginTop: 6 }}>
            Counts are evidence signals, not unique affected report totals; final publishing still requires expert review of the generated drafts.
          </div>
        </div>

        <ThresholdChangeList title="Base Threshold Template Changes" changes={baseChanges} />
        <ThresholdChangeList title="Conditional Rule Changes" changes={conditionalChanges} />

        <div style={{ ...s.panel, marginTop: 12 }}>
          <div style={s.label}>Review-Only Accepted Proposals</div>
          {reviewOnly.map((proposal) => (
            <div key={proposal.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
              <div style={{ fontWeight: 900 }}>{proposal.title}</div>
              <div style={s.hint}>{proposal.metric} · {proposal.priority} priority</div>
              <div style={{ marginTop: 6 }}>{proposal.recommendation}</div>
            </div>
          ))}
          {reviewOnly.length === 0 && <div style={{ ...s.hint, marginTop: 8 }}>Every accepted proposal has at least one automatic draft change.</div>}
        </div>

        <div style={{ ...s.row, justifyContent: "flex-end", marginTop: 14 }}>
          <button type="button" style={s.secondary} onClick={onClose} disabled={loading}>Cancel</button>
          <button type="button" style={s.btn} onClick={onCreateDraft} disabled={loading}>
            {loading ? "Creating..." : "Create Drafts From This Preview"}
          </button>
        </div>
      </section>
    </div>
  );
}

function LearningList({ title, items }) {
  return (
    <div style={s.panel}>
      <div style={s.label}>{title}</div>
      <div style={{ marginTop: 8 }}>
        {(items || []).slice(0, 8).map((item) => (
          <div key={item.pattern} style={{ display: "flex", justifyContent: "space-between", gap: 10, borderTop: "1px solid var(--border)", padding: "7px 0" }}>
            <span style={{ overflowWrap: "anywhere" }}>{item.pattern}</span>
            <strong>{formatCount(item.count)}</strong>
          </div>
        ))}
        {(!items || items.length === 0) && <div style={s.hint}>No recorded patterns yet.</div>}
      </div>
    </div>
  );
}

function ExcelArtemisFocusPanel({ focus, onOpenReview }) {
  const issueTypes = focus?.issueTypes || [];
  const recentItems = focus?.recentItems || [];
  return (
    <div style={{ ...s.panel, marginTop: 12 }}>
      <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <div style={s.subTitle}>PDF / Snapshot Review Focus</div>
          <div style={s.hint}>Open comparison discrepancies that need client or data-review attention before they inform global threshold decisions.</div>
        </div>
        <button type="button" style={s.secondary} onClick={() => onOpenReview("all")}>Open All</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
        {issueTypes.map((item) => (
          <button
            key={item.issueType}
            type="button"
            style={{ ...s.panel, textAlign: "left", cursor: "pointer" }}
            onClick={() => onOpenReview(item.issueType)}
          >
            <div style={s.label}>{formatIssueType(item.issueType)}</div>
            <div style={{ fontWeight: 900, fontSize: 20, marginTop: 4 }}>{formatCount(item.itemCount || 0)}</div>
            <div style={s.hint}>{formatCount(item.issueCount || 0)} issue row{item.issueCount === 1 ? "" : "s"}</div>
          </button>
        ))}
        {issueTypes.length === 0 && <div style={s.hint}>No open PDF / Snapshot discrepancy buckets are currently recorded.</div>}
      </div>
      {recentItems.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={s.label}>Recent Review Items</div>
          {recentItems.slice(0, 4).map((item) => (
            <div key={item.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <strong>{item.labSampleId || "N/A"}</strong>
                <button
                  type="button"
                  style={{ ...s.secondary, padding: "6px 10px" }}
                  onClick={() => onOpenReview(item.issueTypes?.[0] || "all")}
                >
                  Review
                </button>
              </div>
              <div style={{ ...s.hint, marginTop: 3 }}>{(item.issueTypes || []).map(formatIssueType).join(", ")} · {formatCount(item.issueCount || 0)} issue row{item.issueCount === 1 ? "" : "s"}</div>
              <div style={{ ...s.hint, marginTop: 3 }}>Decision: {formatExcelArtemisDecision(item.annotation?.decisionStatus)}</div>
              <div style={{ ...s.hint, marginTop: 3, overflowWrap: "anywhere" }}>{item.workbookPath}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatRange(range) {
  if (!range) return "N/A";
  const min = Number(range.min);
  const max = Number(range.max);
  const format = (value) => Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "N/A";
  return min === max ? `${format(min)} (${formatCount(range.count)})` : `${format(min)}-${format(max)} (${formatCount(range.count)})`;
}

function formatStatusCounts(counts = {}) {
  const parts = [
    ["Normal", counts.normal],
    ["Warn", counts.warn],
    ["Critical", counts.critical],
    ["N/A", counts.na],
  ].filter(([, count]) => Number(count || 0) > 0);
  return parts.length ? parts.map(([label, count]) => `${label}: ${formatCount(count)}`).join(" · ") : "No statuses";
}

function thresholdActionLabel(action) {
  const labels = {
    draft_saved: "Draft Saved",
    published: "Published",
    draft_discarded: "Draft Discarded",
    archived: "Archived",
  };
  return labels[action] || String(action || "Threshold Change").replace(/_/g, " ");
}

function DraftSimulationPanel({ simulations }) {
  return (
    <div style={{ ...s.panel, marginTop: 12 }}>
      <div style={s.subTitle}>Draft Simulation Preview</div>
      <div style={s.hint}>
        Estimated historical overlap for saved threshold drafts compared with currently published thresholds. This is a review aid only; it does not publish changes or fully re-score every historical report.
      </div>
      {(simulations || []).map((simulation) => (
        <div key={simulation.id} style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 900 }}>{simulation.draftName}</div>
              <div style={s.hint}>{simulation.configType} draft · {formatDate(simulation.updatedAt)} · {formatCount(simulation.changeCount)} changed threshold value{simulation.changeCount === 1 ? "" : "s"}</div>
            </div>
            <span style={s.pill}>{simulation.configType}</span>
          </div>
          {simulation.learningProvenance?.summary && (
            <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: "#eff6ff", color: "#1e3a8a" }}>
              <strong>Expert Knowledge Summary</strong>
              <div style={{ marginTop: 4 }}>{simulation.learningProvenance.summary}</div>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginTop: 10 }}>
            <MiniStat label="Candidate Records" value={simulation.impact?.candidateRecordCount} />
            <MiniStat label="Concern Outcomes" value={simulation.impact?.candidateConcernCount} />
            <MiniStat label="Mismatch Signals" value={simulation.impact?.candidateMismatchCount} />
            <MiniStat label="Candidate Groups" value={simulation.impact?.candidateGroupCount} />
          </div>
          {(simulation.impact?.exampleSampleIds || []).length > 0 && (
            <div style={{ ...s.hint, marginTop: 8 }}>Examples: {simulation.impact.exampleSampleIds.join(", ")}</div>
          )}
          {(simulation.changes || []).slice(0, 5).map((change, index) => (
            <div key={`${simulation.id}-${change.metric}-${change.templateName}-${index}`} style={{ borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: 8 }}>
              <div style={{ fontWeight: 800 }}>{changeSubject(change)}</div>
              <div style={s.hint}>
                Before: {formatThresholdValueSet(change.before)} · After: {formatThresholdValueSet(change.after)}
              </div>
              <div style={s.hint}>
                Historical overlap: {formatCount(change.impact?.candidateRecordCount || 0)} records · {formatCount(change.impact?.candidateMismatchCount || 0)} mismatch signals
              </div>
            </div>
          ))}
          {(!simulation.changes || simulation.changes.length === 0) && (
            <div style={{ ...s.hint, marginTop: 8 }}>No differences from the currently published {simulation.configType} thresholds were detected in this draft.</div>
          )}
        </div>
      ))}
      {(!simulations || simulations.length === 0) && <div style={{ ...s.hint, marginTop: 8 }}>No saved threshold drafts are available to simulate yet.</div>}
    </div>
  );
}

function MiniStat({ label, value }) {
  const displayValue = typeof value === "number" ? formatCount(value) : value || "0";
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 10, background: "var(--bg-card)" }}>
      <div style={s.hint}>{label}</div>
      <div style={{ fontWeight: 900, fontSize: 18, marginTop: 4 }}>{displayValue}</div>
    </div>
  );
}

function CandidateTable({ title, type, items }) {
  const isViscosity = type === "viscosity";
  const isFuel = type === "fuel";
  const subjectLabel = isViscosity ? "Template / Grade" : isFuel ? "Template / Component" : "Template / Brand";
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ ...s.label, marginBottom: 6 }}>{title}</div>
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>{subjectLabel}</th>
              <th style={s.th}>Current</th>
              <th style={s.th}>Historical Statuses</th>
              <th style={s.th}>Observed Ranges</th>
              <th style={s.th}>Examples</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((item) => (
              <tr key={`${type}-${item.templateName}-${item.viscosityGrade || item.oilBrand || item.componentType}`}>
                <td style={{ ...s.td, minWidth: 170 }}>
                  <strong>{item.templateName}</strong>
                  <div style={s.hint}>{isViscosity ? item.viscosityGrade : isFuel ? item.componentType : item.oilBrand}</div>
                  {!isViscosity && !isFuel && item.rawBrands?.length > 1 && (
                    <div style={s.hint}>Aliases: {item.rawBrands.join(", ")}</div>
                  )}
                </td>
                <td style={{ ...s.td, minWidth: 120 }}>
                  {isViscosity ? (
                    item.currentBand ? `Low ${item.currentBand.low ?? "N/A"} · High ${item.currentBand.high ?? "N/A"}` : "No current band"
                  ) : (
                    item.currentThreshold ? `Warn ${item.currentThreshold.warn ?? "N/A"} · Fail ${item.currentThreshold.fail ?? "N/A"}` : isFuel ? "Inactive or missing" : "No current threshold"
                  )}
                </td>
                <td style={{ ...s.td, minWidth: 170 }}>
                  <div>{formatStatusCounts(item.statusCounts)}</div>
                  <div style={s.hint}>
                    {formatCount(item.concernCount)} historical warning/critical · {formatCount(item.mismatches)} post-override mismatch{item.mismatches === 1 ? "" : "es"}
                  </div>
                </td>
                <td style={{ ...s.td, minWidth: 210 }}>
                  <div>Normal: {formatRange(item.ranges?.normal)}</div>
                  <div>Warn: {formatRange(item.ranges?.warn)}</div>
                  <div>Critical: {formatRange(item.ranges?.critical)}</div>
                </td>
                <td style={{ ...s.td, minWidth: 170 }}>
                  <div>{(item.sampleIds || []).join(", ") || "N/A"}</div>
                  <div style={s.hint}>{item.recommendation}</div>
                </td>
              </tr>
            ))}
            {(!items || items.length === 0) && (
              <tr>
                <td style={s.td} colSpan="5">No candidate groups available yet. Run comparison batches with metric-level results first.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OutstandingSamplesModal({ samples, total, limit, page, search, loading, sort, onLimitChange, onPageChange, onSearchChange, onSortChange, onSelect, onArchive, onClose }) {
  const scrollRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const totalPages = Math.max(Math.ceil(total / limit), 1);
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;
  function handleScroll(event) {
    setShowBackToTop(event.currentTarget.scrollTop > 80);
  }
  function scrollToTop() {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section ref={scrollRef} style={s.modal} onScroll={handleScroll} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.sectionTitle}>Outstanding Samples</div>
            <div style={s.hint}>
              Showing {samples.length} of {total} sample{total === 1 ? "" : "s"} without completed test measurements{search ? " matching your search" : ""}.
            </div>
          </div>
          <div style={s.row}>
            <label style={{ ...s.hint, fontWeight: 800 }}>
              Rows{" "}
              <select style={s.input} value={limit} onChange={(event) => onLimitChange(event.target.value)}>
                {[10, 25, 50, 100, 250].map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <button type="button" style={s.secondary} disabled={!canPrev || loading} onClick={() => onPageChange(page - 1)}>Previous</button>
            <span style={s.hint}>Page {page + 1} of {totalPages}</span>
            <button type="button" style={s.secondary} disabled={!canNext || loading} onClick={() => onPageChange(page + 1)}>Next</button>
            <button type="button" style={s.secondary} onClick={onClose}>Close</button>
          </div>
        </div>

        <div style={{ ...s.row, marginBottom: 12 }}>
          <input
            style={{ ...s.input, minWidth: 280, flex: "1 1 320px" }}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by name, email, or sample ID"
            aria-label="Search outstanding samples by name, email, or sample ID"
          />
          {search && (
            <button type="button" style={s.secondary} onClick={() => onSearchChange("")}>
              Clear Search
            </button>
          )}
        </div>

        {loading && <div style={{ ...s.hint, marginBottom: 10 }}>Loading outstanding sample data...</div>}

        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead>
              <tr>
                <SortableTh label="Purchase Date" field="purchaseDate" sort={sort} onSort={onSortChange} />
                <SortableTh label="Name" field="name" sort={sort} onSort={onSortChange} />
                <SortableTh label="Email" field="email" sort={sort} onSort={onSortChange} />
                <SortableTh label="Sample ID" field="sampleId" sort={sort} onSort={onSortChange} />
                <SortableTh label="Form Submitted" field="formSubmitted" sort={sort} onSort={onSortChange} />
                <SortableTh label="Status" field="status" sort={sort} onSort={onSortChange} />
                <th style={s.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {samples.map((sample) => (
                <tr
                  key={sample.labSampleId}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(sample.labSampleId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(sample.labSampleId);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td style={s.td}>{formatDate(sample.purchaseDate) || "N/A"}</td>
                  <td style={s.td}>{sample.customerName || "N/A"}</td>
                  <td style={s.td}>{sample.customerEmail || "N/A"}</td>
                  <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontWeight: 800 }}>{sample.labSampleId}</td>
                  <td style={s.td}>{formatDate(sample.formSubmittedAt) || "N/A"}</td>
                  <td style={s.td}>{sample.sampleStatus || "N/A"}</td>
                  <td style={s.td}>
                    <button
                      type="button"
                      style={s.danger}
                      onClick={(event) => {
                        event.stopPropagation();
                        onArchive(sample.labSampleId);
                      }}
                    >
                      Archive
                    </button>
                  </td>
                </tr>
              ))}
              {samples.length === 0 && !loading && (
                <tr>
                  <td style={s.td} colSpan="7">{search ? "No outstanding samples match this search." : "No outstanding samples found."}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          style={{ ...s.backToTop, ...(showBackToTop ? s.backToTopVisible : {}) }}
          onClick={scrollToTop}
          aria-hidden={!showBackToTop}
          tabIndex={showBackToTop ? 0 : -1}
        >
          <span style={{ fontSize: 30, lineHeight: 1 }}>↑</span>
          <span style={{ fontSize: 12 }}>Back to Top</span>
        </button>
      </section>
    </div>
  );
}

function ArchivedSamplesModal({ samples, loading, onSelect, onRestore, onClose }) {
  const [sort, setSort] = useState({ field: "archived", direction: "desc" });
  const sortedSamples = useMemo(() => sortRows(samples, sort, {
    archived: (sample) => sample.archivedAt,
    name: (sample) => sample.customerName,
    email: (sample) => sample.customerEmail,
    sampleId: (sample) => sample.labSampleId,
    originalStatus: (sample) => sample.sampleStatus,
    archiveStatus: (sample) => sample.archiveStatus || "archived",
  }), [samples, sort]);
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section style={s.modal} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.sectionTitle}>Archive</div>
            <div style={s.hint}>{samples.length} archived outstanding sample{samples.length === 1 ? "" : "s"}.</div>
          </div>
          <button type="button" style={s.secondary} onClick={onClose}>Close</button>
        </div>
        {loading && <div style={{ ...s.hint, marginBottom: 10 }}>Loading archive...</div>}
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead>
              <tr>
                <SortableTh label="Archived" field="archived" sort={sort} onSort={(field) => setSort((current) => toggleSort(current, field))} />
                <SortableTh label="Name" field="name" sort={sort} onSort={(field) => setSort((current) => toggleSort(current, field))} />
                <SortableTh label="Email" field="email" sort={sort} onSort={(field) => setSort((current) => toggleSort(current, field))} />
                <SortableTh label="Sample ID" field="sampleId" sort={sort} onSort={(field) => setSort((current) => toggleSort(current, field))} />
                <SortableTh label="Original Status" field="originalStatus" sort={sort} onSort={(field) => setSort((current) => toggleSort(current, field))} />
                <SortableTh label="Archive Status" field="archiveStatus" sort={sort} onSort={(field) => setSort((current) => toggleSort(current, field))} />
                <th style={s.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedSamples.map((sample) => (
                <tr
                  key={sample.labSampleId}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(sample)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(sample);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td style={s.td}>{formatDate(sample.archivedAt) || "N/A"}</td>
                  <td style={s.td}>{sample.customerName || "N/A"}</td>
                  <td style={s.td}>{sample.customerEmail || "N/A"}</td>
                  <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontWeight: 800 }}>{sample.labSampleId}</td>
                  <td style={s.td}>{sample.sampleStatus || "N/A"}</td>
                  <td style={s.td}>{sample.archiveStatus || "archived"}</td>
                  <td style={s.td}>
                    {sample.archiveStatus === "archived" ? (
                      <button
                        type="button"
                        style={s.btn}
                        onClick={(event) => {
                          event.stopPropagation();
                          onRestore(sample.labSampleId);
                        }}
                      >
                        Reinstate
                      </button>
                    ) : (
                      <span style={s.hint}>Restored {formatDate(sample.restoredAt)}</span>
                    )}
                  </td>
                </tr>
              ))}
              {samples.length === 0 && !loading && <tr><td style={s.td} colSpan="7">No archived samples found.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function CustomerDetailModal({ detail, onSaveAccount, onOpenReport, onClose }) {
  const account = detail.customer.account;
  const [role, setRole] = useState(account?.role || "customer");
  const [status, setStatus] = useState(account?.status || "active");
  const canUpdate = Boolean(account);
  const email = detail.customer.customerEmail;
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <div style={{ ...s.modal, ...s.detailModal }} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.sectionTitle}>Customer Details</div>
            <div style={s.hint}>{detail.customer.customerName || "N/A"} - {email}</div>
          </div>
          <div style={s.row}>
            <a style={{ ...s.btn, textDecoration: "none" }} href={dashboardUrl(email)} target="_blank" rel="noreferrer">Open Dashboard</a>
            <button type="button" style={s.secondary} onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{ ...s.panel, marginBottom: 14 }}>
          <div style={s.subTitle}>Dashboard Account</div>
          {account ? (
            <div style={s.row}>
              <label>Role <select style={s.input} value={role} onChange={(event) => setRole(event.target.value)}>
                <option value="customer">customer</option>
                <option value="admin">admin</option>
              </select></label>
              <label>Status <select style={s.input} value={status} onChange={(event) => setStatus(event.target.value)}>
                {account.isAlphaTester && <option value="invited">invited</option>}
                <option value="active">active</option>
                <option value="hold">hold</option>
                {account.isAlphaTester && <option value="revoked">revoked</option>}
              </select></label>
              <button type="button" style={s.btn} onClick={() => onSaveAccount(email, role, status)}>Save Account</button>
              <span style={s.hint}>{account.isAlphaTester ? "Alpha tester; " : ""}created {formatDate(account.createdAt)}; last login {formatDate(account.lastLogin) || "N/A"}</span>
            </div>
          ) : (
            <div style={s.hint}>No dashboard account exists for this customer yet. You can still open a temporary dashboard view for admin purposes.</div>
          )}
        </div>
        <div style={s.subTitle}>Associated Reports</div>
        <div style={{ overflowX: "auto" }}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Sample ID</th><th style={s.th}>Vehicle</th><th style={s.th}>Component</th><th style={s.th}>Tested</th><th style={s.th}>Submitted</th><th style={s.th}>Status</th><th style={s.th}>Action</th></tr></thead>
            <tbody>
              {detail.reports.map((report) => (
                <tr key={report.labSampleId}>
                  <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontWeight: 800 }}>{report.labSampleId}</td>
                  <td style={s.td}>{report.vehicle || "N/A"}</td>
                  <td style={s.td}>{report.componentType || "N/A"}</td>
                  <td style={s.td}>{formatDate(report.testedDate) || "N/A"}</td>
                  <td style={s.td}>{formatDate(report.submittedDate) || "N/A"}</td>
                  <td style={s.td}>{report.isReady ? "Ready" : "Pending"}</td>
                  <td style={s.td}>
                    <div style={s.row}>
                      <button type="button" style={s.secondary} onClick={() => onOpenReport(report.labSampleId)}>View Report</button>
                      <a style={{ ...s.secondary, textDecoration: "none" }} href={dashboardUrl(email, report.labSampleId)} target="_blank" rel="noreferrer">Dashboard</a>
                    </div>
                  </td>
                </tr>
              ))}
              {detail.reports.length === 0 && <tr><td style={s.td} colSpan="7">No reports found for this customer.</td></tr>}
            </tbody>
          </table>
        </div>
        {!canUpdate && <div style={{ ...s.hint, marginTop: 10 }}>Role and status can be changed after a dashboard account exists for this email.</div>}
      </div>
    </div>
  );
}

function ReportDetailModal({ detail, onSaved, onClose }) {
  const [view, setView] = useState("home");
  const [releaseReview, setReleaseReview] = useState(detail.review || null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseMessage, setReleaseMessage] = useState(null);
  const report = detail.report;
  const sampleId = report.lab_sample_id || report.labSampleId;
  const customerEmail = report.customer_email || report.customerEmail;
  const vehicle = [report.vehicle_year, report.vehicle_make, report.vehicle_model].filter(Boolean).join(" ");
  const headerButton = {
    ...s.btn,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 36,
    boxSizing: "border-box",
    font: "inherit",
    fontSize: 13,
    lineHeight: 1,
    padding: "0 12px",
    textDecoration: "none",
  };
  const headerSecondaryButton = {
    ...s.secondary,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 36,
    boxSizing: "border-box",
    font: "inherit",
    fontSize: 13,
    lineHeight: 1,
    padding: "0 12px",
  };
  const rows = [
    ["Sample ID", sampleId],
    ["Customer", report.customer_name || report.customerName],
    ["Email", customerEmail],
    ["Status", report.isReady ? "Ready" : "Pending"],
    ["Template", detail.templateName],
    ["Tested", formatDate(report.sample_tested_date || report.testedDate)],
    ["Submitted", formatDate(report.response_submission_timestamp || report.submittedDate)],
    ["Vehicle", vehicle],
    ["Vehicle Name", report.vehicle_name],
    ["VIN", report.vehicle_vin],
    ["Component", report.component_type],
    ["Engine Miles", report.engine_miles],
    ["Sample Miles", report.sample_miles],
    ["Oil Brand", report.oil_brand],
    ["Oil Viscosity", report.oil_viscosity],
    ["Overall Summary", detail.summary?.overall],
    ["Contamination", detail.summary?.contamination],
    ["Wear", detail.summary?.wear],
    ["Oil Condition", detail.summary?.oil],
  ];
  const measurements = Object.entries(report)
    .filter(([, value]) => typeof value === "number" && Number.isFinite(value))
    .sort(([a], [b]) => a.localeCompare(b));

  useEffect(() => {
    setReleaseReview(detail.review || null);
  }, [detail.review]);

  async function approveReportFromHeader(disposition="release") {
    setReleaseLoading(true);
    setReleaseMessage(null);
    try {
      const res = await approveAdminReport(sampleId,disposition);
      setReleaseReview(res.data.review);
      setReleaseMessage({ type: "ok", text: disposition==="save"?"Report approved and added to the release queue.":"Report approved and released." });
      onSaved?.(res.data);
    } catch (err) {
      setReleaseMessage({ type: "err", text: err.response?.data?.error || "Unable to approve report." });
    } finally {
      setReleaseLoading(false);
    }
  }

  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <div style={{ ...s.modal, ...s.detailModal }} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ position: "sticky", top: -16, zIndex: 2, margin: "-16px -16px 14px", padding: "16px", background: "var(--bg-card)", borderBottom: "1px solid var(--border)" }}>
          <div style={{ ...s.row, justifyContent: "space-between" }}>
            <div>
              <div style={s.sectionTitle}>Oil Analysis Report</div>
              <div style={s.hint}>{sampleId} - {customerEmail}</div>
            </div>
            <div style={s.row}>
              <button type="button" style={headerButton} onClick={() => setView(view === "review" ? "home" : "review")}>
                {view === "review" ? "Report Home" : "Thresholds and Notes"}
              </button>
              <a style={headerButton} href={dashboardUrl(customerEmail, sampleId)} target="_blank" rel="noreferrer">Dashboard View</a>
              <button type="button" style={headerSecondaryButton} onClick={onClose}>Close</button>
            </div>
          </div>
          {view === "review" && (
            <div style={{ ...s.panel, marginTop: 14, borderColor: ["approved","approved_pending_release","released"].includes(releaseReview?.reviewStatus) ? "#16a34a" : "var(--border)" }}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <div>
                  <div style={s.label}>Customer Release</div>
                  <div style={s.hint}>
                    {["approved","approved_pending_release","released"].includes(releaseReview?.reviewStatus)
                      ? `${releaseReview.reviewStatus==="approved_pending_release"?"Approved and awaiting customer release":"Released"}${releaseReview.approvedBy ? ` by ${releaseReview.approvedBy}` : ""}${releaseReview.approvedAt ? ` on ${formatDate(releaseReview.approvedAt)}` : ""}.`
                      : "Queued or eligible reports stay pending for customers until approved."}
                  </div>
                  {releaseMessage && <div style={{ ...(releaseMessage.type === "ok" ? s.ok : s.err), fontSize: 12, fontWeight: 800, marginTop: 4 }}>{releaseMessage.text}</div>}
                </div>
                <div style={s.row}><button type="button" style={s.secondary} onClick={()=>approveReportFromHeader("save")} disabled={releaseLoading || ["approved","approved_pending_release","released"].includes(releaseReview?.reviewStatus)}>Approve and Save</button><button type="button" style={s.btn} onClick={()=>approveReportFromHeader("release")} disabled={releaseLoading || ["approved","released"].includes(releaseReview?.reviewStatus)}>Approve and Release</button></div>
              </div>
            </div>
          )}
        </div>

        {view === "review" ? (
          <ReportReviewModal detail={detail} embedded onSaved={onSaved} />
        ) : (
          <>
            {detail.oilGeekNote && (
              <div style={{ ...s.panel, marginBottom: 14 }}>
                <div style={{ ...s.label, color: "var(--teal-dark)" }}>Note from the Oil Geek</div>
                <div style={{ color: "var(--text-primary)", marginTop: 8, whiteSpace: "pre-line", lineHeight: 1.55 }}>{detail.oilGeekNote}</div>
              </div>
            )}
            {detail.templateName && (
              <div style={{ ...s.panel, marginBottom: 14, borderColor: "var(--teal)", background: "color-mix(in srgb, var(--teal) 10%, transparent)" }}>
                <div style={{ ...s.label, color: "var(--teal-dark)" }}>Admin threshold template</div>
                <div style={{ ...s.value, fontSize: 20 }}>{detail.templateName}</div>
                <div style={s.hint}>{detail.thresholdMode === "custom" ? "Custom thresholds are saved for this report." : "This report is using global thresholds."}</div>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10, marginBottom: 14 }}>
              {rows.map(([label, value]) => <DetailItem key={label} label={label} value={value} />)}
            </div>
            <div style={s.subTitle}>Measurements</div>
            <div style={{ overflowX: "auto" }}>
              <table style={s.table}>
                <thead><tr><th style={s.th}>Metric</th><th style={s.th}>Value</th><th style={s.th}>Status</th></tr></thead>
                <tbody>
                  {measurements.map(([metric, value]) => (
                    <tr key={metric}>
                      <td style={s.td}>{metric}</td>
                      <td style={s.td}>{value}</td>
                      <td style={s.td}>{detail.evaluation?.[metric] || "N/A"}</td>
                    </tr>
                  ))}
                  {measurements.length === 0 && <tr><td style={s.td} colSpan="3">No numeric measurements available.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DetailItem({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ ...s.card, boxShadow: "none", padding: 12 }}>
      <div style={s.label}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--text-primary)", marginTop: 4, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}

function OutstandingSampleDetailModal({ sample, onArchive, onClose }) {
  const vehicle = [sample.vehicleYear, sample.vehicleMake, sample.vehicleModel].filter(Boolean).join(" ");
  const oil = [sample.oilBrand, sample.oilViscosity].filter(Boolean).join(" ");
  const canArchive = Boolean(onArchive);
  const details = [
    ["Sample ID", sample.labSampleId],
    ["Retailer Sample ID", sample.retailerSampleId],
    ["Customer", sample.customerName],
    ["Email", sample.customerEmail],
    ["Phone", sample.customerPhoneNumber],
    ["Purchase Date", formatDate(sample.purchaseDate)],
    ["Lab Sample Created", formatDate(sample.labSampleCreatedAt)],
    ["Sample Created", formatDate(sample.sampleCreatedAt)],
    ["Form Submitted", formatDate(sample.formSubmittedAt)],
    ["Sample Collected", formatDate(sample.sampleCollectedDate)],
    ["Status", sample.sampleStatus],
    ["Shipped", sample.shipped],
    ["Shipped At", formatDate(sample.shippedAt)],
    ["Arrived At Lab", sample.arrivedAtLab],
    ["Arrived At Lab At", formatDate(sample.arrivedAtLabAt)],
    ["Submitted Sample Form", sample.submittedSampleForm],
    ["Orphan", sample.isOrphan],
    ["Vehicle", vehicle],
    ["Vehicle Name", sample.vehicleName],
    ["VIN", sample.vehicleVin],
    ["Application", sample.vehicleApplication],
    ["Engine Type", sample.vehicleEngineType],
    ["Component", sample.componentType],
    ["Engine Miles", sample.engineMiles],
    ["Sample Miles", sample.sampleMiles],
    ["Oil", oil],
    ["Customer Notes", sample.customerNotes],
    ["Internal Notes", sample.notes],
    ["Last Updated", formatDate(sample.lastUpdatedUtc)],
  ];

  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <div style={{ ...s.modal, ...s.detailModal }} onMouseDown={(event) => event.stopPropagation()}>
        <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={s.sectionTitle}>Customer / Order Details</div>
            <div style={s.hint}>{sample.labSampleId} {sample.customerName ? `- ${sample.customerName}` : ""}</div>
          </div>
          <div style={s.row}>
            {canArchive && (
              <button type="button" style={s.danger} onClick={() => onArchive(sample.labSampleId)}>
                Archive
              </button>
            )}
            <button type="button" style={s.secondary} onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}>
          {details.map(([label, value]) => <DetailItem key={label} label={label} value={value} />)}
        </div>
      </div>
    </div>
  );
}

const ALERT_TYPE_LABELS = {
  alpha_question: "Alpha question",
  alpha_problem: "Alpha problem",
  alpha_suggestion: "Alpha suggestion",
};

function AlertRow({ alert, onResolve, onOpen }) {
  const [note, setNote] = useState("");
  return (
    <tr onClick={onOpen} style={{ cursor: "pointer" }}>
      <td style={s.td}>{ALERT_TYPE_LABELS[alert.requestType] || alert.requestType}</td>
      <td style={s.td}>{alert.email}</td>
      <td style={s.td}>{alert.labSampleId || "N/A"}</td>
      <td style={{ ...s.td, minWidth: alert.requestType?.startsWith("alpha_") ? 280 : undefined, whiteSpace: "pre-wrap" }}>
        <div>{Object.entries(alert.payloadSummary || {}).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join("; ") || "N/A"}</div>
        {alert.requestType?.startsWith("alpha_") && (
          <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(); }} style={{ border: "none", background: "transparent", color: "var(--teal-dark)", fontWeight: 900, fontSize: 12, padding: "4px 0 0", cursor: "pointer" }}>
            Open full feedback →
          </button>
        )}
      </td>
      <td style={s.td}>{alert.status}</td>
      <td style={s.td}>
        {alert.status === "review_pending" ? (
          <div style={s.row} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            <input style={s.input} value={note} onChange={e => setNote(e.target.value)} placeholder="Internal note" />
            <button type="button" style={s.btn} onClick={() => onResolve("resolved", note)}>Resolve</button>
            <button type="button" style={s.danger} onClick={() => onResolve("rejected", note)}>Reject</button>
          </div>
        ) : alert.internalNote || alert.reviewedBy || "Reviewed"}
      </td>
    </tr>
  );
}

function AlphaFeedbackAlertModal({ alert, onReply, onClose }) {
  const [reply, setReply] = useState(alert.adminReply || "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const isAlpha = alert.requestType?.startsWith("alpha_");
  const message = alert.payloadSummary?.message || "No message was provided.";
  const category = alert.payloadSummary?.category || alert.requestType?.replace(/^alpha_/, "") || "feedback";

  async function sendReply() {
    setSending(true);
    setError("");
    try {
      await onReply(alert, reply);
    } catch (err) {
      setError(err.response?.data?.error || "The reply could not be sent.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section
        style={{ ...s.modal, width: "min(720px, 96vw)", padding: 24 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="alpha-feedback-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, borderBottom: "1px solid var(--border)", paddingBottom: 16 }}>
          <div>
            <div style={s.label}>Alpha tester feedback</div>
            <h2 id="alpha-feedback-title" style={{ margin: "5px 0 0", color: "var(--text-primary)", fontSize: 23 }}>{ALERT_TYPE_LABELS[alert.requestType] || alert.requestType}</h2>
          </div>
          <button type="button" style={s.secondary} onClick={onClose}>Close</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, margin: "18px 0" }}>
          <div><div style={s.label}>Tester</div><div style={{ color: "var(--text-primary)", marginTop: 4, fontWeight: 800 }}>{alert.email}</div></div>
          <div><div style={s.label}>Report</div><div style={{ color: "var(--text-primary)", marginTop: 4, fontWeight: 800 }}>{alert.labSampleId || "No report selected"}</div></div>
          <div><div style={s.label}>Submitted</div><div style={{ color: "var(--text-primary)", marginTop: 4 }}>{alert.createdAt ? new Date(alert.createdAt).toLocaleString() : "Unknown"}</div></div>
          <div><div style={s.label}>Status</div><div style={{ color: "var(--text-primary)", marginTop: 4, fontWeight: 800 }}>{alert.status?.replace("_", " ")}</div></div>
        </div>

        <div style={{ ...s.panel, background: "var(--bg-page)", marginBottom: 18 }}>
          <div style={{ ...s.label, marginBottom: 8 }}>{category}</div>
          <div style={{ color: "var(--text-primary)", whiteSpace: "pre-wrap", lineHeight: 1.65, fontSize: 15 }}>{message}</div>
        </div>

        {alert.adminReply ? (
          <div style={{ ...s.panel, borderColor: "color-mix(in srgb, var(--teal) 45%, var(--border))" }}>
            <div style={{ ...s.label, color: "var(--teal-dark)" }}>Reply sent</div>
            <div style={{ color: "var(--text-primary)", whiteSpace: "pre-wrap", lineHeight: 1.6, marginTop: 8 }}>{alert.adminReply}</div>
            <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 10 }}>{alert.repliedBy || "Admin"}{alert.repliedAt ? ` · ${new Date(alert.repliedAt).toLocaleString()}` : ""}</div>
          </div>
        ) : isAlpha ? (
          <div>
            <label htmlFor="alpha-feedback-reply" style={{ ...s.label, display: "block", marginBottom: 8 }}>Reply to tester</label>
            <textarea
              id="alpha-feedback-reply"
              style={{ ...s.input, width: "100%", minHeight: 130, resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
              value={reply}
              maxLength={5000}
              onChange={(event) => setReply(event.target.value)}
              placeholder="Write Lake's reply. This will be emailed to the alpha tester."
            />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
              <span style={{ color: error ? "#b91c1c" : "var(--text-muted)", fontSize: 12 }}>{error || `${reply.length}/5000 characters`}</span>
              <button type="button" style={{ ...s.btn, opacity: sending || reply.trim().length < 2 ? 0.55 : 1 }} disabled={sending || reply.trim().length < 2} onClick={sendReply}>
                {sending ? "Sending…" : "Send reply and resolve"}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function BaseEditor({ config, templateName, templateNames, setTemplateName, updateMetric, updateMetricAcrossTemplates, updateViscosityMapping }) {
  const [editorSection, setEditorSection] = useState("templates");
  const [allocationMetric, setAllocationMetric] = useState(null);
  const [allocationScope, setAllocationScope] = useState("current");
  const [allocationSelected, setAllocationSelected] = useState([]);
  const viscosityMapping = config?.template_selection_rules?.viscosity_class_mapping || {};
  const availableAllocationTemplates = useMemo(() => {
    if (!allocationMetric) return [];
    return templateNames.filter((name) => Boolean(config?.templates?.[name]?.[allocationMetric.metric]));
  }, [allocationMetric, config, templateNames]);
  const allocationTargets = useMemo(() => {
    if (!allocationMetric) return [];
    if (allocationScope === "current") return [templateName];
    if (allocationScope === "engine") return availableAllocationTemplates.filter((name) => /^ENGINE-/i.test(name) || /^Leaded-/i.test(name));
    if (allocationScope === "diesel") return availableAllocationTemplates.filter((name) => /^DIESEL-/i.test(name));
    if (allocationScope === "all") return availableAllocationTemplates;
    return allocationSelected.filter((name) => availableAllocationTemplates.includes(name));
  }, [allocationMetric, allocationScope, allocationSelected, availableAllocationTemplates, templateName]);
  function openAllocation(metric, threshold) {
    setAllocationMetric({ metric, threshold: { ...clone(threshold), type: editableThresholdType(threshold.type) } });
    setAllocationScope("current");
    setAllocationSelected([templateName]);
  }
  function applyAllocation() {
    if (!allocationMetric || allocationTargets.length === 0) return;
    updateMetricAcrossTemplates(allocationMetric.metric, allocationMetric.threshold, allocationTargets);
    setAllocationMetric(null);
  }
  return (
    <div style={s.panel}>
      <div style={s.tabBar}>
        <button type="button" style={editorSection === "templates" ? s.tabActive : s.tab} onClick={() => setEditorSection("templates")}>Threshold Templates</button>
        <button type="button" style={editorSection === "viscosity" ? s.tabActive : s.tab} onClick={() => setEditorSection("viscosity")}>Viscosity Map</button>
      </div>
      {editorSection === "templates" ? (
        <>
          <div style={{ ...s.row, position: "sticky", top: 0, zIndex: 8, justifyContent: "space-between", marginBottom: 12, padding: "10px 0 12px", background: "var(--bg-card)", borderBottom: "1px solid var(--border-subtle)" }}>
            <div>
              <div style={s.subTitle}>Template Thresholds</div>
              <div style={s.hint}>Choose one template, edit its metric ranges, then save the draft.</div>
            </div>
            <select style={{ ...s.input, minWidth: 180 }} value={templateName} onChange={e => setTemplateName(e.target.value)}>
              {templateNames.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          <div style={{ overflowX: "auto", maxHeight: 520, border: "1px solid var(--border-subtle)", borderRadius: 8 }}>
            <table style={s.table}>
              <thead><tr><th style={{ ...s.th, position: "sticky", top: 0, zIndex: 4, background: "var(--bg-card)" }}>Metric</th><th style={{ ...s.th, position: "sticky", top: 0, zIndex: 4, background: "var(--bg-card)" }}>Pass</th><th style={{ ...s.th, position: "sticky", top: 0, zIndex: 4, background: "var(--bg-card)" }}>Warn</th><th style={{ ...s.th, position: "sticky", top: 0, zIndex: 4, background: "var(--bg-card)" }}>Fail</th><TypeHeaderCell style={{ position: "sticky", top: 0, zIndex: 4, background: "var(--bg-card)" }} /><th style={{ ...s.th, position: "sticky", top: 0, zIndex: 4, background: "var(--bg-card)" }}>Apply</th></tr></thead>
              <tbody>
                {metricEntries(config, templateName).map(([metric, threshold]) => {
                  const isParticleCount = metric === "PARTICLE_COUNT";
                  const isBooleanFlag = threshold.type === "BOOL" &&
                    threshold.pass === undefined &&
                    threshold.warn === undefined &&
                    threshold.fail === undefined;
                  return (
                    <tr key={metric}>
                      <td style={s.td}>{metric}</td>
                      {isParticleCount ? (
                        <td style={s.td} colSpan="4">
                          <div style={{display:"flex",gap:18,alignItems:"center",flexWrap:"wrap"}}>
                            <span><strong>Good through:</strong> {(threshold.goodMax||[23,20,17]).join(" / ")}</span>
                            <span><strong>Fail starting:</strong> {(threshold.failMin||[26,23,20]).join(" / ")}</span>
                            <span style={s.hint}>Values between are Watch · Worst component controls</span>
                          </div>
                        </td>
                      ) : ["pass", "warn", "fail"].map(field => (
                        <td style={s.td} key={field}>
                          {isBooleanFlag ? (
                            <span style={s.hint}>N/A</span>
                          ) : (
                            <input style={s.input} type="number" value={threshold[field] ?? ""} onChange={e => updateMetric(metric, field, e.target.value)} />
                          )}
                        </td>
                      ))}
                      {!isParticleCount && <td style={s.td}><select style={s.input} value={editableThresholdType(threshold.type)} onChange={e => updateMetric(metric, "type", e.target.value)}>
                        {THRESHOLD_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select></td>}
                      <td style={s.td}>
                        {isParticleCount ? <span style={s.pill}>Global · All templates</span> : <button type="button" style={{ ...s.secondary, padding: "7px 10px", whiteSpace: "nowrap" }} onClick={() => openAllocation(metric, threshold)}>Allocate Change</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {allocationMetric && (
            <div style={s.modalBackdrop} onMouseDown={() => setAllocationMetric(null)}>
              <section style={{ ...s.modal, ...s.detailModal }} onMouseDown={(event) => event.stopPropagation()}>
                <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div style={s.sectionTitle}>Allocate Threshold Change</div>
                    <div style={s.hint}>
                      Apply the current {allocationMetric.metric} values from {templateName} to other templates in this draft. This does not publish anything until you save and publish the draft.
                    </div>
                  </div>
                  <button type="button" style={s.secondary} onClick={() => setAllocationMetric(null)}>Close</button>
                </div>
                <div style={{ ...s.panel, marginBottom: 12 }}>
                  <div style={s.label}>Values to apply</div>
                  <div style={{ ...s.row, marginTop: 8 }}>
                    <span style={s.pill}>Pass: {allocationMetric.threshold.pass ?? "N/A"}</span>
                    <span style={s.pill}>Warn: {allocationMetric.threshold.warn ?? "N/A"}</span>
                    <span style={s.pill}>Fail: {allocationMetric.threshold.fail ?? "N/A"}</span>
                    <span style={s.pill}>Type: {THRESHOLD_TYPE_OPTIONS.find(([value]) => value === editableThresholdType(allocationMetric.threshold.type))?.[1] || "Higher value is worse"}</span>
                  </div>
                </div>
                <div style={s.label}>Apply to</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8, marginTop: 8 }}>
                  {[
                    ["current", "Just this template"],
                    ["engine", "All engine oils"],
                    ["diesel", "All diesel engine oils"],
                    ["all", "Across all templates"],
                    ["specific", "Choose specific templates"],
                  ].map(([value, label]) => (
                    <label key={value} style={{ ...s.secondary, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="radio" name="allocation-scope" checked={allocationScope === value} onChange={() => setAllocationScope(value)} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                {allocationScope === "specific" && (
                  <div style={{ ...s.panel, marginTop: 12, maxHeight: 220, overflow: "auto" }}>
                    <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={s.label}>Specific templates</div>
                      <button
                        type="button"
                        style={{ ...s.secondary, padding: "6px 10px" }}
                        onClick={() => setAllocationSelected(availableAllocationTemplates)}
                      >
                        Select all
                      </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 6 }}>
                      {availableAllocationTemplates.map((name) => (
                        <label key={name} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={allocationSelected.includes(name)}
                            onChange={(event) => {
                              setAllocationSelected((current) => event.target.checked
                                ? Array.from(new Set([...current, name]))
                                : current.filter((item) => item !== name));
                            }}
                          />
                          {name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ ...s.hint, marginTop: 12 }}>
                  {allocationTargets.length} matching template{allocationTargets.length === 1 ? "" : "s"} will receive this metric's Pass/Warn/Fail/Type values.
                </div>
                <div style={{ ...s.row, justifyContent: "flex-end", marginTop: 14 }}>
                  <button type="button" style={s.secondary} onClick={() => setAllocationMetric(null)}>Cancel</button>
                  <button type="button" style={s.btn} onClick={applyAllocation} disabled={allocationTargets.length === 0}>Apply Change</button>
                </div>
              </section>
            </div>
          )}
        </>
      ) : (
        <>
          <div style={s.subTitle}>Viscosity Map</div>
          <div style={s.hint}>Oil viscosity maps to the template class used during threshold selection. For example, 5W-30 resolves to ENGINE-30 for gasoline engines.</div>
          <div style={{ overflowX: "auto", marginTop: 10, maxHeight: 520, border: "1px solid var(--border-subtle)", borderRadius: 8 }}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Oil Viscosity</th><th style={s.th}>Template Class</th></tr></thead>
              <tbody>
                {Object.entries(viscosityMapping).map(([viscosity, mappedClass]) => (
                  <tr key={viscosity}>
                    <td style={s.td}>{viscosity}</td>
                    <td style={s.td}>
                      <input style={s.input} value={mappedClass} onChange={e => updateViscosityMapping(viscosity, e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ConditionalEditor({ config, updateConditional }) {
  const [ruleSection, setRuleSection] = useState("fuel");
  const rules = config.active_rules || {};
  const availableSections = [
    ["fuel", "Fuel Dilution", Boolean(rules.FUEL_DILUTION)],
    ["tin", "Tin", Boolean(rules.TIN)],
    ["wearRate", "Computed Wear", Boolean(rules.TOTAL_METALS_CALCULATION || rules.WEAR_RATE_PER_1000_MILES)],
    ["oilBrands", "Oil Brand Overrides", Boolean(rules.OXIDATION || rules.TITANIUM)],
    ["pending", "Pending Rules", Boolean(config.pending_rules)],
  ].filter(([, , visible = true]) => visible);
  return (
    <div style={s.panel}>
      <div style={s.tabBar}>
        {availableSections.map(([key, label]) => (
          <button key={key} type="button" style={ruleSection === key ? s.tabActive : s.tab} onClick={() => setRuleSection(key)}>{label}</button>
        ))}
      </div>
      {ruleSection === "fuel" && rules.FUEL_DILUTION && (
        <RuleThresholds title="Fuel Dilution" ruleKey="FUEL_DILUTION" entries={{ gasoline: rules.FUEL_DILUTION.gasoline, diesel: rules.FUEL_DILUTION.diesel }} updateConditional={updateConditional} />
      )}
      {ruleSection === "tin" && rules.TIN && (
        <RuleThresholds title="Tin" ruleKey="TIN" entries={{ molybdenum_lte_100: rules.TIN.molybdenum_lte_100, molybdenum_gt_100: rules.TIN.molybdenum_gt_100 }} updateConditional={updateConditional} />
      )}
      {ruleSection === "wearRate" && (
        <ComputedWearRuleEditor rules={rules} updateConditional={updateConditional} />
      )}
      {ruleSection === "oilBrands" && (
        <div style={{ maxHeight: 560, overflowY: "auto", paddingRight: 4 }}>
          {["OXIDATION", "TITANIUM"].map(ruleKey => rules[ruleKey] && (
            <div key={ruleKey} style={{ marginBottom: 18 }}>
              <div style={s.subTitle}>{ruleKey} Oil Brand Overrides</div>
              {rules[ruleKey].default && <WarnFailRow label="default" path={[ruleKey, "default"]} value={rules[ruleKey].default} updateConditional={updateConditional} />}
              {Object.entries(rules[ruleKey].brand_thresholds || {}).map(([brand, value]) => (
                <WarnFailRow key={brand} label={brand} path={[ruleKey, "brand_thresholds", brand]} value={value} updateConditional={updateConditional} />
              ))}
            </div>
          ))}
        </div>
      )}
      {ruleSection === "pending" && config.pending_rules && (
        <div>
          <div style={s.subTitle}>Pending Conditional Rules</div>
          <div style={s.hint}>These were captured from the workbook but are not active yet because they need additional calibration or rule-shape decisions.</div>
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Rule</th><th style={s.th}>Source</th><th style={s.th}>Reason</th></tr></thead>
              <tbody>
                {Object.entries(config.pending_rules).map(([rule, detail]) => (
                  <tr key={rule}><td style={s.td}>{rule}</td><td style={s.td}>{detail.source}</td><td style={s.td}>{detail.reason}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ComputedWearRuleEditor({ rules, updateConditional }) {
  const totalRule = rules.TOTAL_METALS_CALCULATION || {};
  const wearRule = rules.WEAR_RATE_PER_1000_MILES || {};
  return (
    <div>
      <div style={s.subTitle}>Total Metals Calculation</div>
      <div style={s.hint}>{totalRule.description || "Calculated from the report's wear-metal measurements."}</div>
      <div style={{ ...s.panel, marginTop: 10, padding: 12 }}>
        <div><strong>Included source measurements:</strong> {(totalRule.component_columns || []).join(", ") || "—"}</div>
        <div style={{ marginTop: 6 }}><strong>Excluded by Lake’s workbook settings:</strong> {(totalRule.excluded_components || []).join(", ") || "None"}</div>
        <div style={{ marginTop: 6, color: "var(--text-muted)", fontSize: 12 }}><strong>Source:</strong> {totalRule.source || "—"}</div>
      </div>

      <div style={{ ...s.subTitle, marginTop: 18 }}>Wear Rate per 1,000 Miles</div>
      <div style={s.hint}>{wearRule.description || "Calculated from Total Metals and miles on the oil."}</div>
      <div style={{ ...s.panel, marginTop: 10, padding: 12 }}>
        <div><strong>Formula:</strong> {wearRule.formula || "(total_metals / sample_miles) * 1000"}</div>
        <div style={{ marginTop: 6 }}><strong>Mileage field:</strong> Miles on oil (`sample_miles`)</div>
        <div style={{ marginTop: 6, color: "var(--text-muted)", fontSize: 12 }}><strong>Source:</strong> {wearRule.source || "—"}</div>
      </div>
      <div style={{ overflowX: "auto", marginTop: 12 }}>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Miles on Oil</th><th style={s.th}>Normal Upper Limit</th><th style={s.th}>Warning Upper Limit</th></tr></thead>
          <tbody>
            {(wearRule.bands || []).map((band, index) => (
              <tr key={band.key || band.label}>
                <td style={s.td}>{band.label}</td>
                <td style={s.td}><input style={s.input} type="number" value={band.normal_upper} onChange={(event) => updateConditional(["WEAR_RATE_PER_1000_MILES", "bands", index, "normal_upper"], event.target.value)} /></td>
                <td style={s.td}><input style={s.input} type="number" value={band.warning_upper} onChange={(event) => updateConditional(["WEAR_RATE_PER_1000_MILES", "bands", index, "warning_upper"], event.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RuleThresholds({ title, ruleKey, entries, updateConditional }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={s.label}>{title}</div>
      {Object.entries(entries).map(([name, value]) => (
        <div key={name} style={{ ...s.row, marginTop: 8 }}>
          <strong style={{ minWidth: 170 }}>{name}</strong>
          {["pass", "warn", "fail"].map(field => (
            <label key={field}>{field} <input style={s.input} type="number" value={value[field]} onChange={e => updateConditional([ruleKey, name, field], e.target.value)} /></label>
          ))}
        </div>
      ))}
    </div>
  );
}

function EffectiveThresholdPreview({ baseConfig, sample, updatePreview, templateName, thresholds }) {
  const viscosityOptions = Object.keys(baseConfig?.template_selection_rules?.viscosity_class_mapping || {});
  const rows = Object.entries(thresholds || {}).filter(([, threshold]) => threshold._conditionalSource);
  return (
    <div style={s.panel}>
      <div style={s.sectionTitle}>Effective Threshold Preview</div>
      <div style={s.hint}>Choose sample context to see template selection plus oil brand, viscosity, fuel, molybdenum, and mileage-band overrides.</div>
      <div style={{ ...s.row, marginTop: 10, marginBottom: 12 }}>
        <label>Component <select style={s.input} value={sample.component_type} onChange={e => updatePreview("component_type", e.target.value)}>
          {["Engine", "Transmission", "Gear Box"].map(value => <option key={value} value={value}>{value}</option>)}
        </select></label>
        <label>Fuel <select style={s.input} value={sample.vehicle_fuel_type} onChange={e => updatePreview("vehicle_fuel_type", e.target.value)}>
          {["Gasoline", "Diesel", "Leaded Fuel", "Race Fuel - Leaded"].map(value => <option key={value} value={value}>{value}</option>)}
        </select></label>
        <label>Viscosity <select style={s.input} value={sample.oil_viscosity} onChange={e => updatePreview("oil_viscosity", e.target.value)}>
          {viscosityOptions.map(value => <option key={value} value={value}>{value}</option>)}
        </select></label>
        <label>Oil Brand <input style={s.input} value={sample.oil_brand} onChange={e => updatePreview("oil_brand", e.target.value)} /></label>
        <label>Molybdenum <input style={s.input} type="number" value={sample.molybdenum} onChange={e => updatePreview("molybdenum", e.target.value)} /></label>
        <label>Miles on Oil <input style={s.input} type="number" value={sample.sample_miles} onChange={e => updatePreview("sample_miles", e.target.value)} /></label>
      </div>
      <div style={{ ...s.status, color: "var(--text-primary)", marginBottom: 8 }}>Resolved template: {templateName}</div>
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead><tr><th style={s.th}>Metric</th><th style={s.th}>Pass</th><th style={s.th}>Warn</th><th style={s.th}>Fail</th><th style={s.th}>Type</th><th style={s.th}>Conditional Source</th></tr></thead>
          <tbody>
            {Object.entries(thresholds || {}).map(([metric, threshold]) => (
              <tr key={metric}>
                <td style={s.td}>{metric}</td>
                <td style={s.td}>{threshold.pass}</td>
                <td style={s.td}>{threshold.warn}</td>
                <td style={s.td}>{threshold.fail}</td>
                <td style={s.td}>{thresholdTypeLabel(threshold.type)}</td>
                <td style={s.td}>{threshold._conditionalSource || "Base template"}</td>
              </tr>
            ))}
            {Object.keys(thresholds || {}).length === 0 && <tr><td style={s.td} colSpan="6">No thresholds resolved for this context.</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && <div style={{ ...s.hint, marginTop: 8 }}>{rows.length} metric{rows.length === 1 ? "" : "s"} changed by conditional logic.</div>}
    </div>
  );
}

function WarnFailRow({ label, path, value, updateConditional }) {
  return (
    <div style={{ ...s.row, marginTop: 8 }}>
      <strong style={{ minWidth: 170 }}>{label}</strong>
      {value.pass !== undefined && <label>pass <input style={s.input} type="number" value={value.pass} onChange={e => updateConditional([...path, "pass"], e.target.value)} /></label>}
      {["warn", "fail"].map(field => (
        <label key={field}>{field} <input style={s.input} type="number" value={value[field]} onChange={e => updateConditional([...path, field], e.target.value)} /></label>
      ))}
    </div>
  );
}
